import moment from "moment";
import { z } from "zod";
import { defineSchema, TableSchema } from "../tables/defineSchema";
import { xRow } from "../tables/row";
import { type TableName } from "../tables/tableNames";

export type { TableName };
export type DbType = "sqlite" | "mysql" | "surreal";

export type Unite<A extends ReadonlyArray<unknown>> = A[number];

export type FetchesType<T> = {
  [Key in keyof T]: T[Key] extends
    | number
    | (number | null | undefined)
    | number[]
    | (number[] | null | undefined)
    | string
    | (string | null | undefined)
    | string[]
    | (string[] | null | undefined)
    | boolean
    ? never
    : Key;
}[keyof T];

export type AiDiscussion = Row & {
  name: string;
  description: string;
};

export const aiMessageActionTypes = [
  "userMessage",
  "consultationExtraction",
  "information",
  "task",
  "none",
] as const;

export type AiMessage = Row & {
  discussionId: string;
  content: string;
  role: "user" | "assistant";
  actionType: (typeof aiMessageActionTypes)[number];
  tasks: AiTask[] | null | undefined;
};

export type AiTask = Row & {
  name: string;
  message: string;
  prompt: string;
  description: string;
  actions: (AiTaskAction | null | undefined)[];
  status: PatientItemStatus;
};

export type AiTaskAction = Row & {
  name: string;
  type: "uiAction" | "fetchAction" | "generateAction";
  description: string;
  status: PatientItemStatus;
  steps: (AiTaskStep | null | undefined)[];
  plannedNextTasks: string;
  result?: string | null | undefined;
};

export type AiTaskStep = Row & {
  name: string;
  description: string;
  targetId: string;
  type: "submitInput" | "input" | "click";
  status: PatientItemStatus;
  value: string;
};

export type UiItemType =
  | InputsType
  | "toast"
  | "drawer"
  | "dialog"
  | "card"
  | "compartment"
  | "button"
  | "tab"
  | "navbarButton"
  | "text"
  | "simpleText"
  | "searchBar";

export type UiItem = {
  id?: string | null | undefined;
  parentId?: string | null | undefined;
  ref?: any;
  title: any | null | undefined;
  description?: string | (() => string);
  type: UiItemType;
  subtitle?: any | null | undefined;
  placeholder?: string;
  options?:
    | {
        value: string | null | undefined;
        label: string | null | undefined;
      }[]
    | null
    | undefined;
  unitName?: string;
  isClickable?: boolean;
  value?: any;
  leading?: string;
  trailing?: string;
  onChange?: (e: any) => void;
  onClick?: (e: any) => void;
  onEnter?: (e: any, value: any) => void;
  content?: string;
};

export type UiItems = Record<string, UiItem>;
export type UiTreeItem = {
  id?: string | null | undefined;
  title: any | null | undefined;
  description?: string;
  type: UiItemType;
  isClickable?: boolean;
  subtitle?: any | null | undefined;
  placeholder?: string;
  options?:
    | {
        value: string | null | undefined;
        label: string | null | undefined;
      }[]
    | null
    | undefined;
  unitName?: string;
  value?: any;
  leading?: string;
  trailing?: string;
  content?: string;
  children?: UiTreeItem[];
};
export type UiTree = UiTreeItem[];

export type MysteryGame = Row & {
  name: string;
  userContext: string;
  description: string;
  numberOfCharacters: number;
  difficultyLevel: "easy" | "medium" | "hard";
  characters: (MysteryCharacter | null | undefined)[];
  events: (MysteryEvent | null | undefined)[];
  locations: (MysteryLocation | null | undefined)[];
  clues: (MysteryClue | null | undefined)[];
  rawGeneratedDetails: string;
};

export type MysteryCharacter = Row & {
  name: string;
  physicalDescription: string;
  mentalDescription: string;
  whatTheyKnow: string;
  whatTheyDoNotKnow: string;
  isCulprit: boolean;
  imageUrl: string | null | undefined;
};

export type MysteryEvent = Row & {
  name: string;
  description: string;
  imageUrl: string | null | undefined;
};

export type MysteryLocation = Row & {
  name: string;
  description: string;
  imageUrl: string | null | undefined;
};

export type MysteryClue = Row & {
  name: string;
  description: string;
  imageUrl: string | null | undefined;
  location: MysteryLocation | null | undefined;
  hasBeenFound: boolean;
};

type StringRecordId = `${TableName}:${string}`;
type RecordId = {
  id: string;
  table: TableName;
  toString: () => StringRecordId;
  toJson: () => any;
};

type Row = {
  id?: string | null | undefined;
  creator: User | null | undefined;
  _modifiedDate?: string | null | undefined;
  _date?: string | null | undefined;
};

const zRow = z.object({
  id: z.string({ description: "rows" }),
  creator: z.string(),
  _modifiedDate: z.string().optional(),
  _date: z.string().optional(),
});

export type Language = "en" | "fr" | "ar";

export type AiContextMessage = {
  role: "system" | "assistant" | "user";
  content: string;
};

export type CardType = keyof typeof cardTypes;

export type MigratedItem = Row & {
  oldTable: string;
  oldValue?: string;
  oldId: string;
  newItem: any;
};

export type MigrationSession = Row & {
  dbName: string | null | undefined;
  status: "pending" | "done" | "error";
  phase: MigrationPhase;
  itemsCount: number;
  totalItemsCount: number;
  startDatetime: string;
  currentStartDatetime?: string;
  currentEndDatetime?: string;
  shouldAddItems: boolean;
  shouldAddPatients: boolean;
  shouldAddTasks: boolean;
  shouldUpdateWaitingLists: boolean;
};

export type MigrationPhase = "users" | "patients" | "items" | "tasks";
export const migrationPhases: Record<MigrationPhase, string> = {
  users: "Utilisateurs",
  patients: "Patients",
  items: "Items",
  tasks: "Tâches",
};

//
//
//! ///////////////////////////////////////
//! ////////    DATA SERVER ///////////////////
//! ///////////////////////////////////////
//

export type ServerData = {
  ip?: string;
  port?: string;
  loggedInUser?: User | null | undefined;
  referentielConfig: RemedConfig | null | undefined;
};

export type ServerSessionData = {
  userId?: string | null | undefined;
};

//
//
//! ///////////////////////////////////////
//! ////////    USER ///////////////////
//! ///////////////////////////////////////
//

export const allGrades: Record<
  Grade,
  {
    id: Grade;
    name: string;
  }
> = {
  Dr: {
    id: "Dr",
    name: "Docteur",
  },
  Pr: {
    id: "Pr",
    name: "Professeur",
  },
  Résident: {
    id: "Résident",
    name: "Résident",
  },
  Assitant: {
    id: "Assitant",
    name: "Assitant",
  },
  MCA: {
    id: "MCA",
    name: "Maître de conférences A",
  },
  MCB: {
    id: "MCB",
    name: "Maître de conférences B",
  },
  Externe: {
    id: "Externe",
    name: "Externe",
  },
  Interne: {
    id: "Interne",
    name: "Interne",
  },
  Autre: {
    id: "Autre",
    name: "Autre",
  },
} as const;

export type Grade =
  | "Dr"
  | "Pr"
  | "Externe"
  | "Interne"
  | "Résident"
  | "Assitant"
  | "MCA"
  | "MCB"
  | "Autre";

export type User = Row & {
  firstName: string;
  lastName: string;
  sex: "homme" | "femme";
  email: string;
  address: string;
  token: string | null | undefined;
  lastTokenDate: string | null | undefined;
  phoneNumber: string;
  password: string;
  profilePic: Upload | null | undefined;
  grade: Grade;
  lastOnlineDate?: string;
  patientId?: string | null | undefined;
  role: Role | null | undefined;
  workingPlan?: WorkingPlan | null | undefined;
  speciality: Speciality | null | undefined;
  service?: Service | null | undefined;
  permissions: (Permission | null | undefined)[];
  interdictions: (Permission | null | undefined)[];
  appointmentTypes: (AppointmentType | null | undefined)[];

  canValidateTypes: (AppointmentType | null | undefined)[] | null | undefined;
  canScheduleTypes: (AppointmentType | null | undefined)[] | null | undefined;

  remedLicence?: string | null | undefined;
  isValidated?: boolean;
  favouriteCalendars?: (User | null | undefined)[] | null | undefined;
  preferredBox?: Box | null | undefined;
  structureUnit?: StructureUnit | null | undefined;
};

export const xUser = defineSchema({
  tableName: "users",
  columns: {
    ...xRow.columns,

    firstName: {
      type: "string",
    },
    lastName: {
      type: "string",
    },
    sex: {
      type: "string",
    },
    email: {
      type: "string",
    },
    address: {
      type: "string",
    },
    token: {
      type: "string",
    },
    lastTokenDate: {
      type: "string",
    },
    phoneNumber: {
      type: "string",
    },
    password: {
      type: "string",
    },
    profilePic: {
      type: "string",

      isForeignKeyOf: "uploads",
    },
    grade: {
      type: "string",
    },
    lastOnlineDate: {
      type: "string",
    },
    patientId: {
      type: "string",
    },
    role: {
      type: "string",
      isForeignKeyOf: "roles",
    },
    workingPlan: {
      type: "string",
      isForeignKeyOf: "workingPlans",
    },
    speciality: {
      type: "string",
      isForeignKeyOf: "specialities",
    },
    service: {
      type: "string",
      isForeignKeyOf: "services",
    },
    permissions: {
      type: "string",
      isArray: true,
    },
    interdictions: {
      type: "string",
      isArray: true,
    },
    appointmentTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },

    canValidateTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },
    canScheduleTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },
    remedLicence: {
      type: "string",
    },
    isValidated: {
      type: "boolean",
    },
    favouriteCalendars: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
    preferredBox: {
      type: "string",

      isForeignKeyOf: "boxes",
    },
    structureUnit: {
      type: "string",

      isForeignKeyOf: "units",
    },
  },
} as const);

export type DocumentsTheme =
  | "default"
  | "thickBorders"
  | "curvy"
  | "fullColors";

export type ThemeColor = "blue" | "green" | "red" | "purple" | "orange";
export const themeColors: Record<
  ThemeColor,
  {
    id: ThemeColor;
    name: string;
  }
> = {
  blue: {
    id: "blue",
    name: "Bleu",
  },
  green: {
    id: "green",
    name: "Vert",
  },
  red: {
    id: "red",
    name: "Rouge",
  },
  purple: {
    id: "purple",
    name: "Violet",
  },
  orange: {
    id: "orange",
    name: "Orange",
  },
};

export const documentsThemes: Record<
  DocumentsTheme,
  { id: DocumentsTheme; name: string }
> = {
  default: { id: "default", name: "Défaut" },
  curvy: { id: "curvy", name: "Courbes" },
  thickBorders: { id: "thickBorders", name: "Bords épais" },
  fullColors: { id: "fullColors", name: "Couleurs pleines" },
};

export type HeaderType =
  | "none"
  | "default"
  | "empty"
  | "textLeft"
  | "textLeftAndRightImageCenter"
  | "textLeftImageCenterDateRight"
  | "textRight"
  | "textCenter"
  | "noBarcode";

export const headerTypes: Record<HeaderType, { id: HeaderType; name: string }> =
  {
    default: { id: "default", name: "Défaut" },
    textLeft: { id: "textLeft", name: "Texte à gauche" },
    textRight: { id: "textRight", name: "Texte à droite" },
    textCenter: { id: "textCenter", name: "Texte au centre" },
    textLeftAndRightImageCenter: {
      id: "textLeftAndRightImageCenter",
      name: "Texte à gauche et droite avec image au centre",
    },
    textLeftImageCenterDateRight: {
      id: "textLeftImageCenterDateRight",
      name: "Texte à gauche avec image au centre et date à droite",
    },
    noBarcode: { id: "noBarcode", name: "Sans code barre" },
    none: { id: "none", name: "Aucun" },
    empty: { id: "empty", name: "Vide" },
  };

export type footerType =
  | "none"
  | "default"
  | "empty"
  | "textLeft"
  | "textRight"
  | "textCenter";

export const footerTypes: Record<footerType, { id: footerType; name: string }> =
  {
    none: { id: "none", name: "Aucun" },
    default: { id: "default", name: "Défaut" },
    empty: { id: "empty", name: "Vide" },
    textLeft: { id: "textLeft", name: "Texte à gauche" },
    textRight: { id: "textRight", name: "Texte à droite" },
    textCenter: { id: "textCenter", name: "Texte au centre" },
  };

export type UserLight = Pick<User, "id" | "firstName" | "lastName" | "grade">;

export type Speciality = Row & {
  name: string;
  secondaryName: string;

  frequentScores?: (Score | null | undefined)[];
  frequentReferences?: (Reference | null | undefined)[];
  frequentMedicines?: (Medicine | null | undefined)[];
  frequentTemplates?: (DocumentTemplate | null | undefined)[];
  frequentOrdonnanceTemplates?: (OrdonnanceTemplate | null | undefined)[];
  frequentReferenceGroups?: (ReferencesGroup | null | undefined)[];
  initialExaminationFormulaires?: (Reference | null | undefined)[];
  evolutionFormulaires?: (Reference | null | undefined)[];
  admissionFormulaires?: (Reference | null | undefined)[];
  shiftFormulaires?: (Reference | null | undefined)[];
};

export const xSpeciality = defineSchema<Speciality>({
  tableName: "specialities",
  columns: {
    ...xRow.columns,
    frequentReferenceGroups: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "referencesGroups",
    },
    frequentOrdonnanceTemplates: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ordonnanceTemplates",
    },
    name: {
      type: "string",
    },
    secondaryName: {
      type: "string",
    },
    frequentScores: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "scores",
    },
    frequentReferences: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    frequentMedicines: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "medicines",
    },
    frequentTemplates: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "documentTemplates",
    },
    initialExaminationFormulaires: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    evolutionFormulaires: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    admissionFormulaires: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    shiftFormulaires: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
  },
} as const);

export type Permission =
  | "mysteryPage"
  | "medLegDashboardPage"
  | "ecgDashboardPage"
  | "ecgsPage"
  | "ecgUsersPage"
  | "ecgProfilePage"
  | "patientPage"
  | "patientsPage"
  | "notificationsPage"
  | "waitingRoomsPage"
  | "explorationsPage"
  | "hospitalisationsPage"
  | "dashboardPage"
  | "adminDashboardPage"
  | "statisticsPage"
  | "calendarPage"
  | "anapathPage"
  | "anapathTasksPage"
  | "documentsPage"
  | "humanResourcesPage"
  | "laboratoireDashboardPage"
  | "pharmacyDashboardPage"
  | "pharmacyServicesPage"
  | "pharmacyCategoriesPage"
  | "pharmacyLocationsPage"
  | "pharmacyImputationsPage"
  | "pharmacyCommandsPage"
  | "pharmacyEntriesPage"
  | "pharmacySuppliersPage"
  | "pharmacyBrandsPage"
  | "pharmacyTendersPage"
  | "pharmacyProductsPage"
  | "pharmacyReleasesPage"
  | "pharmacyRecapsPage"
  | "stocksPage"
  | "inventoryPage"
  | "accountingPage"
  | "feedPage"
  | "messageriePage"
  | "admissionsPage"
  | "usersPage"
  | "rolesPage"
  | "profilePage"
  | "configurationPage"
  | "ticketsPage"
  | "serverPollsPage"
  | "structuresPage"
  | "referentielPage"
  | "loginPage"
  | "openWaitingRoomAppointment"
  | "nurseView"
  | "antecedents"
  | "admissionDocuments"
  | "ecrfFichesPage"
  | "ecrfDashboardPage"
  | "ecrfValidationPage"
  | "ecrfForms"
  | "ecrfUsersPage";

export type Role = Row & {
  id?: string;
  name: string;
  color: string;
  description: string;
  permissions: (Permission | null | undefined)[];
  initialPage: Permission | null | undefined;
};

export const xRole = defineSchema<Role>({
  tableName: "roles",

  columns: {
    ...xRow.columns,
    name: {
      type: "string",
    },
    color: {
      type: "string",
    },
    description: {
      type: "string",
    },
    permissions: {
      type: "string",
      isArray: true,
    },
    initialPage: {
      type: "string",
    },
  },
} as const);

export type FrequentRole =
  | "doctor"
  | "reception"
  | "biologie"
  | "rh"
  | "Secrétariat chef de service"
  | "radiology_reception"
  | "private_doctor"
  | "waiting_screen"
  | "nurse"
  | "lab"
  | "patient"
  | "imagerie"
  | "pharmacie"
  | "reception_urgences"
  | "admissions"
  | "cardiologue"
  | "administrateur"
  | "technicien_anapath";

export const frequentRoles: Record<FrequentRole, Partial<Role>> = {
  administrateur: {
    id: "roles:1718842166511433dko32jl",
    name: "administrateur",
    color: "purple",
  },
  doctor: {
    id: "roles:MA__",
    color: "blue",
    name: "doctor",
  },
  reception: {
    id: "roles:MQ__",
    color: "orange",
    name: "reception",
  },
  biologie: {
    id: "roles:MTU_",
    color: "purple",
    name: "biologie",
  },
  rh: {
    id: "roles:MTY2ODk0MDMzMzkzMzYyNjQ_",
    color: "orange",
    name: "rh",
  },
  "Secrétariat chef de service": {
    id: "roles:MTY3MDA5MzU2Mjc5ODQzNzI_",
    color: "orange",
    name: "Secrétariat chef de service",
  },
  radiology_reception: {
    id: "roles:MTY3MTM5NzEyNjA1ODM3MDA_",
    color: "orange",
    name: "radiology_reception",
  },
  private_doctor: {
    id: "roles:Mg__",
    color: "blue",
    name: "private_doctor",
  },
  waiting_screen: {
    id: "roles:Mw__",
    color: "orange",
    name: "waiting_screen",
  },
  nurse: {
    id: "roles:MzE_",
    color: "green",
    name: "nurse",
  },
  lab: {
    id: "roles:NQ__",
    color: "purple",
    name: "lab",
  },
  patient: {
    id: "roles:Njk_",
    color: "red",
    name: "patient",
  },
  imagerie: {
    id: "roles:NzA_",
    color: "blue",
    name: "imagerie",
  },
  pharmacie: {
    id: "roles:NzE_",
    color: "purple",
    name: "pharmacie",
  },
  reception_urgences: {
    id: "roles:NzI_",
    color: "orange",
    name: "reception_urgences",
  },
  admissions: {
    id: "roles:NzM_",
    color: "orange",
    name: "admissions",
  },
  cardiologue: {
    id: "roles:1718840693056kczpz9tbki",
    color: "red",
    name: "cardiologue",
  },
  technicien_anapath: {
    id: "roles:1739960735653dzebxaaks",
    color: "green",
    name: "Technicien anapath",
  },
} as const;

export type WorkingPlan = Row & {
  fridayStart?: string;
  fridayEnd?: string;
  saturdayStart?: string;
  saturdayEnd?: string;
  sundayStart?: string;
  sundayEnd?: string;
  mondayStart?: string;
  mondayEnd?: string;
  tuesdayStart?: string;
  tuesdayEnd?: string;
  wednesdayStart?: string;
  wednesdayEnd?: string;
  thursdayStart?: string;
  thursdayEnd?: string;
};

export const xWorkingPlan = defineSchema<WorkingPlan>({
  tableName: "workingPlans",
  columns: {
    ...xRow.columns,

    fridayStart: {
      type: "string",
    },
    fridayEnd: {
      type: "string",
    },
    saturdayStart: {
      type: "string",
    },
    saturdayEnd: {
      type: "string",
    },
    sundayStart: {
      type: "string",
    },
    sundayEnd: {
      type: "string",
    },
    mondayStart: {
      type: "string",
    },
    mondayEnd: {
      type: "string",
    },
    tuesdayStart: {
      type: "string",
    },
    tuesdayEnd: {
      type: "string",
    },
    wednesdayStart: {
      type: "string",
    },
    wednesdayEnd: {
      type: "string",
    },
    thursdayStart: {
      type: "string",
    },
    thursdayEnd: {
      type: "string",
    },
  },
} as const);

export type TextHistory = Row & {
  id?: string;
  inputId: string | null | undefined;
  name: string;
  value: string;
};

export const xTextHistory = defineSchema<TextHistory>({
  tableName: "textHistories",
  columns: {
    ...xRow.columns,

    inputId: {
      type: "string",
    },
    name: {
      type: "string",
    },
    value: {
      type: "string",
    },
  },
} as const);

//
//
//! ///////////////////////////////////////
//! //////// CONFIDENTIALITY ///////////////////
//! ///////////////////////////////////////
//
//

export type Confidentiality = Row & {
  type: ConfidentialityType;
  patient: Patient | null | undefined;
  demands: (ConfidentialityDemand | null | undefined)[] | null | undefined;
  users: (User | null | undefined)[] | null | undefined;
  services: (Service | null | undefined)[] | null | undefined;
  units: (StructureUnit | null | undefined)[] | null | undefined;
  specialities: (Speciality | null | undefined)[] | null | undefined;
};

export type ConfidentialityDemand = Row & {
  status: ConfidentialityDemandStatus;
  grantedBy: User | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  parentId: string | null | undefined;
  user: User | null | undefined;
};

export const xConfidentiality = defineSchema<Confidentiality>({
  tableName: "confidentialities",
  columns: {
    ...xRow.columns,

    type: {
      type: "string",
    },
    patient: {
      type: "string",
      isForeignKeyOf: "patients",
    },
    demands: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "confidentialityDemands",
    },
    users: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
    services: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "services",
    },
    units: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "units",
    },
    specialities: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "specialities",
    },
  },
} as const);

export const xConfidentialityDemand = defineSchema<ConfidentialityDemand>({
  tableName: "confidentialityDemands",
  columns: {
    ...xRow.columns,

    status: {
      type: "string",
    },
    grantedBy: {
      type: "string",
      isForeignKeyOf: "users",
    },
    startDate: {
      type: "string",
    },
    endDate: {
      type: "string",
    },
    parentId: {
      type: "string",
    },
    user: {
      type: "string",
      isForeignKeyOf: "users",
    },
  },
} as const);

export type ConfidentialityDemandStatus = "pending" | "granted" | "refused";
export const confidentialityDemandStatuses: Record<
  ConfidentialityDemandStatus,
  {
    id: ConfidentialityDemandStatus;
    name: string;
    color: "blue" | "green" | "red";
  }
> = {
  pending: {
    id: "pending",
    color: "blue",
    name: "En attente",
  },
  granted: {
    id: "granted",
    color: "green",
    name: "Accordé",
  },
  refused: {
    id: "refused",
    color: "red",
    name: "Refusé",
  },
};

export type ConfidentialityType =
  | "all"
  | "users"
  | "services"
  | "units"
  | "specialities";

export const confidentialityTypes: Record<
  ConfidentialityType,
  {
    id: ConfidentialityType;
    name: string;
  }
> = {
  all: {
    id: "all",
    name: "Tous",
  },
  users: {
    id: "users",
    name: "Utilisateurs",
  },
  services: {
    id: "services",
    name: "Services",
  },
  units: {
    id: "units",
    name: "Unités",
  },
  specialities: {
    id: "specialities",
    name: "Spécialités",
  },
};

//
//! ///////////////////////////////////////
//! ////////    PATIENT ITEMS ///////////////////
//! ///////////////////////////////////////
//

export type Patient = Row & {
  shortId?: string;
  recordId?: string; // physical record id
  recordCreationDate?: string; // physical record creation date
  firstName?: string;
  firstNameAr?: string;
  lastName?: string;
  lastNameAr?: string;
  maidenName?: string;
  searchInfo?: string;
  searchInfoAr?: string;
  birth?: string;
  birthHour?: string;
  presumed?: boolean;
  birthTerm?: number; // in weeks
  sex?: "homme" | "femme";
  address?: string;
  addressAr?: string;
  nationality?: string;
  wilaya?: string;
  commune?: string;
  phoneNumber?: string;
  secondaryPhoneNumber?: string;
  email?: string;
  profilePic?: Upload | null | undefined;
  carceralStatus?: "" | "libre" | "détenu" | "gardé à vue";
  deathStatus?: "" | "vivant" | "décédé";
  deathDate?: string;
  deathCause?: string;
  familyMemberFullName?: string;
  familyMemberPhoneNumber?: string;
  familyMemberAddress?: string;

  maritalStatus?: "" | "célibataire" | "marié(e)" | "divorcé(e)" | "veuf/veuve";
  fatherName?: string;
  motherName?: string;
  spouseName?: string;
  childrenCount?: string;
  profession?: string;
  bloodType?: "A+" | "A-" | "B+" | "B-" | "AB+" | "AB-" | "O+" | "O-";
  socialSecurityNumber?: string;
  assuranceAffiliation?: AssuranceAffiliation;
  nin?: string;

  confidentiality: Confidentiality | null | undefined;
  services: (Service | null | undefined)[] | null | undefined;

  familyMembers: (FamilyMember | null | undefined)[] | null | undefined;
  isFamilyMember?: boolean;
  notes?: string;
  minAge?: string;
  maxAge?: string;
};

export const xPatient_fts = defineSchema({
  tableName: "patients_fts",
  columns: {
    id: {
      type: "string",
      isPrimaryKey: true,
    },
    searchInfo: {
      type: "string",
    },
  },
} as const);

export const assuranceAffiliations: Record<
  string,
  { id: string; name: string }
> = {
  "Non assuré": {
    id: "Non assuré",
    name: "Non assuré",
  },
  CNAS: {
    id: "CNAS",
    name: "CNAS",
  },
  CASNOS: {
    id: "CASNOS",
    name: "CASNOS",
  },
  "Caisse militaire": {
    id: "Caisse militaire",
    name: "Caisse militaire",
  },
  "Solidarité nationale": {
    id: "Solidarité nationale",
    name: "Solidarité nationale",
  },
} as const;

export type AssuranceAffiliation = keyof typeof assuranceAffiliations;

export type FamilyMember = Row & {
  relationship: FamilyMemberRelationship;
  patient: Patient | null | undefined;
};

export type FamilyMemberRelationship =
  | "père"
  | "mère"
  | "grand_père"
  | "grand_mère"
  | "frère"
  | "soeur"
  | "fils"
  | "fille"
  | "cousin"
  | "oncle"
  | "tante"
  | "cousine"
  | "neveu"
  | "nièce";

export const familyMemberRelationships: Record<
  FamilyMemberRelationship,
  {
    id: FamilyMemberRelationship;
    name: string;
    color: "blue" | "purple";
  }
> = {
  père: {
    id: "père",
    name: "Père",
    color: "blue",
  },
  mère: {
    id: "mère",
    name: "Mère",
    color: "purple",
  },
  grand_père: {
    id: "grand_père",
    name: "Grand-père",
    color: "blue",
  },
  grand_mère: {
    id: "grand_mère",
    name: "Grand-mère",
    color: "purple",
  },

  fils: {
    id: "fils",
    name: "Fils",
    color: "blue",
  },
  fille: {
    id: "fille",
    name: "Fille",
    color: "purple",
  },
  frère: {
    id: "frère",
    name: "Frère",
    color: "blue",
  },
  soeur: {
    id: "soeur",
    name: "Soeur",
    color: "purple",
  },
  cousin: {
    id: "cousin",
    name: "Cousin",
    color: "blue",
  },
  cousine: {
    id: "cousine",
    name: "Cousine",
    color: "purple",
  },
  oncle: {
    id: "oncle",
    name: "Oncle",
    color: "blue",
  },
  tante: {
    id: "tante",
    name: "Tante",
    color: "purple",
  },

  neveu: {
    id: "neveu",
    name: "Neveu",
    color: "blue",
  },
  nièce: {
    id: "nièce",
    name: "Nièce",
    color: "purple",
  },
};

export type PatientLight = Pick<
  Patient,
  | "id"
  | "firstName"
  | "lastName"
  | "birth"
  | "confidentiality"
  | "services"
  | "_date"
>;

export type Upload = Row & {
  field?: Field;
  parent?: RefAnswer | null | undefined;
  thumbnail?: string;
  name: string;
  url: string | null;
  patient: Patient | null | undefined;
  original_name: string;
  description?: string | null | undefined;
  fileType: "image" | "video" | "audio" | "document" | "other";
  confidentiality?: Confidentiality | null | undefined;
  progress?: number;
  compressionProgress?: number;
  error?: string | null;
};

export type DicomReception = Row & {
  filePath: string;
  patient: Patient | null | undefined;
  type: "radiography" | "ct" | "MRImageStorage" | "ultrasound" | "other" | any;
};

export const zUpload = z.object({
  ...zRow.shape,
  field: z.string().optional(),
  parent: z.string().optional(),
  thumbnail: z.string().optional(),
  name: z.string(),
  url: z.string().optional(),
  patient: z.string().optional(),
  original_name: z.string(),
  description: z.string().optional(),
  fileType: z.enum(["image", "video", "audio", "document", "other"]),
  confidentiality: z.string().optional(),
  progress: z.number().optional(),
  compressionProgress: z.number().optional(),
  error: z.string().optional(),
});

export type RefAnswer = Row & {
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[];
  reference?: Reference | null | undefined;
  patient: Patient | null | undefined;
  fieldAnswers: (FieldAnswer | null | undefined)[];
  confidentiality?: Confidentiality | null | undefined;
  isAbsent?: boolean;
  status?: PatientItemStatus;
};

export type FieldAnswer = Row & {
  value?: any;
  field?: Field;
  parent: RefAnswer | null | undefined;
  patient: Patient | null | undefined;

  //
  status?: PatientItemStatus;
  start?: string;
  end?: string;
  finishedDate?: string | null | undefined;
  cancelDate?: string | null | undefined;
  doctor?: User | null | undefined;
  notes?: string | null | undefined;
};

export const patientColumnsNames: Record<keyof Patient, string> = {
  shortId: "Identifiant",
  firstName: "Prénom",
  lastName: "Nom",
  firstNameAr: "Prénom (ar)",
  lastNameAr: "Nom (ar)",
  maidenName: "Nom de jeune fille",
  searchInfo: "Nom complet",
  searchInfoAr: "Nom complet (ar)",
  birth: "Date de naissance",
  birthTerm: "Terme de naissance",
  birthHour: "Heure de naissance",
  presumed: "Présumé",
  sex: "Sexe",
  address: "Adresse",
  addressAr: "Adresse (ar)",

  nationality: "Nationalité",
  wilaya: "Wilaya",
  commune: "Commune",
  phoneNumber: "Numéro de téléphone",
  secondaryPhoneNumber: "Numéro de téléphone secondaire",
  email: "Email",
  carceralStatus: "Statut carcéral",
  deathStatus: "Statut de décès",
  deathDate: "Date de décès",
  deathCause: "Cause du décès",
  familyMemberFullName: "Personne de confiance",
  familyMemberPhoneNumber: "Numéro de téléphone de la personne de confiance",
  familyMemberAddress: "Adresse de la personne de confiance",
  maritalStatus: "Statut marital",
  fatherName: "Nom du père",
  motherName: "Nom de la mère",
  spouseName: "Nom du conjoint",
  childrenCount: "Nombre d'enfants",
  profession: "Profession",
  bloodType: "Groupage",
  socialSecurityNumber: "Numéro de sécurité sociale",
  assuranceAffiliation: "Affiliation à l'assurance",
  nin: "Numéro d'identification national",
  id: "Identifiant",
  recordId: "Numéro de dossier",
  recordCreationDate: "Date de création du dossier",
  profilePic: "Photo",
  familyMembers: "Famille",
  isFamilyMember: "Membre de la famille",
  notes: "Notes générales",
  minAge: "Âge minimum",
  maxAge: "Âge maximum",

  services: "Services",
  confidentiality: "Confidentialité",
  creator: "Créateur",
  _date: "Date de création",
  _modifiedDate: "Date de modification",
};

//
//! ///////////////////////////////////////
//! ////////    Documents ///////////////////
//! ///////////////////////////////////////
//

export type DocumentTemplate = Row & {
  name: string;
  title: string;
  type?: DocumentTemplateType | null;
  content: string;
  showHeader: boolean;
  showLeftBar: boolean;
  barcodeLocation: BarcodeLocation | null | undefined;
  showTitle: boolean;
  showPatientIdentity: boolean;
  format: DocumentFormat;
  registryName?: string | null | undefined;
  isValidatable: boolean | null | undefined;
  parent: DocumentTemplate | null | undefined;
  reference?: Reference | null | undefined;
};

export type BarcodeLocation = "none" | "title" | "margin" | "header" | "footer";
export const barcodeLocations: Record<
  BarcodeLocation,
  {
    id: BarcodeLocation;
    name: string;
  }
> = {
  none: {
    id: "none",
    name: "Aucun",
  },
  title: {
    id: "title",
    name: "Titre",
  },
  margin: {
    id: "margin",
    name: "Marge",
  },
  header: {
    id: "header",
    name: "En-tête",
  },
  footer: {
    id: "footer",
    name: "Pied de page",
  },
};

export type DocumentFormat = "a4" | "a5";

export type DocumentTemplateType =
  | "standard"
  | "admission"
  | "statistics"
  | "letter";

export const documentTemplateTypes: Record<
  DocumentTemplateType,
  {
    id: DocumentTemplateType;
    name: string;
  }
> = {
  standard: {
    id: "standard",
    name: "Standard",
  },
  admission: {
    id: "admission",
    name: "Admission",
  },
  statistics: {
    id: "statistics",
    name: "Statistiques",
  },
  letter: {
    id: "letter",
    name: "Courrier",
  },
};

export type CompletionType = "summary" | "interpretation" | "conclusion";

export const completionTypes: Record<
  CompletionType,
  {
    id: CompletionType;
    name: string;
  }
> = {
  summary: {
    id: "summary",
    name: "Compte rendu par IA",
  },
  interpretation: {
    id: "interpretation",
    name: "Interprétation par IA",
  },
  conclusion: {
    id: "conclusion",
    name: "Conclusion par IA",
  },
};

export type PrintingDemand = Row & {
  printDate?: string | null | undefined;

  service: Service | null | undefined;
  patientDocument?: PatientDocument | null | undefined;
  status: PatientItemStatus;
};

export type PatientDocument = Row & {
  content: string;
  title?: string;

  registryNumber?: number | null | undefined;
  patient: Patient | null | undefined;

  parent?: FolderItem | null | undefined;
  template?: DocumentTemplate | null | undefined;
  refAnswer: RefAnswer | null | undefined; //done
  status: "pending" | "saved" | "validated" | "printed";
  isValidatedBy?: User | null | undefined;
  confidentiality?: Confidentiality | null | undefined;
  initialCreator?: User | null | undefined;
};

export type PatientDocumentLight = Pick<
  PatientDocument,
  | "id"
  | "creator"
  | "confidentiality"
  | "registryNumber"
  | "status"
  | "template"
  | "isValidatedBy"
  | "initialCreator"
  | "title"
  | "_date"
> & {
  creator: UserLight | null | undefined;
};

export type DocumentVariable = Row & {
  id: string;
  name: string;
  value: string | null | undefined;
  category: "documentVariable";
  source:
    | "manualVariables"
    | "statistics"
    | "condition"
    | "simpleHtml"
    | "fieldTitle"
    | "fieldValue"
    | "patientDocument"
    | "patient"
    | "user"
    | "section";
  format: DocumentVariableFormat;
  fieldId: string | null | undefined;
  sectionId: string | null | undefined;
  nullValue: string | null | undefined;

  statisticsReport?: StatisticsReport | null | undefined;

  conditionGroup?: ConditionGroup | null | undefined;
  conditionReplacement?: string | null | undefined;
  conditionElseReplacement?: string | null | undefined;

  creator: string | null | undefined;
  _date?: string | null | undefined;
};

export type ManualVariable =
  | "BED_NAME"
  | "SERVICE_NAME"
  | "EXIT_MODE"
  | "ENTRY_MODE"
  | "EXIT_DATE"
  | "ENTRY_DATE"
  | "ADMISSION_NUMBER"
  | "PATIENT_DIAGNOSTICS";

export const manualVariables: Record<
  ManualVariable,
  {
    label: string;
    value: string;
  }
> = {
  BED_NAME: {
    label: "Nom du lit",
    value: "BED_NAME",
  },
  SERVICE_NAME: {
    label: "Nom du service",
    value: "SERVICE_NAME",
  },
  EXIT_MODE: {
    label: "Mode de sortie",
    value: "EXIT_MODE",
  },
  ENTRY_MODE: {
    label: "Mode d'entrée",
    value: "ENTRY_MODE",
  },
  EXIT_DATE: {
    label: "Date de sortie",
    value: "EXIT_DATE",
  },
  ENTRY_DATE: {
    label: "Date d'entrée",
    value: "ENTRY_DATE",
  },
  ADMISSION_NUMBER: {
    label: "N° d'admission",
    value: "ADMISSION_NUMBER",
  },
  PATIENT_DIAGNOSTICS: {
    label: "Diagnostics",
    value: "PATIENT_DIAGNOSTICS",
  },
} as const;

export type VariableTemplate = Row & {
  name: string;
  content: string;
  category: "standard";
};

export type DocumentVariableFormat =
  | "text"
  | "number"
  | "LL"
  | "HH:mm"
  | "LL HH:mm"
  | "DD/MM/YYYY"
  | "DD/MM/YYYY HH:mm"
  | "YYYY"
  | "ageDays"
  | "ageMonths"
  | "ageYears"
  | "capitalize"
  | "capitalizeFirst";

export const documentVariableFormats: Record<
  DocumentVariableFormat,
  {
    id: DocumentVariableFormat;
    name: string;
    exec: (a: any) => any;
  }
> = {
  text: {
    id: "text",
    name: "Texte",
    exec: (a) => a,
  },

  number: {
    id: "number",
    name: "Nombre",
    exec: (a) => parseInt(a?.toString() || "0"),
  },
  LL: {
    id: "LL",
    name: "Date (LL)",
    exec: (a) => moment(a).format("LL"),
  },

  "DD/MM/YYYY": {
    id: "DD/MM/YYYY",
    name: "Date  (DD/MM/YYYY)",
    exec: (a) => moment(a).format("DD/MM/YYYY"),
  },

  "LL HH:mm": {
    id: "LL HH:mm",
    name: "Date et heure  (LL HH:mm)",
    exec: (a) => moment(a).format("LL HH:mm"),
  },
  "DD/MM/YYYY HH:mm": {
    id: "DD/MM/YYYY HH:mm",
    name: "Date et heure (DD/MM/YYYY HH:mm)",
    exec: (a) => moment(a).format("DD/MM/YYYY HH:mm"),
  },
  "HH:mm": {
    id: "HH:mm",
    name: "Heure (HH:mm)",
    exec: (a) => moment(a).format("HH:mm"),
  },
  YYYY: {
    id: "YYYY",
    name: "Année (YYYY)",
    exec: (a) => moment(a).format("YYYY"),
  },
  ageDays: {
    id: "ageDays",
    name: "Age en jours",
    exec: (a) => moment().diff(a, "days"),
  },
  ageMonths: {
    id: "ageMonths",
    name: "Age en mois",
    exec: (a) => moment().diff(a, "months"),
  },
  ageYears: {
    id: "ageYears",
    name: "Age en années",
    exec: (a) => moment().diff(a, "years"),
  },
  capitalize: {
    id: "capitalize",
    name: "Majuscule",
    exec: (a) => a?.toUpperCase(),
  },
  capitalizeFirst: {
    id: "capitalizeFirst",
    name: "Majuscule première lettre",
    exec: (a) => a?.charAt(0).toUpperCase() + a?.slice(1),
  },
};

//
//! ///////////////////////////////////////
//! ////////    CONDITIONS ///////////////////
//! ///////////////////////////////////////
//
export type ConditionGroup = Row & {
  id: string;
  name: string;
  category: "conditionGroup";
  type: "and" | "or";
  conditions: (DocumentCondition | ConditionGroup)[];

  _date?: string | null | undefined;
};

export type DocumentCondition = Row & {
  id: string;
  category: "condition";
  a: string;
  operator: ConditionOperator;
  b: string;
};

export type ConditionOperator =
  | "="
  | "!="
  | ">"
  | "<"
  | ">="
  | "<="
  | "isNull"
  | "isNotNull"
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "isTrue"
  | "isFalse";

export const conditionOperators: Record<
  ConditionOperator,
  {
    id: ConditionOperator;
    name: string;
    exec: (a: any, b: any) => boolean;
  }
> = {
  "=": {
    id: "=",
    name: "Egal à",
    exec: (a, b) => a === b,
  },
  "!=": {
    id: "!=",
    name: "Différent de",
    exec: (a, b) => a !== b,
  },
  ">": {
    id: ">",
    name: "Supérieur à",
    exec: (a, b) => parseFloat(a || 0) > parseFloat(b || 0),
  },
  "<": {
    id: "<",
    name: "Inférieur à",
    exec: (a, b) => parseFloat(a || 0) < parseFloat(b || 0),
  },
  ">=": {
    id: ">=",
    name: "Supérieur ou égal à",
    exec: (a, b) => parseFloat(a || 0) >= parseFloat(b || 0),
  },
  "<=": {
    id: "<=",
    name: "Inférieur ou égal à",
    exec: (a, b) => parseFloat(a || 0) <= parseFloat(b || 0),
  },
  isNull: {
    id: "isNull",
    name: "Est nul",
    exec: (a, b) => a == null || a?.trim() == "",
  },
  isNotNull: {
    id: "isNotNull",
    name: "N'est pas nul",
    exec: (a, b) => a != null,
  },
  contains: {
    id: "contains",
    name: "Contient",
    exec: (a, b) => a?.includes(b || ""),
  },
  notContains: {
    id: "notContains",
    name: "Ne contient pas",
    exec: (a, b) => !a?.includes(b || ""),
  },
  startsWith: {
    id: "startsWith",
    name: "Commence par",
    exec: (a, b) => a?.startsWith(b || ""),
  },
  endsWith: {
    id: "endsWith",
    name: "Finit par",
    exec: (a, b) => a?.endsWith(b || ""),
  },
  isTrue: {
    id: "isTrue",
    name: "Est vrai",
    exec: (a, b) => a === true || a === "true",
  },
  isFalse: {
    id: "isFalse",
    name: "Est faux",
    exec: (a, b) => a === false || a === "false",
  },
};

//
//! ///////////////////////////////////////
//! ////////    Scores ///////////////////
//! ///////////////////////////////////////
//
export type Score = Row & {
  name: string;
  description: string;
  formula: string;
  formulaType: ScoreFomulaType;

  sections: (FieldsSection | null | undefined)[];
  fieldsIntervals: (ScoreInterval | null | undefined)[];
  intervals: (ScoreInterval | null | undefined)[];
};

export type ScoreFomulaType = "sum" | "average" | "custom";
export const scoreFomulaTypes: Record<
  ScoreFomulaType,
  {
    id: ScoreFomulaType;
    name: string;
  }
> = {
  sum: {
    id: "sum",
    name: "Somme",
  },
  average: {
    id: "average",
    name: "Moyenne",
  },
  custom: {
    id: "custom",
    name: "Formule",
  },
};

export type ScoreInterval = Row & {
  value: any;
  min: number;
  max: number;
  type: "minMax" | "points" | "value";
  points: number;
  interpretation: string;
  color: string;

  field: Field | null | undefined;
};

export type PatientScore = Row & {
  score: Score;
  answers: (FieldAnswer | null | undefined)[];
  interval: ScoreInterval | null | undefined;
  points: number;
  patient: Patient | null | undefined;
  parent?: FolderItem | null | undefined;

  confidentiality?: Confidentiality | null | undefined;
};

//
//
//! ///////////////////////////////////////
//! ////////    Medicines ///////////////////
//! ///////////////////////////////////////
//
//

export type Medicine = Row & {
  name: string;
  dci?: string;
  dosage?: string;
  form: MedicineForm | null | undefined;
  defaultUnit: MedicineUnit;
  defaultRoute: MedicineRoute | null | undefined;
  usageCount?: number;
};

export type MedicineForm =
  | "comprimé"
  | "capsule"
  | "gélule"
  | "suppositoire"
  | "injectable"
  | "patch"
  | "solution"
  | "suspension"
  | "pommade"
  | "crème"
  | "gel"
  | "sirop"
  | "aérosol"
  | "poudre"
  | "inhalateur"
  | "goutte"
  | "collyre"
  | "pastille"
  | "émulsion"
  | "granulé"
  | "baume"
  | "implant"
  | "mousse"
  | "concentré"
  | "disque transdermique"
  | "liniment"
  | "geste";

export type TreatmentCategory =
  | "Per os"
  | "Injectable"
  | "Solutés"
  | "Aerosols"
  | "Gestes";
export const treatmentCategories: Record<
  TreatmentCategory,
  {
    id: TreatmentCategory;

    name: string;
    forms: MedicineForm[];
  }
> = {
  "Per os": {
    id: "Per os",
    name: "Per os",
    forms: [
      "comprimé",
      "capsule",
      "gélule",
      "sirop",
      "poudre",
      "granulé",
      "pastille",
      "concentré",
    ],
  },
  Injectable: {
    id: "Injectable",
    name: "Injectable",
    forms: ["injectable"],
  },
  Solutés: {
    id: "Solutés",
    name: "Solutés",
    forms: ["solution", "suspension", "émulsion"],
  },
  Aerosols: {
    id: "Aerosols",
    name: "Aerosols",
    forms: ["aérosol", "inhalateur"],
  },
  Gestes: {
    id: "Gestes",
    name: "Gestes",
    forms: ["suppositoire", "pommade", "crème", "gel", "liniment"],
  },
};

export const medicineForms: Record<
  MedicineForm,
  {
    id: MedicineForm;
    name: string;
    routes: MedicineRoute[];
  }
> = {
  comprimé: {
    id: "comprimé",
    name: "Comprimé(s)",
    routes: ["orale"],
  },
  capsule: {
    id: "capsule",
    name: "Capsule(s)",
    routes: ["orale"],
  },
  gélule: {
    id: "gélule",
    name: "Gélule(s)",
    routes: ["orale"],
  },
  suppositoire: {
    id: "suppositoire",
    name: "Suppositoire(s)",
    routes: ["rectale"],
  },
  injectable: {
    id: "injectable",
    name: "Injectable(s)",
    routes: [
      "IM",
      "IVL",
      "IVD",
      "SC",
      "intrathécale",
      "endovésicale",
      "entérale",
    ],
  },
  patch: {
    id: "patch",
    name: "Patch(s) transdermique(s)",
    routes: ["transdermique"],
  },
  solution: {
    id: "solution",
    name: "Solution",
    routes: [
      "orale",
      "IM",
      "IVL",
      "IVD",
      "SC",
      "entérale",
      "sublinguale",
      "intrathécale",
      "endovésicale",
      "vaginale",
      "oculaire",
      "auriculaire",
      "nasale",
      "inhalation",
      "nébulisation",
      "cutanée",
    ],
  },
  suspension: {
    id: "suspension",
    name: "Suspension",
    routes: ["orale", "IM", "IVL", "IVD", "SC", "cutanée", "entérale"],
  },
  pommade: {
    id: "pommade",
    name: "Pommade",
    routes: [
      "cutanée",
      "oculaire",
      "auriculaire",
      "nasale",
      "vaginale",
      "rectale",
      "transdermique",
    ],
  },
  crème: {
    id: "crème",
    name: "Crème",
    routes: [
      "cutanée",
      "oculaire",
      "auriculaire",
      "nasale",
      "vaginale",
      "rectale",
      "transdermique",
    ],
  },
  gel: {
    id: "gel",
    name: "Gel",
    routes: [
      "cutanée",
      "oculaire",
      "auriculaire",
      "nasale",
      "vaginale",
      "rectale",
      "transdermique",
    ],
  },
  sirop: {
    id: "sirop",
    name: "Sirop",
    routes: ["orale", "entérale"],
  },
  aérosol: {
    id: "aérosol",
    name: "Aérosol(s)",
    routes: ["inhalation", "nasale", "auriculaire"],
  },
  poudre: {
    id: "poudre",
    name: "Poudre",
    routes: ["orale", "IM", "IVL", "IVD", "SC", "cutanée", "entérale"],
  },
  inhalateur: {
    id: "inhalateur",
    name: "Inhalateur",
    routes: ["inhalation"],
  },
  goutte: {
    id: "goutte",
    name: "Goutte",
    routes: ["orale", "auriculaire", "oculaire", "nasale", "entérale"],
  },
  collyre: {
    id: "collyre",
    name: "Collyre",
    routes: ["oculaire"],
  },
  pastille: {
    id: "pastille",
    name: "Pastille",
    routes: ["orale"],
  },
  émulsion: {
    id: "émulsion",
    name: "Émulsion",
    routes: ["cutanée", "vaginale", "rectale"],
  },
  granulé: {
    id: "granulé",
    name: "Granulé",
    routes: ["orale"],
  },
  baume: {
    id: "baume",
    name: "Baume",
    routes: ["cutanée"],
  },
  implant: {
    id: "implant",
    name: "Implant",
    routes: ["cutanée"],
  },

  mousse: {
    id: "mousse",
    name: "Mousse",
    routes: ["cutanée"],
  },

  concentré: {
    id: "concentré",
    name: "Concentré",
    routes: ["orale", "entérale"],
  },

  "disque transdermique": {
    id: "disque transdermique",
    name: "Disque transdermique",
    routes: ["transdermique"],
  },
  liniment: {
    id: "liniment",
    name: "Liniment",
    routes: ["cutanée"],
  },
  geste: {
    id: "geste",
    name: "Geste",
    routes: ["geste"],
  },
};

export type MedicineRoute =
  | "orale"
  | "IM"
  | "IVD"
  | "IVL"
  | "IVC"
  | "SC"
  | "entérale"
  | "sublinguale"
  | "intrathécale"
  | "endovésicale"
  | "rectale"
  | "vaginale"
  | "oculaire"
  | "auriculaire"
  | "nasale"
  | "inhalation"
  | "nébulisation"
  | "cutanée"
  | "geste"
  | "transdermique";

export const medicineRoutes: Record<
  MedicineRoute,
  {
    id: MedicineRoute;
    name: string;
    shortName: string;
  }
> = {
  orale: {
    id: "orale",
    shortName: "per os",
    name: "Orale",
  },
  IM: {
    id: "IM",
    shortName: "IM",
    name: "Intramusculaire (IM)",
  },
  IVL: {
    id: "IVL",
    shortName: "IVL",
    name: "Intraveineuse lente (IVL)",
  },
  IVD: {
    id: "IVD",
    shortName: "IVD",
    name: "Intraveineuse directe (IVD)",
  },
  IVC: {
    id: "IVC",
    shortName: "IVC",
    name: "Intraveineuse continue (IVC)",
  },
  SC: {
    id: "SC",
    shortName: "SC",
    name: "Sous-cutanée (SC)",
  },
  entérale: {
    id: "entérale",
    shortName: "entérale",
    name: "Entérale",
  },
  intrathécale: {
    id: "intrathécale",
    shortName: "intrathéc.",
    name: "Intrathécale",
  },
  endovésicale: {
    id: "endovésicale",
    shortName: "endovésic.",
    name: "Endovésicale",
  },

  sublinguale: {
    id: "sublinguale",
    shortName: "sublinguale",
    name: "Sublinguale",
  },

  rectale: {
    id: "rectale",
    shortName: "rectale",
    name: "Rectale",
  },
  vaginale: {
    id: "vaginale",
    shortName: "vaginale",
    name: "Vaginale",
  },
  oculaire: {
    id: "oculaire",
    shortName: "oculaire",
    name: "Oculaire",
  },
  auriculaire: {
    id: "auriculaire",
    shortName: "auriculaire",
    name: "Auriculaire",
  },
  nasale: {
    id: "nasale",
    shortName: "nasale",
    name: "Nasale",
  },
  inhalation: {
    id: "inhalation",
    shortName: "inhal.",
    name: "Inhalation",
  },
  nébulisation: {
    id: "nébulisation",
    shortName: "nébul.",
    name: "Nébulisation",
  },
  cutanée: {
    id: "cutanée",
    shortName: "cutanée",
    name: "Cutanée",
  },
  transdermique: {
    id: "transdermique",
    shortName: "transderm.",
    name: "Transdermique",
  },
  geste: {
    id: "geste",
    shortName: "geste",
    name: "geste",
  },
};

export type MedicineUnit =
  | "comprimé"
  | "capsule"
  | "gélule"
  | "suppositoire"
  | "patch"
  | "spray"
  | "injection"
  | "sachet"
  | "flacon"
  | "goutte"
  | "nébulisation"
  | "bouffée"
  | "ampoule"
  | "DDP"
  | "application"
  | "fois"
  | "mg"
  | "cc"
  | "g"
  | "ml"
  | "l"
  | "UI"
  | "mg/kg"
  | "mg/kg/h"
  | "mg/h"
  | "cc/h"
  | "mg/m2"
  | "mg/m2/h"
  | "ml/kg"
  | "ml/kg/h"
  | "ml/m2"
  | "ml/m2/h"
  | "ml/h";

export const medicineUnits: Record<
  MedicineUnit,
  {
    id: MedicineUnit;
    name: string;
    shortName: string;
  }
> = {
  comprimé: {
    id: "comprimé",
    name: "Comprimé(s)",
    shortName: "cp",
  },
  g: {
    id: "g",
    name: "g",
    shortName: "g",
  },
  mg: {
    id: "mg",
    name: "mg",
    shortName: "mg",
  },

  ml: {
    id: "ml",
    name: "ml",
    shortName: "ml",
  },
  l: {
    id: "l",
    name: "l",
    shortName: "l",
  },
  cc: {
    id: "cc",
    name: "cc",
    shortName: "cc",
  },

  UI: {
    id: "UI",
    name: "UI",
    shortName: "UI",
  },

  "mg/h": {
    id: "mg/h",
    name: "mg/h",
    shortName: "mg/h",
  },
  "cc/h": {
    id: "cc/h",
    name: "cc/h",
    shortName: "cc/h",
  },
  "ml/h": {
    id: "ml/h",
    name: "ml/h",
    shortName: "ml/h",
  },

  "mg/kg": {
    id: "mg/kg",
    name: "mg/kg",
    shortName: "mg/kg",
  },
  "mg/kg/h": {
    id: "mg/kg/h",
    name: "mg/kg/h",
    shortName: "mg/kg/h",
  },

  "mg/m2": {
    id: "mg/m2",
    name: "mg/m2",
    shortName: "mg/m2",
  },
  "mg/m2/h": {
    id: "mg/m2/h",
    name: "mg/m2/h",
    shortName: "mg/m2/h",
  },

  "ml/m2": {
    id: "ml/m2",
    name: "ml/m2",
    shortName: "ml/m2",
  },
  "ml/m2/h": {
    id: "ml/m2/h",
    name: "ml/m2/h",
    shortName: "ml/m2/h",
  },
  "ml/kg": {
    id: "ml/kg",
    name: "ml/kg",
    shortName: "ml/kg",
  },
  "ml/kg/h": {
    id: "ml/kg/h",
    name: "ml/kg/h",
    shortName: "ml/kg/h",
  },

  DDP: {
    id: "DDP",
    name: "DDP(s)",
    shortName: "DDP",
  },
  fois: {
    id: "fois",
    name: "Fois",
    shortName: "fois",
  },

  injection: {
    id: "injection",
    name: "Injection(s)",
    shortName: "inj",
  },
  application: {
    id: "application",
    name: "Application(s)",
    shortName: "app",
  },

  capsule: {
    id: "capsule",
    name: "Capsule(s)",
    shortName: "cp",
  },
  gélule: {
    id: "gélule",
    name: "Gélule(s)",
    shortName: "gl",
  },
  suppositoire: {
    id: "suppositoire",
    name: "Suppositoire(s)",
    shortName: "sup",
  },
  patch: {
    id: "patch",
    name: "Patch(s)",
    shortName: "pt",
  },
  spray: {
    id: "spray",
    name: "Spray(s)",
    shortName: "spr",
  },

  sachet: {
    id: "sachet",
    name: "Sachet(s)",
    shortName: "sach",
  },
  flacon: {
    id: "flacon",
    name: "Flacon(s)",
    shortName: "fl",
  },
  goutte: {
    id: "goutte",
    name: "Goutte(s)",
    shortName: "gt",
  },
  nébulisation: {
    id: "nébulisation",
    name: "Nébulisation(s)",
    shortName: "néb",
  },
  bouffée: {
    id: "bouffée",
    name: "Bouffée(s)",
    shortName: "bou",
  },
  ampoule: {
    id: "ampoule",
    name: "Ampoule(s)",
    shortName: "amp",
  },
};

export type MedicineHoraire =
  | "00:00:00"
  | "01:00:00"
  | "02:00:00"
  | "03:00:00"
  | "04:00:00"
  | "05:00:00"
  | "06:00:00"
  | "07:00:00"
  | "08:00:00"
  | "09:00:00"
  | "10:00:00"
  | "11:00:00"
  | "12:00:00"
  | "13:00:00"
  | "14:00:00"
  | "15:00:00"
  | "16:00:00"
  | "17:00:00"
  | "18:00:00"
  | "19:00:00"
  | "20:00:00"
  | "21:00:00"
  | "22:00:00"
  | "23:00:00"
  | "h0"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "h7"
  | "h8"
  | "h9"
  | "h10"
  | "h11"
  | "h12"
  | "h13"
  | "h14"
  | "h15"
  | "h16"
  | "h17"
  | "h18"
  | "h19"
  | "h20"
  | "h21"
  | "h22"
  | "h23"
  | "h24"
  | "h36"
  | "h48"
  | "h60"
  | "h72"
  | "1x/j"
  | "2x/j"
  | "3x/j"
  | "4x/j"
  | "5x/j"
  | "6x/j"
  | "7x/j"
  | "8x/j"
  | "9x/j"
  | "10x/j"
  | "le matin"
  | "à midi"
  | "le soir"
  | "la nuit"
  | "avant de dormir"
  | "au réveil"
  | "à jeun"
  | "après repas"
  | "avant repas"
  | "pendant repas"
  | "à la demande"
  | "au besoin"
  | "chaque heure"
  | "toutes les 2 heures"
  | "toutes les 3 heures"
  | "toutes les 4 heures"
  | "toutes les 5 heures"
  | "toutes les 6 heures"
  | "toutes les 8 heures"
  | "1 jour sur 2"
  | "1 jour sur 3"
  | "1 jour sur 4"
  | "1 jour sur 5"
  | "1 jour sur 6"
  | "1x/semaine"
  | "2x/semaine"
  | "3x/semaine"
  | "4x/semaine"
  | "5x/semaine"
  | "1x/mois"
  | "2x/mois"
  | "3x/mois"
  | "4x/mois"
  | "5x/mois";

export const medicineHoraires: Record<
  MedicineHoraire,
  {
    id: MedicineHoraire;
    name: string;
    getTime?: (date: moment.Moment) => string | undefined | null;
    getHoraire?: () => MedicineHoraire;
    types: OrdonnanceCategory[];
  }
> = {
  "08:00:00": {
    id: "08:00:00",
    name: "08:00",
    getTime: (date) => date.format("YYYY-MM-DD 08:00:00"),
    getHoraire: () => "le matin",
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "09:00:00": {
    id: "09:00:00",
    name: "09:00",
    getHoraire: () => "le matin",
    getTime: (date) => date.format("YYYY-MM-DD 09:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "10:00:00": {
    id: "10:00:00",
    name: "10:00",
    getHoraire: () => "le matin",
    getTime: (date) => date.format("YYYY-MM-DD 10:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "11:00:00": {
    id: "11:00:00",
    name: "11:00",
    getHoraire: () => "le matin",
    getTime: (date) => date.format("YYYY-MM-DD 11:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "12:00:00": {
    id: "12:00:00",
    name: "12:00",
    getHoraire: () => "à midi",
    getTime: (date) => date.format("YYYY-MM-DD 12:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "13:00:00": {
    id: "13:00:00",
    name: "13:00",
    getHoraire: () => "à midi",
    getTime: (date) => date.format("YYYY-MM-DD 13:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "14:00:00": {
    id: "14:00:00",
    name: "14:00",
    getHoraire: () => "le soir",
    getTime: (date) => date.format("YYYY-MM-DD 14:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "15:00:00": {
    id: "15:00:00",
    name: "15:00",
    getHoraire: () => "le soir",
    getTime: (date) => date.format("YYYY-MM-DD 15:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "16:00:00": {
    id: "16:00:00",
    name: "16:00",
    getHoraire: () => "le soir",
    getTime: (date) => date.format("YYYY-MM-DD 16:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "17:00:00": {
    id: "17:00:00",
    name: "17:00",
    getHoraire: () => "le soir",
    getTime: (date) => date.format("YYYY-MM-DD 17:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "18:00:00": {
    id: "18:00:00",
    name: "18:00",
    getHoraire: () => "le soir",
    getTime: (date) => date.format("YYYY-MM-DD 18:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "19:00:00": {
    id: "19:00:00",
    name: "19:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 19:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "20:00:00": {
    id: "20:00:00",
    name: "20:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 20:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "21:00:00": {
    id: "21:00:00",
    name: "21:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 21:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "22:00:00": {
    id: "22:00:00",
    name: "22:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 22:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "23:00:00": {
    id: "23:00:00",
    name: "23:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 23:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "00:00:00": {
    id: "00:00:00",
    name: "00:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 00:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "01:00:00": {
    id: "01:00:00",
    name: "01:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 01:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "02:00:00": {
    id: "02:00:00",
    name: "02:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 02:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "03:00:00": {
    id: "03:00:00",
    name: "03:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 03:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "04:00:00": {
    id: "04:00:00",
    name: "04:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 04:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "05:00:00": {
    id: "05:00:00",
    name: "05:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 05:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "06:00:00": {
    id: "06:00:00",
    name: "06:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 06:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  "07:00:00": {
    id: "07:00:00",
    name: "07:00",
    getHoraire: () => "la nuit",
    getTime: (date) => date.format("YYYY-MM-DD 07:00:00"),
    types: ["hospital", "hospitalOncology", "hdjOncology"],
  },
  h0: {
    id: "h0",
    name: "h0",
    getTime: (date) => date.add(0, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h1: {
    id: "h1",
    name: "h1",
    getTime: (date) => date.add(1, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h2: {
    id: "h2",
    name: "h2",
    getTime: (date) => date.add(2, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h3: {
    id: "h3",
    name: "h3",
    getTime: (date) => date.add(3, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h4: {
    id: "h4",
    name: "h4",
    getTime: (date) => date.add(4, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h5: {
    id: "h5",
    name: "h5",
    getTime: (date) => date.add(5, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h6: {
    id: "h6",
    name: "h6",
    getTime: (date) => date.add(6, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h7: {
    id: "h7",
    name: "h7",
    getTime: (date) => date.add(7, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h8: {
    id: "h8",
    name: "h8",
    getTime: (date) => date.add(8, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h9: {
    id: "h9",
    name: "h9",
    getTime: (date) => date.add(9, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h10: {
    id: "h10",
    name: "h10",
    getTime: (date) => date.add(10, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h11: {
    id: "h11",
    name: "h11",
    getTime: (date) => date.add(11, "hours").format("YYYY-MM-DD HH:mm:ss"),

    types: [],
  },
  h12: {
    id: "h12",
    name: "h12",
    getTime: (date) => date.add(12, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h13: {
    id: "h13",
    name: "h13",
    getTime: (date) => date.add(13, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h14: {
    id: "h14",
    name: "h14",
    getTime: (date) => date.add(14, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h15: {
    id: "h15",
    name: "h15",
    getTime: (date) => date.add(15, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h16: {
    id: "h16",
    name: "h16",
    getTime: (date) => date.add(16, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h17: {
    id: "h17",
    name: "h17",
    getTime: (date) => date.add(17, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h18: {
    id: "h18",
    name: "h18",
    getTime: (date) => date.add(18, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h19: {
    id: "h19",
    name: "h19",
    getTime: (date) => date.add(19, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h20: {
    id: "h20",
    name: "h20",
    getTime: (date) => date.add(20, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h21: {
    id: "h21",
    name: "h21",
    getTime: (date) => date.add(21, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h22: {
    id: "h22",
    name: "h22",
    getTime: (date) => date.add(22, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h23: {
    id: "h23",
    name: "h23",
    getTime: (date) => date.add(23, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  h24: {
    id: "h24",
    name: "h24",
    getTime: (date) => date.add(24, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },

  h36: {
    id: "h36",
    name: "h36",
    getTime: (date) => date.add(36, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },

  h48: {
    id: "h48",
    name: "h48",
    getTime: (date) => date.add(48, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },

  h60: {
    id: "h60",
    name: "h60",
    getTime: (date) => date.add(60, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },

  h72: {
    id: "h72",
    name: "h72",
    getTime: (date) => date.add(72, "hours").format("YYYY-MM-DD HH:mm:ss"),
    types: [],
  },
  "1x/j": {
    id: "1x/j",
    name: "1x/j",
    types: ["ambulatory"],
  },
  "2x/j": {
    id: "2x/j",
    name: "2x/j",
    types: ["ambulatory"],
  },
  "3x/j": {
    id: "3x/j",
    name: "3x/j",
    types: ["ambulatory"],
  },
  "4x/j": {
    id: "4x/j",
    name: "4x/j",
    types: ["ambulatory"],
  },

  "5x/j": {
    id: "5x/j",
    name: "5x/j",
    types: ["ambulatory"],
  },
  "6x/j": {
    id: "6x/j",
    name: "6x/j",
    types: ["ambulatory"],
  },
  "7x/j": {
    id: "7x/j",
    name: "7x/j",
    types: ["ambulatory"],
  },
  "8x/j": {
    id: "8x/j",
    name: "8x/j",
    types: ["ambulatory"],
  },
  "9x/j": {
    id: "9x/j",
    name: "9x/j",
    types: ["ambulatory"],
  },
  "10x/j": {
    id: "10x/j",
    name: "10x/j",
    types: ["ambulatory"],
  },
  "le matin": {
    id: "le matin",
    name: "le matin",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 09:00:00"),
  },
  "à midi": {
    id: "à midi",
    name: "à midi",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 12:00:00"),
  },
  "le soir": {
    id: "le soir",
    name: "le soir",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 18:00:00"),
  },
  "la nuit": {
    id: "la nuit",
    name: "la nuit",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 23:00:00"),
  },
  "avant de dormir": {
    id: "avant de dormir",
    name: "avant de dormir",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 22:00:00"),
  },
  "au réveil": {
    id: "au réveil",
    name: "au réveil",
    types: ["ambulatory"],
    getTime: (date) => date.format("YYYY-MM-DD 08:00:00"),
  },
  "à jeun": {
    id: "à jeun",
    name: "à jeun",
    types: ["ambulatory"],
  },
  "après repas": {
    id: "après repas",
    name: "après repas",
    types: ["ambulatory"],
  },
  "avant repas": {
    id: "avant repas",
    name: "avant repas",
    types: ["ambulatory"],
  },
  "pendant repas": {
    id: "pendant repas",
    name: "pendant repas",
    types: ["ambulatory"],
  },
  "à la demande": {
    id: "à la demande",
    name: "à la demande",
    types: ["ambulatory"],
  },
  "au besoin": {
    id: "au besoin",
    name: "au besoin",
    types: ["ambulatory"],
  },
  "chaque heure": {
    id: "chaque heure",
    name: "chaque heure",
    types: ["ambulatory"],
  },
  "toutes les 2 heures": {
    id: "toutes les 2 heures",
    name: "toutes les 2 heures",
    types: ["ambulatory"],
  },
  "toutes les 3 heures": {
    id: "toutes les 3 heures",
    name: "toutes les 3 heures",
    types: ["ambulatory"],
  },
  "toutes les 4 heures": {
    id: "toutes les 4 heures",
    name: "toutes les 4 heures",
    types: ["ambulatory"],
  },
  "toutes les 5 heures": {
    id: "toutes les 5 heures",
    name: "toutes les 5 heures",
    types: ["ambulatory"],
  },
  "toutes les 6 heures": {
    id: "toutes les 6 heures",
    name: "toutes les 6 heures",
    types: ["ambulatory"],
  },
  "toutes les 8 heures": {
    id: "toutes les 8 heures",
    name: "toutes les 8 heures",
    types: ["ambulatory"],
  },
  "1 jour sur 2": {
    id: "1 jour sur 2",
    name: "1 jour sur 2",
    types: ["ambulatory"],
  },
  "1 jour sur 3": {
    id: "1 jour sur 3",
    name: "1 jour sur 3",
    types: ["ambulatory"],
  },
  "1 jour sur 4": {
    id: "1 jour sur 4",
    name: "1 jour sur 4",
    types: ["ambulatory"],
  },
  "1 jour sur 5": {
    id: "1 jour sur 5",
    name: "1 jour sur 5",
    types: ["ambulatory"],
  },
  "1 jour sur 6": {
    id: "1 jour sur 6",
    name: "1 jour sur 6",
    types: ["ambulatory"],
  },

  "1x/semaine": {
    id: "1x/semaine",
    name: "1x/semaine",
    types: ["ambulatory"],
  },
  "2x/semaine": {
    id: "2x/semaine",
    name: "2x/semaine",
    types: ["ambulatory"],
  },
  "3x/semaine": {
    id: "3x/semaine",
    name: "3x/semaine",
    types: ["ambulatory"],
  },
  "4x/semaine": {
    id: "4x/semaine",
    name: "4x/semaine",
    types: ["ambulatory"],
  },
  "5x/semaine": {
    id: "5x/semaine",
    name: "5x/semaine",
    types: ["ambulatory"],
  },
  "1x/mois": {
    id: "1x/mois",
    name: "1x/mois",
    types: ["ambulatory"],
  },
  "2x/mois": {
    id: "2x/mois",
    name: "2x/mois",
    types: ["ambulatory"],
  },
  "3x/mois": {
    id: "3x/mois",
    name: "3x/mois",
    types: ["ambulatory"],
  },
  "4x/mois": {
    id: "4x/mois",
    name: "4x/mois",
    types: ["ambulatory"],
  },
  "5x/mois": {
    id: "5x/mois",
    name: "5x/mois",
    types: ["ambulatory"],
  },
};

export type DilutionSolution =
  | "Sérum Salé 0.9%"
  | "Sérum Glucosé 5%"
  | "Sérum glucosé 10%"
  | "Sérum glucosé 15%"
  | "Sérum glucosé 20%"
  | "Sérum glucosé 30%"
  | "Sérum glucosé 50%"
  | "Bicarbonate de sodium 1.4%"
  | "Bicarbonate de sodium 4.2%"
  | "Bicarbonate de sodium 8.4%";

export const dilutionSolutions: Record<
  DilutionSolution,
  {
    id: DilutionSolution;
    name: string;
  }
> = {
  "Sérum Salé 0.9%": {
    id: "Sérum Salé 0.9%",
    name: "Sérum Salé 0.9%",
  },
  "Sérum Glucosé 5%": {
    id: "Sérum Glucosé 5%",
    name: "Sérum Glucosé 5%",
  },
  "Sérum glucosé 10%": {
    id: "Sérum glucosé 10%",
    name: "Sérum glucosé 10%",
  },
  "Sérum glucosé 15%": {
    id: "Sérum glucosé 15%",
    name: "Sérum glucosé 15%",
  },
  "Sérum glucosé 20%": {
    id: "Sérum glucosé 20%",
    name: "Sérum glucosé 20%",
  },
  "Sérum glucosé 30%": {
    id: "Sérum glucosé 30%",
    name: "Sérum glucosé 30%",
  },
  "Sérum glucosé 50%": {
    id: "Sérum glucosé 50%",
    name: "Sérum glucosé 50%",
  },
  "Bicarbonate de sodium 1.4%": {
    id: "Bicarbonate de sodium 1.4%",
    name: "Bicarbonate de sodium 1.4%",
  },
  "Bicarbonate de sodium 4.2%": {
    id: "Bicarbonate de sodium 4.2%",
    name: "Bicarbonate de sodium 4.2%",
  },
  "Bicarbonate de sodium 8.4%": {
    id: "Bicarbonate de sodium 8.4%",
    name: "Bicarbonate de sodium 8.4%",
  },
};

export type TaskDuration =
  | 10
  | 15
  | 20
  | 25
  | 30
  | 35
  | 40
  | 45
  | 50
  | 55
  | 60
  | 75
  | 90
  | 120
  | 150
  | 180
  | 240
  | 300
  | 360
  | 420
  | 480
  | 540
  | 600
  | 660
  | 720
  | 780
  | 840
  | 900
  | 960
  | 1020
  | 1080
  | 1140
  | 1200
  | 1260
  | 1320
  | 1380
  | 1440;

export const taskDurations: Record<
  TaskDuration,
  {
    id: TaskDuration;
    name: string;
    minutes: number;
  }
> = {
  10: {
    id: 10,
    name: "10 minutes",
    minutes: 10,
  },
  15: {
    id: 15,
    name: "15 minutes",
    minutes: 15,
  },
  20: {
    id: 20,
    name: "20 minutes",
    minutes: 20,
  },
  25: {
    id: 25,
    name: "25 minutes",
    minutes: 25,
  },
  30: {
    id: 30,
    name: "30 minutes",
    minutes: 30,
  },
  35: {
    id: 35,
    name: "35 minutes",
    minutes: 35,
  },
  40: {
    id: 40,
    name: "40 minutes",
    minutes: 40,
  },
  45: {
    id: 45,
    name: "45 minutes",
    minutes: 45,
  },
  50: {
    id: 50,
    name: "50 minutes",
    minutes: 50,
  },
  55: {
    id: 55,
    name: "55 minutes",
    minutes: 55,
  },
  60: {
    id: 60,
    name: "1 heure",
    minutes: 60,
  },
  75: {
    id: 75,
    name: "1 heure 15 minutes",
    minutes: 75,
  },
  90: {
    id: 90,
    name: "1 heure 30 minutes",
    minutes: 90,
  },
  120: {
    id: 120,
    name: "2 heures",
    minutes: 120,
  },
  150: {
    id: 150,
    name: "2 heures 30 minutes",
    minutes: 150,
  },
  180: {
    id: 180,
    name: "3 heures",
    minutes: 180,
  },
  240: {
    id: 240,
    name: "4 heures",
    minutes: 240,
  },
  300: {
    id: 300,
    name: "5 heures",
    minutes: 300,
  },
  360: {
    id: 360,
    name: "6 heures",
    minutes: 360,
  },
  420: {
    id: 420,
    name: "7 heures",
    minutes: 420,
  },
  480: {
    id: 480,
    name: "8 heures",
    minutes: 480,
  },
  540: {
    id: 540,
    name: "9 heures",
    minutes: 540,
  },
  600: {
    id: 600,
    name: "10 heures",
    minutes: 600,
  },

  660: {
    id: 660,
    name: "11 heures",
    minutes: 660,
  },
  720: {
    id: 720,
    name: "12 heures",
    minutes: 720,
  },
  780: {
    id: 780,
    name: "13 heures",
    minutes: 780,
  },
  840: {
    id: 840,
    name: "14 heures",
    minutes: 840,
  },
  900: {
    id: 900,
    name: "15 heures",
    minutes: 900,
  },
  960: {
    id: 960,
    name: "16 heures",
    minutes: 960,
  },
  1020: {
    id: 1020,
    name: "17 heures",
    minutes: 1020,
  },
  1080: {
    id: 1080,
    name: "18 heures",
    minutes: 1080,
  },
  1140: {
    id: 1140,
    name: "19 heures",
    minutes: 1140,
  },
  1200: {
    id: 1200,
    name: "20 heures",
    minutes: 1200,
  },
  1260: {
    id: 1260,
    name: "21 heures",
    minutes: 1260,
  },
  1320: {
    id: 1320,
    name: "22 heures",
    minutes: 1320,
  },
  1380: {
    id: 1380,
    name: "23 heures",
    minutes: 1380,
  },
  1440: {
    id: 1440,
    name: "24 heures",
    minutes: 1440,
  },
};

export type OrdonnanceDuration =
  | "1 jour"
  | "2 jours"
  | "3 jours"
  | "4 jours"
  | "5 jours"
  | "6 jours"
  | "7 jours"
  | "8 jours"
  | "9 jours"
  | "10 jours"
  | "11 jours"
  | "12 jours"
  | "13 jours"
  | "14 jours"
  | "15 jours"
  | "16 jours"
  | "17 jours"
  | "18 jours"
  | "19 jours"
  | "20 jours"
  | "21 jours"
  | "22 jours"
  | "23 jours"
  | "24 jours"
  | "25 jours"
  | "26 jours"
  | "27 jours"
  | "28 jours"
  | "29 jours"
  | "30 jours"
  | "1 mois"
  | "1 mois et demi"
  | "2 mois"
  | "2 mois et demi"
  | "3 mois"
  | "3 mois et demi"
  | "4 mois"
  | "4 mois et demi"
  | "5 mois"
  | "5 mois et demi"
  | "6 mois";

export const ordonnanceDurations: Record<
  OrdonnanceDuration,
  {
    id: OrdonnanceDuration;
    name: string;
    days: number;
  }
> = {
  "1 jour": {
    id: "1 jour",
    name: "1 jour",
    days: 1,
  },
  "2 jours": {
    id: "2 jours",
    name: "2 jours",
    days: 2,
  },
  "3 jours": {
    id: "3 jours",
    name: "3 jours",
    days: 3,
  },
  "4 jours": {
    id: "4 jours",
    name: "4 jours",
    days: 4,
  },
  "5 jours": {
    id: "5 jours",
    name: "5 jours",
    days: 5,
  },
  "6 jours": {
    id: "6 jours",
    name: "6 jours",
    days: 6,
  },
  "7 jours": {
    id: "7 jours",
    name: "7 jours",
    days: 7,
  },
  "8 jours": {
    id: "8 jours",
    name: "8 jours",
    days: 8,
  },
  "9 jours": {
    id: "9 jours",
    name: "9 jours",
    days: 9,
  },
  "10 jours": {
    id: "10 jours",
    name: "10 jours",
    days: 10,
  },
  "11 jours": {
    id: "11 jours",
    name: "11 jours",
    days: 11,
  },
  "12 jours": {
    id: "12 jours",
    name: "12 jours",
    days: 12,
  },
  "13 jours": {
    id: "13 jours",
    name: "13 jours",
    days: 13,
  },
  "14 jours": {
    id: "14 jours",
    name: "14 jours",
    days: 14,
  },
  "15 jours": {
    id: "15 jours",
    name: "15 jours",
    days: 15,
  },
  "16 jours": {
    id: "16 jours",
    name: "16 jours",
    days: 16,
  },
  "17 jours": {
    id: "17 jours",
    name: "17 jours",
    days: 17,
  },
  "18 jours": {
    id: "18 jours",
    name: "18 jours",
    days: 18,
  },
  "19 jours": {
    id: "19 jours",
    name: "19 jours",
    days: 19,
  },
  "20 jours": {
    id: "20 jours",
    name: "20 jours",
    days: 20,
  },
  "21 jours": {
    id: "21 jours",
    name: "21 jours",
    days: 21,
  },
  "22 jours": {
    id: "22 jours",
    name: "22 jours",
    days: 22,
  },
  "23 jours": {
    id: "23 jours",
    name: "23 jours",
    days: 23,
  },
  "24 jours": {
    id: "24 jours",
    name: "24 jours",
    days: 24,
  },
  "25 jours": {
    id: "25 jours",
    name: "25 jours",
    days: 25,
  },
  "26 jours": {
    id: "26 jours",
    name: "26 jours",
    days: 26,
  },
  "27 jours": {
    id: "27 jours",
    name: "27 jours",
    days: 27,
  },
  "28 jours": {
    id: "28 jours",
    name: "28 jours",
    days: 28,
  },
  "29 jours": {
    id: "29 jours",
    name: "29 jours",
    days: 29,
  },
  "30 jours": {
    id: "30 jours",
    name: "30 jours",
    days: 30,
  },
  "1 mois": {
    id: "1 mois",
    name: "1 mois",
    days: 30,
  },
  "1 mois et demi": {
    id: "1 mois et demi",
    name: "1 mois et demi",
    days: 45,
  },
  "2 mois": {
    id: "2 mois",
    name: "2 mois",
    days: 60,
  },
  "2 mois et demi": {
    id: "2 mois et demi",
    name: "2 mois et demi",
    days: 75,
  },
  "3 mois": {
    id: "3 mois",
    name: "3 mois",
    days: 90,
  },
  "3 mois et demi": {
    id: "3 mois et demi",
    name: "3 mois et demi",
    days: 105,
  },
  "4 mois": {
    id: "4 mois",
    name: "4 mois",
    days: 120,
  },
  "4 mois et demi": {
    id: "4 mois et demi",
    name: "4 mois et demi",
    days: 135,
  },
  "5 mois": {
    id: "5 mois",
    name: "5 mois",
    days: 150,
  },
  "5 mois et demi": {
    id: "5 mois et demi",
    name: "5 mois et demi",
    days: 165,
  },
  "6 mois": {
    id: "6 mois",
    name: "6 mois",
    days: 180,
  },
};

//
//
//! ///////////////////////////////////////
//! ////////    Ordonnance ///////////////////
//! ///////////////////////////////////////
//
//

export type Ordonnance = Row & {
  category: OrdonnanceCategory;
  patient: Patient | null | undefined;
  parent?: FolderItem | null | undefined;

  template?: OrdonnanceTemplate;
  weight?: number;
  height?: number;
  bmi?: number;
  surface?: number;
  ordonnanceMedicines: (OrdonnanceMedicine | null | undefined)[];
  confidentiality?: Confidentiality | null | undefined;
};

export type OrdonnanceTemplate = Row & {
  name: string;
  ordonnanceMedicines: (OrdonnanceMedicine | null | undefined)[];
  category: OrdonnanceCategory;
};

export type OrdonnanceCategory =
  | "hospital"
  | "ambulatory"
  | "hospitalOncology"
  | "hdjOncology";

export const ordonnanceCategories: Record<
  OrdonnanceCategory,
  {
    id: OrdonnanceCategory;
    name: string;
  }
> = {
  hospital: {
    id: "hospital",
    name: "Hospitalier",
  },
  ambulatory: {
    id: "ambulatory",
    name: "Ambulatoire",
  },
  hospitalOncology: {
    id: "hospitalOncology",
    name: "Oncologie (Hospitalisation)",
  },
  hdjOncology: {
    id: "hdjOncology",
    name: "Oncologie (HDJ)",
  },
};

export type OrdonnanceMedicine = Row & {
  duration?: OrdonnanceDuration;
  notes: string;
  startDate?: string;
  endDate?: string;
  medicine: Medicine;
  intakes: (Intake | null | undefined)[];
  tasks: (Task | null | undefined)[];
  ordonnance?: Ordonnance;
  patient?: Patient | null | undefined;
};

export type Intake = Row & {
  posology: string | number | null | undefined;
  duration: number;
  notes?: string;

  solution: DilutionSolution | null | undefined;
  dilutionVolume: number | null | undefined;

  horaire: MedicineHoraire;
  unit: MedicineUnit;
  route: MedicineRoute;
};

export type TaskStatus = "finished" | "pending" | "canceled";
export type Task = Row & {
  start?: string;
  end?: string;
  finishedDate?: string | null | undefined;
  cancelDate?: string | null | undefined;
  status?: TaskStatus;
  medicine: Medicine | null | undefined;
  patient?: Patient | null | undefined;
  parent?: FolderItem | null | undefined;
  doctor?: User | null | undefined;
  creator?: User | null | undefined;
  posology: string | number | null | undefined;
  dilutionVolume?: number | null | undefined;
  dilutionSolution?: DilutionSolution | null | undefined;
  duration?: number | null | undefined;
  notes?: string | null | undefined;
  unit: MedicineUnit;
  route: MedicineRoute;
};

//
//! ///////////////////////////////////////
//! ////////    Income ///////////////////
//! ///////////////////////////////////////
//
export type IncomeStatus = "pending" | "paid" | "canceled";

export const incomeStatuses: Record<
  IncomeStatus,
  {
    id: IncomeStatus;
    name: string;
    color: string;
  }
> = {
  pending: {
    id: "pending",
    name: "En attente",
    color: "#FFC107",
  },
  paid: {
    id: "paid",
    name: "Payé",
    color: "#FFC107",
  },
  canceled: {
    id: "canceled",
    name: "Annulé",
    color: "#FFC107",
  },
};

export type Income = Row & {
  basePrice: number;
  status: "pending" | "paid" | "canceled";
  total: number;

  patient: Patient | null | undefined;
  parent?: FolderItem | null | undefined;
  appointmentTypes: (AppointmentType | null | undefined)[];
  credits: (Credit | null | undefined)[];
};

export type Credit = Row & {
  amount: number;

  income: Income | null | undefined;
  patient: Patient | null | undefined;
  status: IncomeStatus;
};

//
//
//! ///////////////////////////////////////
//! ////////    Oncologie ///////////////////
//! ///////////////////////////////////////
//
//

export type Protocol = Row & {
  name: string;
  days: (ProtocolDay | null | undefined)[];
  intercure: number;
  appointmentType: AppointmentType | null | undefined;
  numberOfCycles: number;
  description: string;
};

export type ProtocolType =
  | "adjuvant"
  | "neoadjuvant"
  | "induction"
  | "consolidation"
  | "rattrapage"
  | "maintenance"
  | "palliatif"
  | "periopératoire"
  | "1ère ligne"
  | "2ème ligne"
  | "3ème ligne"
  | "4ème ligne"
  | "5ème ligne";
export const protocolTypes: Record<
  ProtocolType,
  {
    id: ProtocolType;
    name: string;
  }
> = {
  adjuvant: {
    id: "adjuvant",
    name: "Adjuvant",
  },
  neoadjuvant: {
    id: "neoadjuvant",
    name: "Néoadjuvant",
  },
  induction: {
    id: "induction",
    name: "Induction",
  },
  consolidation: {
    id: "consolidation",
    name: "Consolidation",
  },
  rattrapage: {
    id: "rattrapage",
    name: "Rattrapage",
  },
  maintenance: {
    id: "maintenance",
    name: "Maintenance",
  },
  palliatif: {
    id: "palliatif",
    name: "Palliatif",
  },
  periopératoire: {
    id: "periopératoire",
    name: "Périopératoire",
  },
  "1ère ligne": {
    id: "1ère ligne",
    name: "1ère ligne",
  },
  "2ème ligne": {
    id: "2ème ligne",
    name: "2ème ligne",
  },
  "3ème ligne": {
    id: "3ème ligne",
    name: "3ème ligne",
  },
  "4ème ligne": {
    id: "4ème ligne",
    name: "4ème ligne",
  },
  "5ème ligne": {
    id: "5ème ligne",
    name: "5ème ligne",
  },
};

export type ProtocolDay = Row & {
  day: number | null | undefined;
  protocol: Protocol | null | undefined;
  templates: (OrdonnanceTemplate | null | undefined)[];
};

//
//! ///////////////////////////////////////
//! ////////    APPOINTMENTS ///////////////////
//! ///////////////////////////////////////
//

export type Appointment = Row & {
  start: string | null | undefined;
  end: string | null | undefined;
  anchorDate: string | null | undefined;
  arrivedDate?: string;
  ticketNumber?: TicketNumber | null | undefined;
  demandService: Service | null | undefined;
  service: Service | null | undefined;
  isHospitalized?: boolean;
  isUrgent?: boolean | null | undefined;
  notes?: string;

  status: AppointmentStatus;
  doctor: User | null | undefined;

  patient: Patient | null | undefined;
  type: AppointmentType | null | undefined;
  item: FolderItem | Exam | null | undefined;
  demandContent?: RefAnswer | null | undefined;

  emergencyLevel?: EmergencyLevel;
  uploads?: (Upload | null | undefined)[] | null | undefined;

  protocolType?: ProtocolType;
  protocol?: Protocol;
  protocolCycle?: number;
  protocolDay?: ProtocolDay | null | undefined;

  confidentiality?: Confidentiality | null | undefined;
  externalId?: string;

  preparations?: (Reference | null | undefined)[] | null | undefined;

  refusalNotes?: string | null | undefined;
  refusalCategory?: RefusalCategory | null | undefined;
  readBy?: User[] | null | undefined;
  initialMotives?: (RefAnswer | null | undefined)[] | null | undefined;
};

export type RefusalCategory =
  | "noIndication"
  | "noMaterial"
  | "insufficientData"
  | "noConstrast"
  | "noTechnicalStaff"
  | "counterIndication"
  | "informationError"
  | "incompatibleQuantity"
  | "missingProducts"
  | "other";

export const refusalCategories: Record<
  RefusalCategory,
  {
    id: RefusalCategory;
    name: string;
    type?: "anapath" | "other" | null;
  }
> = {
  noIndication: {
    id: "noIndication",
    name: "Absence d'indication",
    type: "other",
  },
  noMaterial: {
    id: "noMaterial",
    name: "Panne matérielle",
    type: "other",
  },
  insufficientData: {
    id: "insufficientData",
    name: "Renseignements cliniques ou biologiques insuffisants",
    type: "other",
  },
  noConstrast: {
    id: "noConstrast",
    name: "Absence de produit de contraste",
    type: "other",
  },
  noTechnicalStaff: {
    id: "noTechnicalStaff",
    name: "Défaut de plateau technique",
    type: "other",
  },
  counterIndication: {
    id: "counterIndication",
    name: "Contre-indication",
    type: "other",
  },
  informationError: {
    id: "informationError",
    name: "Erreur de renseignements",
    type: "anapath",
  },
  incompatibleQuantity: {
    id: "incompatibleQuantity",
    name: "Nombre de flacons ou blocs incompatible",
    type: "anapath",
  },
  missingProducts: {
    id: "missingProducts",
    name: "Manque de réactifs",
    type: "anapath",
  },
  other: {
    id: "other",
    name: "Autre",
  },
};

export type GeneralStatus =
  | PatientItemStatus
  | AppointmentStatus
  | ConfidentialityDemandStatus
  | StockStatus;

export type StatusChange = Row & {
  oldStatus: GeneralStatus;
  newStatus: GeneralStatus;
  item: any;
};

export type TicketNumber = Row & {
  number: number;
  type: "ticket" | "jeton";
  prefix?: string | null | undefined;
  emergencyLevel: EmergencyLevel;
  service: Service;
  unit: StructureUnit | null | undefined;
  box: Box;
  notes: string | null | undefined;
};

export type AppointmentStatus =
  | "pending"
  | "sampleReceived"
  | "validated"
  | "refused"
  | "scheduled"
  | "rescheduled"
  | "canceled"
  | "arrived"
  | "resultPendingValidation"
  | "finished"
  | "noShow";

export const appointmentStatuses: Record<
  AppointmentStatus,
  {
    id: AppointmentStatus;
    name: string;
    type: CardType;
    color: string;
  }
> = {
  pending: {
    id: "pending",
    name: "En attente",
    type: "orange",
    color: "#FFC107",
  },
  sampleReceived: {
    id: "sampleReceived",
    name: "Echantillon reçu",
    type: "green",
    color: "#FFC107",
  },

  validated: {
    id: "validated",
    name: "Validé(s)",
    type: "blue",
    color: "#FFC107",
  },
  refused: {
    id: "refused",
    name: "Refusé(s)",
    type: "red",
    color: "#FFC107",
  },
  scheduled: {
    id: "scheduled",
    name: "Programmé(s)",
    type: "green",
    color: "#FFC107",
  },
  rescheduled: {
    id: "rescheduled",
    name: "Replanifié(s)",
    type: "green",

    color: "#FFC107",
  },
  arrived: {
    id: "arrived",
    name: "Arrivé(s)",
    type: "orange",

    color: "#FFC107",
  },
  canceled: {
    id: "canceled",
    name: "Annulé(s)",
    type: "red",

    color: "#FFC107",
  },
  resultPendingValidation: {
    id: "resultPendingValidation",
    name: "Résultat en attente de validation",
    type: "orange",
    color: "#FFC107",
  },
  finished: {
    id: "finished",
    name: "Terminé(s)",
    type: "green",

    color: "#FFC107",
  },
  noShow: {
    id: "noShow",
    name: "Absent(s)",
    type: "purple",
    color: "#FFC107",
  },
};

export type EmergencyLevel =
  | "none"
  | "notUrgent"
  | "moderatelyUrgent"
  | "urgent"
  | "veryUrgent";

export const emergencyLevels: Record<
  EmergencyLevel,
  {
    id: EmergencyLevel;
    name: string;
    color: string;
    colorName: "blue" | "green" | "orange" | "red" | "yellow";
  }
> = {
  none: {
    id: "none",
    name: "Indéfini",
    color: "#FFC107",
    colorName: "blue",
  },
  notUrgent: {
    id: "notUrgent",
    name: "Non urgent",
    color: "#FFC107",
    colorName: "green",
  },
  moderatelyUrgent: {
    id: "moderatelyUrgent",
    name: "Urgence modérée",
    color: "#FCC203",
    colorName: "yellow",
  },
  urgent: {
    id: "urgent",
    name: "Urgent",
    color: "#FFC107",
    colorName: "orange",
  },
  veryUrgent: {
    id: "veryUrgent",
    name: "Très urgent",
    color: "#FFC107",
    colorName: "red",
  },
};

export type AppointmentType = Row & {
  name: string;
  duration: number;
  price: number;
  color: string;
  description?: string;
  itemType:
    | "exam"
    | "cure"
    | "consultation"
    | "hospitalisation"
    | "legal"
    | "anapath";
  needsValidation?: boolean;
  exam?: Reference | null | undefined;
  externalId?: string;
  preparations?: (Reference | null | undefined)[] | null | undefined;
  demandForm: Reference | null | undefined;
};

export type ExternalApiId = "bmLab" | "aissani";
export const externalApis: Record<
  ExternalApiId,
  { id: ExternalApiId; name: string }
> = {
  bmLab: {
    id: "bmLab",
    name: "BM Lab",
  },
  aissani: {
    id: "aissani",
    name: "Aissani",
  },
};

//! ///////////////////////////////////////
//! ////////    AI   ///////////////////
//! ///////////////////////////////////////
export type SuggestedQuestion = Row & {
  title: string;
  text: string;
  importanceScore: number;
  hasBeenAnswered: boolean;
  answer: string | null | undefined;
  whyIsItRelevant: string | null | undefined;
};

export type SuggestedDiagnostic = Row & {
  name: string;
  reasoning: string;
  confidenceScore: number;
};

export type AiGeneralItems = {
  antecedents: {
    type: ReferenceCategory;
    name: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  motives: {
    type: "consultationMotive" | "hospitalizationMotive";
    name: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  historyOfDisease: {
    details: string;
    convertedItem?: {
      type: "story";
      value: Story;
    };
  }[];
  symptoms: {
    name: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  vitals: {
    name: string;
    value: string;
    unit: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  scores: {
    name: string;
    value: string;
    unit: string;
    details: string;
    convertedItem?: {
      type: "patientScore";
      value: PatientScore;
    };
  }[];
  physicalExamFindings: {
    name: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  exams: {
    name: string;
    type: ReferenceCategory;
    results: {
      name: string;
      type: "numeric" | "text";
      value: string;
      unit: string;
      details: string;
      convertedItem?: {
        type: "fieldAnswer";
        value: FieldAnswer;
      };
    }[];
    convertedItem?: {
      type: "exam";
      value: Exam;
    };
  }[];
  prescriptions: {
    medicineName: string;
    dosage: string;
    posology: string;
    details: string;
    convertedItem?: {
      type: "ordonnanceMedicine";
      value: OrdonnanceMedicine;
    };
  }[];
  courseOfAction: {
    description: string;
  }[];
  procedures: {
    name: string;
    details: string;
    convertedItem?: {
      type: "refAnswer";
      value: RefAnswer;
    };
  }[];
  appointments: {
    appointmentType: string;
    hasDatetime: boolean;
    datetime: string;
    convertedItem?: {
      type: "appointment";
      value: Appointment;
    };
  }[];
  other: string;
};

export type AiAudioSession = Row & {
  audioFileUrl?: string;
  parent: FolderItem;
  transcript: string;
  beautifiedTranscript: string;
  diagnostics: (SuggestedDiagnostic | null | undefined)[];
  questions: (SuggestedQuestion | null | undefined)[];
  items: AiAudioSessionItem[];
  uploads: (Upload | null | undefined)[];
};

export type AiAudioSessionItem =
  | {
      type: "refAnswer";
      value: RefAnswer;
    }
  | {
      type: "appointment";
      value: Appointment;
    }
  | {
      type: "ordonnance";
      value: Ordonnance;
    }
  | {
      type: "exam";
      value: Exam;
    }
  | {
      type: "examDemand";
      value: ExamDemand;
    }
  | {
      type: "patientScore";
      value: PatientScore;
    }
  | {
      type: "story";
      value: Story;
    };

//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ////////    SNOMED   ///////////////////
//! ///////////////////////////////////////
export type SnomedConcept = Row & {
  name: string;
  synonyms: string;
  nameLength: number;
  language: "fr" | "en";
  descriptions: SnomedDescription[];
  sourceRelationships: SnomedRelationship[];
  destinationRelationships: SnomedRelationship[];
  parents: SnomedConcept[];
  allParents: SnomedConcept[];
  children: SnomedConcept[];
  originalId: string;
  count: number;
};

export type SnomedDescription = Row & {
  concept: SnomedConcept;

  term: string;
  languageCode: "en" | "fr";
  type: SnomedType;
  originalId: string;
};

export type SnomedRelationship = Row & {
  source: SnomedConcept;
  destination: SnomedConcept;
  type: SnomedType;
  characteristicType: SnomedType;
  relationshipGroupId: number;
  originalId: string;
};

let a: SnomedConcept;

export type SnomedType =
  | "Due to (attribute)"
  | "Associated with (attribute)"
  | "Associated morphology (attribute)"
  | "Has measured component (attribute)"
  | "Is a (attribute)"
  | "Associated function (attribute)"
  | "Has specimen (attribute)"
  | "Specimen source morphology (attribute)"
  | "Specimen source topography (attribute)"
  | "Specimen source identity (attribute)"
  | "Specimen procedure (attribute)"
  | "Part of (attribute)"
  | "Has active ingredient (attribute)"
  | "Subject of information (attribute)"
  | "Causative agent (attribute)"
  | "Associated finding (attribute)"
  | "Component (attribute)"
  | "Onset (attribute)"
  | "Severity (attribute)"
  | "Surface characteristics (attribute)"
  | "Location (attribute)"
  | "Occurrence (attribute)"
  | "Episodicity (attribute)"
  | "Technique (attribute)"
  | "Revision status (attribute)"
  | "Units (attribute)"
  | "After (attribute)"
  | "Stage (attribute)"
  | "Access (attribute)"
  | "Approach (attribute)"
  | "Method (attribute)"
  | "Extent (attribute)"
  | "Priority (attribute)"
  | "Course (attribute)"
  | "Using (attribute)"
  | "Clinical course (attribute)"
  | "Communication with wound (attribute)"
  | "Laterality (attribute)"
  | "Before (attribute)"
  | "Pathological process (qualifier value)"
  | "Instrumentation (attribute)"
  | "Associated procedure (attribute)"
  | "Finding site (attribute)"
  | "Direct device (attribute)"
  | "Direct morphology (attribute)"
  | "Direct substance (attribute)"
  | "Has focus (attribute)"
  | "Has intent (attribute)"
  | "Procedure site (attribute)"
  | "Has definitional manifestation (attribute)"
  | "Temporally follows (attribute)"
  | "Indirect morphology (attribute)"
  | "Indirect device (attribute)"
  | "Has interpretation (attribute)"
  | "Interprets (attribute)"
  | "Associated etiologic finding (attribute)"
  | "Measures (attribute)"
  | "Access instrument (attribute)"
  | "Measurement Method (attribute)"
  | "Property (attribute)"
  | "Recipient category (attribute)"
  | "Scale type (attribute)"
  | "Specimen substance (attribute)"
  | "Time aspect (attribute)"
  | "Pathological process (attribute)"
  | "During (attribute)"
  | "Procedure site - Direct (attribute)"
  | "Procedure site - Indirect (attribute)"
  | "Procedure device (attribute)"
  | "Procedure morphology (attribute)"
  | "Finding context (attribute)"
  | "Procedure context (attribute)"
  | "Temporal context (attribute)"
  | "Subject relationship context (attribute)"
  | "Route of administration (attribute)"
  | "Has dose form (attribute)"
  | "Finding method (attribute)"
  | "Finding informer (attribute)"
  | "Using device (attribute)"
  | "Using energy (attribute)"
  | "Using substance (attribute)"
  | "Surgical approach (attribute)"
  | "Using access device (attribute)"
  | "Property type (attribute)"
  | "Inheres in (attribute)"
  | "Towards (attribute)"
  | "Characterizes (attribute)"
  | "Process duration (attribute)"
  | "Process output (attribute)"
  | "Relative to (attribute)"
  | "Precondition (attribute)"
  | "Direct site (attribute)"
  | "Inherent location (attribute)"
  | "Relative to part of (attribute)"
  | "Has realization (attribute)"
  | "Has disposition (attribute)"
  | "Temporally related to (attribute)"
  | "Has basis of strength substance (attribute)"
  | "Has presentation strength numerator value (attribute)"
  | "Has presentation strength numerator unit (attribute)"
  | "Has presentation strength denominator value (attribute)"
  | "Has presentation strength denominator unit (attribute)"
  | "Has concentration strength denominator unit (attribute)"
  | "Has concentration strength denominator value (attribute)"
  | "Has concentration strength numerator value (attribute)"
  | "Has concentration strength numerator unit (attribute)"
  | "Has dose form administration method (attribute)"
  | "Has dose form transformation (attribute)"
  | "Has dose form intended site (attribute)"
  | "Has dose form release characteristic (attribute)"
  | "Has basic dose form (attribute)"
  | "Has state of matter (attribute)"
  | "Is modification of (attribute)"
  | "Has precise active ingredient (attribute)"
  | "Has unit of presentation (attribute)"
  | "Plays role (attribute)"
  | "Count of base of active ingredient (attribute)"
  | "Count of active ingredient (attribute)"
  | "Has filling (attribute)"
  | "Has device intended site (attribute)"
  | "Has compositional material (attribute)"
  | "Has device characteristic (attribute)"
  | "Has ingredient characteristic (attribute)"
  | "Has product characteristic (attribute)"
  | "Process extends to (attribute)"
  | "Process acts on (attribute)"
  | "Is sterile (attribute)"
  | "Has coating material (attribute)"
  | "Has surface texture (attribute)"
  | "Has absorbability (attribute)"
  | "Has ingredient qualitative strength (attribute)"
  | "Has target population (attribute)"
  | "Synonym (core metadata concept)"
  | "Fully specified name (core metadata concept)";

export const frequentSnomedParents = {
  disease: { id: "concepts:64572001", name: "disease", displayName: "Maladie" },
  finding: { id: "concepts:404684003", name: "finding", displayName: "Signe" },
  substance: {
    id: "concepts:105590001",
    name: "substance",
    displayName: "Substance",
  },
  procedure: {
    id: "concepts:71388002",
    name: "procedure",
    displayName: "Procédure",
  },
  medicine: {
    id: "concepts:410942007",
    name: "medicine",
    displayName: "Médicament",
  },

  bodyStructure: {
    id: "concepts:123037004",
    name: "bodyStructure",
    displayName: "Structure Anatomique",
  },
  organism: {
    id: "concepts:410607006",
    name: "organism",
    displayName: "Organisme",
  },
  cancer: {
    id: "concepts:363346000",
    name: "cancer",
    displayName: "Cancer",
  },
  metastasis: {
    id: "concepts:128462008",
    name: "metastasis",
    displayName: "Métastase",
  },
  intervention: {
    id: "concepts:71388002",
    name: "intervention",
    displayName: "Intervention",
  },
  labExam: {
    id: "concepts:15220000",
    name: "labExam",
    displayName: "Analyse de laboratoire",
  },
  otherExam: {
    id: "concepts:183113008",
    name: "otherExam",
    displayName: "Autre examen",
  },
  imaging: {
    id: "concepts:363679005",
    name: "imaging",
    displayName: "Imagerie",
  },
  diagnosticImaging: {
    id: "concepts:7396004",
    name: "diagnosticImaging",
    displayName: "Imagerie diagnostique",
  },
  exploration: {
    id: "concepts:252142004",
    name: "exploration",
    displayName: "Exploration et investigation",
  },
} as const;
export type FrequentSnomedParent = keyof typeof frequentSnomedParents;

//
//
//! ///////////////////////////////////////
//! ////////    REFERENTIEL ///////////////////
//! ///////////////////////////////////////
//

export type Reference = Row & {
  name?: string;
  snomedConcept?: SnomedConcept | null | undefined;
  isSnomedValidated?: boolean;
  description?: string;
  externalId?: string;
  formulation?: string | null | undefined;
  category?: ReferenceCategory;
  cim10Code?: string | null | undefined;
  cim11Code?: string | null | undefined;
  usageCount?: number;
  sections?: (FieldsSection | null | undefined)[];
  isVisible?: boolean;
  isAddedAutomatically?: boolean;
  emergencyLevel?: EmergencyLevel;
  children?: (Reference | null | undefined)[] | null | undefined;
};

export type ReferencesGroup = Row & {
  name: string;
  type: ReferencesGroupType;
  visibility: "all" | "user";
  usageCount: number;
  references: (Reference | null | undefined)[];
};

export type ReferencesGroupType = "exams" | "antecedents";

export const referenceGroupTypes: Record<
  ReferencesGroupType,
  {
    id: ReferencesGroupType;
    name: string;
  }
> = {
  antecedents: {
    id: "antecedents",
    name: "Antécédents",
  },
  exams: {
    id: "exams",
    name: "Examens",
  },
};

export type ReferenceCategory =
  | "physical"
  | "functional"
  | "syndrome"
  | "motive"
  | "diagnostic"
  | "intervention"
  | "mesure"
  | "allergy"
  | "riskFactor"
  | "physiologicalAntecedent"
  | "personalAntecedent"
  | "surgicalAntecedent"
  | "toxicAntecedent"
  | "familialAntecedent"
  | "exam"
  | "examAnalysis"
  | "examStep"
  | "appointmentPreparation"
  | "sampleType"
  | "other"
  | "imaging"
  | "biology"
  | "ecg"
  | "exploration"
  | "formulaire"
  | "cim11"
  | "cim10"
  | "toxicity"
  | "metastasis"
  | "histologicType"
  | "histologicSubType";

export const examCategoriesIds: ReferenceCategory[] = [
  "exam",
  "imaging",
  "biology",
  "ecg",
  "exploration",
  "other",
];

export const antecedentsCategoriesIds: ReferenceCategory[] = [
  "riskFactor",
  "physiologicalAntecedent",
  "personalAntecedent",
  "surgicalAntecedent",
  "toxicAntecedent",
  "familialAntecedent",
];

export const referenceCategories: Record<
  ReferenceCategory,
  {
    id: ReferenceCategory;
    name: string;
    pluralName: string;
    subCategory: "reference" | "exam" | "antecedent" | "formulaire";
  }
> = {
  functional: {
    id: "functional",
    name: "Signes Fonctionnels",
    pluralName: "Signes Fonctionnels",
    subCategory: "reference",
  },
  allergy: {
    id: "allergy",
    name: "Allergie",
    pluralName: "Allergies",
    subCategory: "reference",
  },
  physical: {
    id: "physical",
    name: "Signes Physiques",
    pluralName: "Signes Physiques",
    subCategory: "reference",
  },
  syndrome: {
    id: "syndrome",
    name: "Syndrome",
    pluralName: "Syndromes",
    subCategory: "reference",
  },
  intervention: {
    id: "intervention",
    name: "Intervention",
    pluralName: "Interventions",
    subCategory: "reference",
  },
  motive: {
    id: "motive",
    name: "Motif",
    pluralName: "Motifs",
    subCategory: "reference",
  },
  diagnostic: {
    id: "diagnostic",
    name: "Diagnostic",
    pluralName: "Diagnostics",
    subCategory: "reference",
  },
  mesure: {
    id: "mesure",
    name: "Mesure",
    pluralName: "Mesures",
    subCategory: "reference",
  },

  cim11: {
    id: "cim11",
    name: "CIM11",
    pluralName: "CIM11",
    subCategory: "reference",
  },
  cim10: {
    id: "cim10",
    name: "CIM10",
    pluralName: "CIM10",
    subCategory: "reference",
  },

  toxicity: {
    id: "toxicity",
    name: "Toxicité",
    pluralName: "Toxicités",
    subCategory: "reference",
  },

  riskFactor: {
    id: "riskFactor",
    name: "Facteur de risque",
    pluralName: "Facteurs de risque",
    subCategory: "antecedent",
  },

  physiologicalAntecedent: {
    id: "physiologicalAntecedent",
    name: "Antécédent physiologique",
    pluralName: "Antécédents physiologiques",
    subCategory: "antecedent",
  },

  personalAntecedent: {
    id: "personalAntecedent",
    name: "Antécédent personnel",
    pluralName: "Antécédents médicaux",
    subCategory: "antecedent",
  },
  surgicalAntecedent: {
    id: "surgicalAntecedent",
    name: "Antécédent chirurgical",
    pluralName: "Antécédents chirurgicaux",
    subCategory: "antecedent",
  },

  toxicAntecedent: {
    id: "toxicAntecedent",
    name: "Antécédent toxique",
    pluralName: "Antécédents toxiques",
    subCategory: "antecedent",
  },

  familialAntecedent: {
    id: "familialAntecedent",
    name: "Antécédent familial",
    pluralName: "Antécédents familiaux",
    subCategory: "antecedent",
  },

  examAnalysis: {
    id: "examAnalysis",
    name: "Analyse",
    pluralName: "Analyses",
    subCategory: "exam",
  },

  exam: {
    id: "exam",
    name: "Examen",
    pluralName: "Examens",
    subCategory: "exam",
  },

  biology: {
    id: "biology",
    name: "Biologie",
    pluralName: "Biologie",
    subCategory: "exam",
  },

  ecg: {
    id: "ecg",
    name: "ECG",
    pluralName: "ECG",
    subCategory: "exam",
  },

  imaging: {
    id: "imaging",
    name: "Imagerie",
    pluralName: "Imagerie",
    subCategory: "exam",
  },

  exploration: {
    id: "exploration",
    name: "Explorations",
    pluralName: "Explorations",
    subCategory: "exam",
  },

  other: {
    id: "other",
    name: "Autres",
    pluralName: "Autres",
    subCategory: "exam",
  },

  formulaire: {
    id: "formulaire",
    name: "Formulaire",
    pluralName: "Formulaires",
    subCategory: "formulaire",
  },

  metastasis: {
    id: "metastasis",
    name: "Métastase",
    pluralName: "Métastases",
    subCategory: "reference",
  },

  histologicType: {
    id: "histologicType",
    name: "Type histologique",
    pluralName: "Types histologiques",
    subCategory: "reference",
  },

  histologicSubType: {
    id: "histologicSubType",
    name: "Sous-type histologique",
    pluralName: "Sous-types histologiques",
    subCategory: "reference",
  },

  examStep: {
    id: "examStep",
    name: "Etape d'examen",
    pluralName: "Etapes d'examen",
    subCategory: "reference",
  },

  appointmentPreparation: {
    id: "appointmentPreparation",
    name: "Préparation de rendez-vous",
    pluralName: "Préparations de rendez-vous",
    subCategory: "reference",
  },

  sampleType: {
    id: "sampleType",
    name: "Type d'échantillon",
    pluralName: "Types d'échantillon",
    subCategory: "reference",
  },
} as const;

export type FieldsSection = Row & {
  name: string;
  type: "simple" | "table" | "group";
  isCommonWithSnomedChildren?: boolean | null | undefined;
  isExpandable: boolean | null | undefined;
  fields: (Field | null | undefined)[];
  tableColumns?: (string | null | undefined)[];
  tableRows?: (string | null | undefined)[];
  conditionGroupStr?: string | null | undefined;
};

export type Field = Row & {
  name: string;
  placeholder: string;
  type: InputsType;
  description: string;
  defaultValue: any;
  isRequired?: boolean | null | undefined;

  source?: "legalMigration" | "migration";
  formulation?: string | null | undefined;

  oldIds?: string[] | null | undefined;
  options: (FieldOption | null | undefined)[];
  unit: FieldUnit | null | undefined;
  min?: number | null | undefined;
  max?: number | null | undefined;

  conditionGroupStr?: string | null | undefined;
};

export type FieldUnit = Row & {
  name: string;
};

export type FieldOption = Row & {
  name: string;
  description: string;
  value: any;
};

//
//
//! ///////////////////////////////////////
//! ////////    STRUCTURES ///////////////////
//! ///////////////////////////////////////
//
//

export type Hospital = Row & {
  name: string;
  type?: "CHU" | "EPH" | "EPSP" | "EHS" | "EHU";
  address?: string | null | undefined;
  latitude?: number;
  longitude?: number;
  wilaya?: string | null | undefined;
};

export type Service = Row & {
  name: string | null | undefined;

  chef?: User | null | undefined;
  hospital?: Hospital;
  waitingScreens?: (WaitingScreen | null | undefined)[] | null | undefined;
  appointmentTypes?: (AppointmentType | null | undefined)[] | null | undefined;
  units?: (StructureUnit | null | undefined)[] | null | undefined;
  url?: string | null | undefined;
  iframeUrl?: string | null | undefined;
  iframeErrorMessage?: string | null | undefined;

  type?: ServiceType | null | undefined;
  apiId?: ExternalApiId;

  theme?: DocumentsTheme;
  color?: ThemeColor;
  headerType?: HeaderType;
  headerImage1?: Upload | null | undefined;
  headerImage2?: Upload | null | undefined;
  backgroundImage?: Upload | null | undefined;
  backgroundImageOpacity?: number;
  backgroundImageHeight?: number;
  backgroundImageWidth?: number;

  logo?: Upload | null | undefined;
  logoOpacity?: number;
  logoHeight?: number;
  logoWidth?: number;
  logoPadding?: number;

  footerType?: footerType;

  showAddressInFooter?: boolean;
  showEmailInFooter?: boolean;
  showPhoneNumberInFooter?: boolean;
  showHeaderBarcodeInsteadOfLogo?: boolean;

  h1?: string | null | undefined;
  h2?: string | null | undefined;
  h3?: string | null | undefined;
  h4?: string | null | undefined;
  h5?: string | null | undefined;
  h6?: string | null | undefined;

  footerH1?: string | null | undefined;
  footerH2?: string | null | undefined;
  footerH3?: string | null | undefined;
  footerH4?: string | null | undefined;
  footerH5?: string | null | undefined;
  footerH6?: string | null | undefined;

  footerLogo?: Upload | null | undefined;
  footerLogoOpacity?: number;
  footerLogoHeight?: number;
  footerLogoWidth?: number;
  footerLogoPadding?: number;
  showFooterBarcodeInsteadOfLogo?: boolean;

  footerAddress?: string | null | undefined;
  footerEmail?: string | null | undefined;
  footerPhoneNumber1?: string | null | undefined;
  footerPhoneNumber2?: string | null | undefined;

  address?: string | null | undefined;
  header?: string | null | undefined;
  email?: string | null | undefined;
  leftBar: string;
  phoneNumber1?: string | null | undefined;
  phoneNumber2?: string | null | undefined;
  phoneNumber3?: string | null | undefined;
};

export type ServiceType =
  | "hospitalisations"
  | "emergencies"
  | "biology"
  | "imaging"
  | "medicalOffice"
  | "centralPharmacy";

export const serviceTypes: Record<
  ServiceType,
  {
    id: ServiceType;
    name: string;
  }
> = {
  hospitalisations: {
    id: "hospitalisations",
    name: "Hospitalisations",
  },
  emergencies: {
    id: "emergencies",
    name: "Urgences",
  },
  biology: {
    id: "biology",
    name: "Biologie",
  },
  centralPharmacy: {
    id: "centralPharmacy",
    name: "Pharmacie centrale",
  },
  imaging: {
    id: "imaging",
    name: "Imagerie",
  },
  medicalOffice: {
    id: "medicalOffice",
    name: "Cabinet médical",
  },
};

export type StructureUnitType =
  | "hospitalisation"
  | "consultation"
  | "emergency"
  | "medecineLegale"
  | "exploration"
  | "operatingRoom"
  | "pharmacie";

export const structureUnitTypes: Record<
  StructureUnitType,
  {
    id: StructureUnitType;
    name: string;
  }
> = {
  hospitalisation: {
    id: "hospitalisation",
    name: "Hospitalisation",
  },
  consultation: {
    id: "consultation",
    name: "Consultation",
  },
  emergency: {
    id: "emergency",
    name: "Urgence",
  },
  exploration: {
    id: "exploration",
    name: "Exploration",
  },
  medecineLegale: {
    id: "medecineLegale",
    name: "Médecine légale",
  },
  operatingRoom: {
    id: "operatingRoom",
    name: "Bloc opératoire",
  },
  pharmacie: {
    id: "pharmacie",
    name: "Pharmacie",
  },
};

export type StructureUnit = Row & {
  name: string;

  service?: Service;
  type?: StructureUnitType;
  chef?: User | null | undefined;
  boxes?: (Box | null | undefined)[] | null | undefined;
  beds?: (Bed | null | undefined)[] | null | undefined;
  generalCategories?:
    | (ProductGeneralCategory | null | undefined)[]
    | null
    | undefined;
  documentTemplates: (DocumentTemplate | null | undefined)[] | null | undefined;
};

export type Box = Row & {
  name?: string;
  ticketPrefix?: string | null | undefined;
  unit: StructureUnit | null | undefined;
  appointmentTypes?: (AppointmentType | null | undefined)[];
  currentAppointment?: Appointment | null | undefined;
  appointments: (Appointment | null | undefined)[];
  type?: StructureUnitType;
  isDisabled?: boolean;
};

export type Bed = Row & {
  name: string;
  unit: StructureUnit;
  type?: "hospitalisation" | "cure";
  color?: string | null | undefined;
  actions?: (BedAction | null | undefined)[] | null | undefined;
};

export type BedAction = Row & {
  type: BedActionType;
  bed: Bed;
  status: "pending" | "done";
  doneBy?: User | null | undefined;
  doneDatetime?: string | null | undefined;
  notes?: string | null | undefined;
};

export type BedActionType = "disinfection" | "reservation" | "emergency";

export const bedActionTypes: Record<
  BedActionType,
  { id: BedActionType; name: string }
> = {
  disinfection: {
    id: "disinfection",
    name: "Désinfection",
  },
  reservation: {
    id: "reservation",
    name: "Réservation",
  },
  emergency: {
    id: "emergency",
    name: "Urgence signalée",
  },
};

export type WaitingScreen = Row & {
  name: string;
  type: WaitingScreenType | null;
  format: WaitingScreenFormat | null;
  description: string;
  quadrant1?: WaitingScreenQuadrant | null | undefined;
  quadrant2?: WaitingScreenQuadrant | null | undefined;
  quadrant3?: WaitingScreenQuadrant | null | undefined;
  quadrant4?: WaitingScreenQuadrant | null | undefined;
  quadrant5?: WaitingScreenQuadrant | null | undefined;
  quadrant6?: WaitingScreenQuadrant | null | undefined;
  boxes: (Box | null | undefined)[];
  leftCornerText?: string | null | undefined;
  leftCornerImage1?: string | null | undefined;
  leftCornerImage2?: string | null | undefined;
  title?: string | null | undefined;
  rightCornerText?: string | null | undefined;
  rightCornerImage1?: string | null | undefined;
  rightCornerImage2?: string | null | undefined;
  inscructionsImage?: string | null | undefined;
  gridCount?: number | null | undefined;
  enableNextScreens: boolean | null | undefined;
  nextScreens: (WaitingScreen | null | undefined)[] | null | undefined;
  nextScreenIntervalInSeconds: number;
};

export type WaitingScreenType = "waitingRoom" | "displayScreen";

export const waitingScreenTypes: Record<
  WaitingScreenType,
  { id: WaitingScreenType; name: string }
> = {
  waitingRoom: {
    id: "waitingRoom",
    name: "Salle d'attente",
  },
  displayScreen: {
    id: "displayScreen",
    name: "Ecran d'affichage",
  },
};

export type WaitingScreenFormat =
  | "displayFormat1"
  | "displayFormat2"
  | "displayFormat3"
  | "displayFormat4"
  | "displayFormat5"
  | "displayFormat6"
  | "displayFormat7"
  | "displayFormat8";

export const waitingScreenFormats: Record<
  WaitingScreenFormat,
  { id: WaitingScreenFormat; name: string }
> = {
  displayFormat1: {
    id: "displayFormat1",
    name: "Format 1",
  },
  displayFormat2: {
    id: "displayFormat2",
    name: "Format 2",
  },
  displayFormat3: {
    id: "displayFormat3",
    name: "Format 3",
  },
  displayFormat4: {
    id: "displayFormat4",
    name: "Format 4",
  },
  displayFormat5: {
    id: "displayFormat5",
    name: "Format 5",
  },
  displayFormat6: {
    id: "displayFormat6",
    name: "Format 6",
  },
  displayFormat7: {
    id: "displayFormat7",
    name: "Format 7",
  },
  displayFormat8: {
    id: "displayFormat8",
    name: "Format 8",
  },
};

export type WaitingScreenQuadrant = Row & {
  type: WaitingScreenQuadrantType;
  file?: Upload | null | undefined;
};

export type WaitingScreenQuadrantType = "file" | "shiftsList";

// | "waitingCount"

export const waitingScreenQuadrantTypes: Record<
  WaitingScreenQuadrantType,
  { id: WaitingScreenQuadrantType; name: string }
> = {
  file: {
    id: "file",
    name: "Fichier",
  },
  shiftsList: {
    id: "shiftsList",
    name: "Liste de garde",
  },
  // waitingCount: {
  //   id: "waitingCount",
  //   name: "Patients en salle d'attente",
  // },
};

//
//
//! ///////////////////////////////////////
//! ////////    FEED ///////////////////
//! ///////////////////////////////////////
//
//

export type Post = Row & {
  content: string;

  category: PostCategory;
  uploads?: (Upload | null | undefined)[];
  comments: (FeedComment | null | undefined)[];
};

export type PostCategory = "case" | "event" | "news" | "replacement" | "other";

export const postCategories: Record<
  PostCategory,
  {
    id: PostCategory;
    name: string;
  }
> = {
  case: {
    id: "case",
    name: "Cas clinique",
  },
  event: {
    id: "event",
    name: "Evénement",
  },
  news: {
    id: "news",
    name: "Actualité",
  },
  replacement: {
    id: "replacement",
    name: "Remplacement",
  },
  other: {
    id: "other",
    name: "Autre",
  },
};

export type FeedComment = Row & {
  content: string;

  uploads?: Upload[];
  post: Post | null | undefined;
};

//
//
//! ///////////////////////////////////////
//! ////////    MESSAGES ///////////////////
//! ///////////////////////////////////////
//
//

export type Message = Row & {
  content: string;
  uploads?: (Upload | null | undefined)[];
  item: any;
  discussion: Discussion | null | undefined;
  readBy: (User | null | undefined)[];
};

export type Discussion = Row & {
  name: string | null | undefined;
  users: (User | null | undefined)[];
  lastMessage: Message | null | undefined;
  type: "simple" | "group" | "ticket";
  status: TicketStatus;
};

export type TicketStatus = "pending" | "finished" | "inProgress" | "canceled";

export const ticketStatuses: Record<
  TicketStatus,
  { id: TicketStatus; name: string; color: string; secondaryColor: string }
> = {
  pending: {
    id: "pending",
    name: "En attente",
    color: "rgba(255, 0, 0, 1)",
    secondaryColor: "rgba(255, 0, 0, 0.1)",
  },

  inProgress: {
    id: "inProgress",
    name: "En cours",
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  canceled: {
    id: "canceled",
    name: "Annulé",
    color: "rgba(0, 0, 255, 1)",
    secondaryColor: "rgba(0, 0, 255, 0.1)",
  },
  finished: {
    id: "finished",
    name: "Terminé",
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
};

//
//
//! ///////////////////////////////////////
//! ////////    HR ///////////////////
//! ///////////////////////////////////////
//
//
export type RhEventType = "affectation" | "shift" | "holiday" | "teaching";

export type RhEvent = Row & {
  start?: string;
  end?: string;
  notes?: string;

  type?: RhEventType;
  unit: StructureUnit | null | undefined;
  user: User | null | undefined;
};

//
//
//! ///////////////////////////////////////
//! ////////    CONFIG ///////////////////
//! ///////////////////////////////////////
//
//

export type RemedConfig = Row & {
  version: string | null | undefined;
  hospital?: Hospital | null | undefined;
  service?: Service | null | undefined;
  radiotherapyForm?: Reference | null | undefined;
  chemotherapyForm: Reference | null | undefined;
  surgeryForm: Reference | null | undefined;
  ventilationForm: Reference | null | undefined;
  anesthesiologyConsultationForm: Reference | null | undefined;
  anesthesiologySummaryForm: Reference | null | undefined;
  complicationForm: Reference | null | undefined;
  weight: Reference | null | undefined;
  height: Reference | null | undefined;
  bmi: Reference | null | undefined;
  surfaceArea: Reference | null | undefined;
  creatinineMdrd: Reference | null | undefined;

  creatinineCockcroft: Reference | null | undefined;
  anapathExam: Reference | null | undefined;
  consultationType: AppointmentType | null | undefined;
  chemotherapyType: AppointmentType | null | undefined;
};

//
//
//! ///////////////////////////////////////
//! ////////    STATISTICS ///////////////////
//! ///////////////////////////////////////
//
//

export type StatisticsReportType = "clinicalData" | "standardReport";

export type StatisticsReport = Row & {
  title?: string;
  males: number;
  females: number;
  patientGroups: Record<string, string[]>;
  patientsDates: Record<string, { date: string; count: number }>;
  appointmentsDates: Record<string, { date: string; count: number }>;
  appointments: (Appointment | null | undefined)[] | null | undefined;
  groups: StatisticsFilter[][];
  type: StatisticsReportType;
  patients: (Patient | null | undefined)[] | null | undefined;
};

export type StatisticsFilterType = "patient" | "appointment" | "refAnswer";

export type StatisticsValueType =
  | "minMaxDate"
  | "minMaxNumber"
  | "text"
  | "select"
  | "boolean";

export type StatisticsFilter = Row & {
  table: "patients" | "appointments" | "refAnswers" | "fieldAnswers";
  localId?: string | null | undefined;
  columnName: string;
  value: any;
  visibleName: string;
  visibleValue?: string | string[];
  valueType: StatisticsValueType;

  subFilters: StatisticsFilter[];
};

export type MinMax = Row & {
  min: string;
  max: string;
};

export type AppointmentTypeCount = {
  type:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  count: number;
  doneCount: number;
  boxesCount: Record<
    string,
    {
      boxId: string;
      boxName: string;
      unitName: string;
      count: number;
    }
  >;
};

export type StatisticsDateCount = {
  date: string;
  count: number;
  maleShiftCount: number;
  femaleShiftCount: number;
  maleDayCount: number;
  femaleDayCount: number;
};

export type HospitalisationsData = {
  datesCount: Record<string, StatisticsDateCount>;

  bedsCount: Record<
    string,
    {
      bedName: string;
      unitName: string;
      count: number;
    }
  >;
  averageStay: number;
  occupancyRate: number;
};

export type DashboardStatisticsReport = {
  patients: Record<string, StatisticsDateCount>;

  hospitalisations: HospitalisationsData;
  emergencies: Record<string, StatisticsDateCount>;
  consultations: Record<string, StatisticsDateCount>;
  deaths: Record<string, StatisticsDateCount>;
  appointments: Record<string, StatisticsDateCount>;
  appointmentTypes: Record<string, AppointmentTypeCount>;
  frequentMotives: Record<
    string,
    { reference: Reference | null | undefined; count: number }
  >;
  frequentDiagnostics: Record<
    string,
    { reference: Reference | null | undefined; count: number }
  >;
};

//
//
//! ///////////////////////////////////////
//! ////////    ECG plateform specific ///////////////////
//! ///////////////////////////////////////
//

export type ECG = Row & {
  patient: Patient | null;
  notes: string;
  uploads: (Upload | null | undefined)[];
  antecedents: (RefAnswer | null | undefined)[];
  interpretations: (EcgInterpretation | null | undefined)[];
  mainUpload?: Upload;
};

export type EcgInterpretation = Row & {
  content: string;
  uploads?: (Upload | null | undefined)[];
};

//
//
//! ///////////////////////////////////////
//! ////////    PATIENT ITEMS ///////////////////
//! ///////////////////////////////////////
//

export type FolderItem =
  | Hospitalisation
  | Consultation
  | Diagnostic
  | Exam
  | Sample;
export type EvolutionItem =
  | Billet
  | InitialExamination
  | Admission
  | Evolution
  | Shift
  | Intervention
  | Avis
  | Ventilation
  | AnesthesiologyConsultation
  | AnesthesiologySummary
  | Complication
  | Toxicity
  | TransferDemand
  | Discharge
  | TemporaryLeave;

export type EvolutionType =
  | "billet"
  | "initialExamination"
  | "admission"
  | "evolution"
  | "shift"
  | "intervention"
  | "avis"
  | "ventilation"
  | "anesthesiologyConsultation"
  | "anesthesiologySummary"
  | "complication"
  | "toxicity"
  | "transferDemand"
  | "discharge"
  | "temporaryLeave";

export type PatientItem = {
  id?: string;
  patient: Patient | null | undefined;
  creator: User | null | undefined;
  readBy: (User | null | undefined)[] | null | undefined;
  confidentiality: Confidentiality | null | undefined;
  _modifiedDate?: string | null | undefined;
  _date?: string | null | undefined;
};

export type Hospitalisation = PatientItem & {
  type: "hospitalisation";
  bed?: Bed;
  source?: "emergency" | "consultation" | "admission" | "other";
  sourceUnit?: StructureUnit;
  sourceStructure?: Service;
  sourceWilaya?: string;
  sourceHospitalName?: string;
  status: PatientItemStatus;
  motives?: (RefAnswer | null | undefined)[];
  diagnostics?: (RefAnswer | null | undefined)[];
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  discharge?: Discharge | null | undefined;
  temporaryLeave?: TemporaryLeave | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  admissionNumber?: string | null | undefined;
  entryMode?: EntryMode;
  decisionDoctor: User | null | undefined;
};

export type SummaryData = {
  item: FolderItem;
  doctors: User[];
  conclusion: string;
  evolutionItems: EvolutionItem[];
  exams: Exam[];
};

export type EntryMode =
  | "programmed"
  | "accident"
  | "emergency"
  | "evacuation"
  | "maternity"
  | "birth"
  | "hdj"
  | "victim"
  | "transfer";

//ayoub
export const entryModes: Record<EntryMode, { id: EntryMode; name: string }> = {
  programmed: {
    id: "programmed",
    name: "Programmée",
  },
  emergency: {
    id: "emergency",
    name: "Urgence",
  },
  hdj: {
    id: "hdj",
    name: "HDJ",
  },

  transfer: {
    id: "transfer",
    name: "Transfert",
  },

  evacuation: {
    id: "evacuation",
    name: "Evacuation",
  },
  maternity: {
    id: "maternity",
    name: "Maternité",
  },
  birth: {
    id: "birth",
    name: "Naissance",
  },
  accident: {
    id: "accident",
    name: "Accident",
  },
  victim: {
    id: "victim",
    name: "Victime d'évènement",
  },
};

export type Consultation = PatientItem & {
  type: "consultation";

  status?: PatientItemStatus;
  motives?: (RefAnswer | null | undefined)[];
  diagnostics?: (RefAnswer | null | undefined)[];
  functionalSigns: (RefAnswer | null | undefined)[];
  physicalSigns: (RefAnswer | null | undefined)[];
  clinicalExam?: RefAnswer | null | undefined;
  appointment?: Appointment | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  aiAudioSessions?: (AiAudioSession | null | undefined)[] | null | undefined;
};

export type InitialExamination = PatientItem & {
  type: "initialExamination";
  status?: PatientItemStatus;
  functionalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  physicalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  refAnswer?: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Admission = PatientItem & {
  type: "admission";
  status?: PatientItemStatus;
  functionalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  physicalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  refAnswer?: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Billet = PatientItem & {
  type: "billet";
  status?: PatientItemStatus;
  functionalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  physicalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  refAnswer?: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  patientDocument?: PatientDocument | null | undefined;
};

export type Evolution = PatientItem & {
  type: "evolution";
  status?: PatientItemStatus;
  functionalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  physicalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  refAnswer?: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Shift = PatientItem & {
  type: "shift";
  status?: PatientItemStatus;
  functionalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  physicalSigns: (RefAnswer | null | undefined)[] | null | undefined;
  refAnswer?: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Intervention = PatientItem & {
  type: "intervention";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Avis = PatientItem & {
  type: "avis";
  status?: PatientItemStatus;
  doctor: User | null | undefined;
  service: Service | null | undefined;
  parent?: FolderItem | null | undefined;
  demandNotes?: string | null | undefined;
  demandUploads?: (Upload | null | undefined)[] | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  isDemand: boolean | null | undefined;
};

export type Ventilation = PatientItem & {
  type: "ventilation";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type AnesthesiologyConsultation = PatientItem & {
  type: "anesthesiologyConsultation";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  refAnswer: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};
export type AnesthesiologySummary = PatientItem & {
  type: "anesthesiologySummary";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  refAnswer: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Complication = PatientItem & {
  type: "complication";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  refAnswer: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Consigne = PatientItem & {
  type: "consigne";
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Story = PatientItem & {
  type: "story";
  status?: PatientItemStatus;
  reference: Reference | null | undefined;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Sample = PatientItem & {
  type: "sample";
  externalDemandId?: string | null | undefined;
  parent?: FolderItem | null | undefined;
  // notes?: string | null | undefined;
  // reference: Reference | null | undefined;
  status?: PatientItemStatus | null | undefined;
  barcode: string | null | undefined;
  number?: number | null | undefined;
  year?: number | null | undefined;
  unit?: StructureUnit | null | undefined;
  numberOfPieces: number | null | undefined;
  refAnswer: RefAnswer | null | undefined;
  targetService: Service | null | undefined;
  conclusion?: string | null | undefined;
  exams: (Exam | null | undefined)[];
  steps?: (ExamStep | null | undefined)[] | null | undefined;
  slices?: (SampleSlice | null | undefined)[] | null | undefined;
  locationCategory?: SampleLocationCategory | null | undefined;
  location?: SampleLocation | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type SampleLocationCategory =
  | "gastro-intestinal"
  | "orl"
  | "cardio-vasculaire"
  | "pleuro-pulmonaire"
  | "gynéco"
  | "hémato"
  | "urinaire"
  | "endocrino"
  | "autres";

export const sampleLocationCategories: Record<
  SampleLocationCategory,
  { id: SampleLocationCategory; name: string }
> = {
  "gastro-intestinal": { id: "gastro-intestinal", name: "Gastro-intestinal" },
  "pleuro-pulmonaire": { id: "pleuro-pulmonaire", name: "Pleuro-pulmonaire" },
  gynéco: { id: "gynéco", name: "Gynéco" },
  orl: { id: "orl", name: "ORL" },
  "cardio-vasculaire": { id: "cardio-vasculaire", name: "Cardio-vasculaire" },
  urinaire: { id: "urinaire", name: "Urinaire" },
  hémato: { id: "hémato", name: "Hémato" },
  endocrino: { id: "endocrino", name: "Endocrino" },
  autres: { id: "autres", name: "Autres" },
};

export type SampleLocation =
  | "bouche"
  | "glandes salivaires"
  | "oesophage"
  | "estomac"
  | "duodenum"
  | "ileum"
  | "colon"
  | "vesicule biliaire et voies biliaires"
  | "appendice"
  | "rectum"
  | "peritoneum"
  | "anus"
  | "pancreas"
  | "foie"
  | "cavum"
  | "larynx"
  | "amygdale"
  | "coeur"
  | "péricarde"
  | "vaisseaux"
  | "poumon"
  | "mediastin"
  | "plèvre"
  | "rein"
  | "pbr"
  | "uretère"
  | "vesse"
  | "urètre"
  | "prostate"
  | "testicules"
  | "sein"
  | "placenta"
  | "produit de conception"
  | "ovaire"
  | "trompes"
  | "uterus"
  | "col"
  | "vagin"
  | "vulve"
  | "ganglion"
  | "pbo"
  | "rate"
  | "thyroide"
  | "parathyroide"
  | "surrénale"
  | "peau et phanères"
  | "ostéo-articulaire"
  | "muscle"
  | "tissu mou"
  | "système nerveux"
  | "oeil et annexes"
  | "autopsie"
  | "non précisé"
  | "autres";

export const sampleLocations: Record<
  SampleLocation,
  { id: SampleLocation; name: string; category: SampleLocationCategory }
> = {
  bouche: { id: "bouche", name: "Bouche", category: "gastro-intestinal" },
  "glandes salivaires": {
    id: "glandes salivaires",
    name: "Glandes salivaires",
    category: "gastro-intestinal",
  },
  oesophage: {
    id: "oesophage",
    name: "Oesophage",
    category: "gastro-intestinal",
  },
  estomac: { id: "estomac", name: "Estomac", category: "gastro-intestinal" },
  duodenum: { id: "duodenum", name: "Duodenum", category: "gastro-intestinal" },
  foie: { id: "foie", name: "Foie", category: "gastro-intestinal" },
  "vesicule biliaire et voies biliaires": {
    id: "vesicule biliaire et voies biliaires",
    name: "Vesicule biliaire et voies biliaires",
    category: "gastro-intestinal",
  },
  pancreas: { id: "pancreas", name: "Pancreas", category: "gastro-intestinal" },

  ileum: { id: "ileum", name: "Ileum", category: "gastro-intestinal" },
  colon: { id: "colon", name: "Colon", category: "gastro-intestinal" },
  appendice: {
    id: "appendice",
    name: "Appendice",
    category: "gastro-intestinal",
  },
  rectum: { id: "rectum", name: "Rectum", category: "gastro-intestinal" },

  peritoneum: {
    id: "peritoneum",
    name: "Peritoneum",
    category: "gastro-intestinal",
  },
  anus: { id: "anus", name: "Anus", category: "gastro-intestinal" },

  cavum: { id: "cavum", name: "Cavum", category: "orl" },
  larynx: { id: "larynx", name: "Larynx", category: "orl" },
  amygdale: { id: "amygdale", name: "Amygdale", category: "orl" },
  coeur: { id: "coeur", name: "Coeur", category: "cardio-vasculaire" },
  péricarde: {
    id: "péricarde",
    name: "Péricarde",
    category: "cardio-vasculaire",
  },

  vaisseaux: {
    id: "vaisseaux",
    name: "Vaisseaux",
    category: "cardio-vasculaire",
  },
  poumon: { id: "poumon", name: "Poumon", category: "pleuro-pulmonaire" },
  mediastin: {
    id: "mediastin",
    name: "Mediastin",
    category: "pleuro-pulmonaire",
  },
  plèvre: { id: "plèvre", name: "Plèvre", category: "pleuro-pulmonaire" },

  rein: { id: "rein", name: "Rein", category: "urinaire" },
  pbr: { id: "pbr", name: "PBR", category: "urinaire" },
  uretère: { id: "uretère", name: "Uretère", category: "urinaire" },
  vesse: { id: "vesse", name: "Vesse", category: "urinaire" },
  urètre: { id: "urètre", name: "Urètre", category: "urinaire" },
  prostate: { id: "prostate", name: "Prostate", category: "urinaire" },
  testicules: { id: "testicules", name: "Testicules", category: "urinaire" },

  sein: { id: "sein", name: "Sein", category: "gynéco" },
  placenta: { id: "placenta", name: "Placenta", category: "gynéco" },
  "produit de conception": {
    id: "produit de conception",
    name: "Produit de conception",
    category: "gynéco",
  },
  ovaire: { id: "ovaire", name: "Ovaire", category: "gynéco" },
  trompes: { id: "trompes", name: "Trompes", category: "gynéco" },
  uterus: { id: "uterus", name: "Uterus", category: "gynéco" },
  col: { id: "col", name: "Col", category: "gynéco" },
  vagin: { id: "vagin", name: "Vagin", category: "gynéco" },
  vulve: { id: "vulve", name: "Vulve", category: "gynéco" },

  ganglion: { id: "ganglion", name: "Ganglion", category: "hémato" },
  pbo: { id: "pbo", name: "PBO", category: "hémato" },
  rate: { id: "rate", name: "Rate", category: "hémato" },

  thyroide: { id: "thyroide", name: "Thyroide", category: "endocrino" },
  parathyroide: {
    id: "parathyroide",
    name: "Parathyroide",
    category: "endocrino",
  },
  surrénale: { id: "surrénale", name: "Surrénale", category: "endocrino" },

  "peau et phanères": {
    id: "peau et phanères",
    name: "Peau et phanères",
    category: "autres",
  },
  "ostéo-articulaire": {
    id: "ostéo-articulaire",
    name: "Ostéo-articulaire",
    category: "autres",
  },
  muscle: { id: "muscle", name: "Muscle", category: "autres" },
  "tissu mou": { id: "tissu mou", name: "Tissu mou", category: "autres" },
  "système nerveux": {
    id: "système nerveux",
    name: "Système nerveux",
    category: "autres",
  },
  "oeil et annexes": {
    id: "oeil et annexes",
    name: "Oeil et annexes",
    category: "autres",
  },
  autopsie: { id: "autopsie", name: "Autopsie", category: "autres" },
  autres: { id: "autres", name: "Autres", category: "autres" },
  "non précisé": { id: "non précisé", name: "Non précisé", category: "autres" },
};

export type Diagnostic = PatientItem & {
  type: "diagnostic";
  reference?: Reference | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  content: RefAnswer | null | undefined;
  exams: (RefAnswer | null | undefined)[] | null | undefined;
  metastasis: (RefAnswer | null | undefined)[] | null | undefined;
  story?: Story | null | undefined;
};

export type Radiotherapy = PatientItem & {
  type: "radiotherapy";
  reference: Reference;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Surgery = PatientItem & {
  type: "surgery";
  reference: Reference;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Chemotherapy = PatientItem & {
  type: "chemotherapy";
  reference: Reference;
  content: RefAnswer | null | undefined;
  parent?: FolderItem | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

export type Metastasis = PatientItem & {
  type: "metastasis";
  reference: Reference | null | undefined;
  content: RefAnswer | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  parent?: FolderItem | null | undefined;
};

export type Toxicity = PatientItem & {
  type: "toxicity";
  reference: Reference;
  content: RefAnswer | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  parent?: FolderItem | null | undefined;
};

export type ExamGroup = Row & {
  type: "examGroup";
  parent?: FolderItem | null | undefined;
  patient: Patient | null | undefined;
  referencesGroup: ReferencesGroup | null | undefined;
};

export type Exam = PatientItem & {
  type: "exam" | "notExam" | "abdeslam";
  reference: Reference;
  content: RefAnswer | null | undefined;
  document?: PatientDocument;
  status: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  parent?: FolderItem | null | undefined;
  samples?: (Sample | null | undefined)[] | null | undefined;
  examGroup?: ExamGroup | null | undefined;
  // steps?: (ExamStep | null | undefined)[] | null | undefined;
};

export type SimpleExam = Pick<
  Exam,
  | "id"
  | "parent"
  | "examGroup"
  | "patient"
  | "type"
  | "reference"
  | "samples"
  | "creator"
  | "status"
  | "_date"
>;

export type ExamStepsGroup = Row & {
  name: string;
  steps: (Reference | null | undefined)[];
};

export type ExamStep = PatientItem & {
  type: "examStep";
  reference: Reference;
  content: RefAnswer | null | undefined;
  status?: PatientItemStatus;
  parent?: Sample | null | undefined;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  slices?: (SampleSlice | null | undefined)[] | null | undefined;
  structureUnit?: StructureUnit | null | undefined;
  doneBy?: User | null | undefined;
  affectedTo: (User | null | undefined)[] | null | undefined;
};

export type ExamDemand = PatientItem & {
  type: "examDemand";
  reference: Reference;
  content: RefAnswer | null | undefined;
  status?: PatientItemStatus;
  notes?: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  parent?: FolderItem | null | undefined;
};

export type MesureSession = PatientItem & {
  type: "mesureSession";
  mesures: (RefAnswer | null | undefined)[] | null | undefined;
  parent: FolderItem | null | undefined;
};

//
//! ///////////////////////////////////////
//! ////////    ANAPATH  ///////////////////
//! ///////////////////////////////////////
//

// export type SampleBloc = Row & {
//   barcode: string | null | undefined;
//   sample: Sample | null | undefined;
//   notes?: string | null | undefined;
//   status?: PatientItemStatus | null | undefined;
//   location: string | null | undefined;
//   conclusion?: string | null | undefined;
// };

export type SampleSlice = Row & {
  barcode: string | null | undefined;
  rootBarcode: string | null | undefined;
  sampleNumber: number;
  sample: Sample | null | undefined;
  // bloc?: SampleBloc | null | undefined;
  notes?: string | null | undefined;
  status?: PatientItemStatus | null | undefined;
  location: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
};

//
//! ///////////////////////////////////////
//! ////////    NOTIFICATIONS  ///////////////////
//! ///////////////////////////////////////
//
export type Notification = Row & {
  type: NotificationType;
  status: NotificationStatus;
  item: any;
  service?: Service | null | undefined;
  patient?: Patient | null | undefined;
  targetUsers: (User | null | undefined)[] | null | undefined;
  readBy: (User | null | undefined)[];
};

export type ItemSubscription = Row & {
  item: any;
  user: User | null | undefined;
  patient?: Patient | null | undefined;
};

export type NotificationType =
  | "newEcg"
  | "newEcgInterpretation"
  | "examResult"
  | "itemChange"
  | "message"
  | "avisDemand"
  | "avisAnswer"
  | "transferDemand"
  | "transferAnswer"
  | "confidentialityDemand"
  | "confidentialityAnswer";

export const notificationTypes: Record<
  NotificationType,
  {
    id: NotificationType;
    name: string;
    body: string;
  }
> = {
  newEcg: {
    id: "newEcg",
    name: "Nouvel ECG",
    body: "Un nouvel ECG a été ajouté",
  },
  newEcgInterpretation: {
    id: "newEcgInterpretation",
    name: "Nouvelle interprétation d'ECG",
    body: "Une nouvelle interprétation d'ECG a été ajoutée",
  },
  examResult: {
    id: "examResult",
    name: "Résultat d'examen",
    body: "Un nouveau résultat d'examen a été ajouté",
  },
  message: {
    id: "message",
    name: "Message",
    body: "Vous avez reçu un nouveau message",
  },
  avisDemand: {
    id: "avisDemand",
    name: "Demande d'avis",
    body: "Une nouvelle demande d'avis a été ajoutée",
  },
  avisAnswer: {
    id: "avisAnswer",
    name: "Réponse à une demande d'avis",
    body: "Une réponse à une demande d'avis a été ajoutée",
  },
  transferDemand: {
    id: "transferDemand",
    name: "Demande de transfert",
    body: "Une nouvelle demande de transfert a été ajoutée",
  },
  confidentialityDemand: {
    id: "confidentialityDemand",
    name: "Demande d'accès à un élément",
    body: "Une nouvelle demande d'accès à un élément a été ajoutée",
  },
  confidentialityAnswer: {
    id: "confidentialityAnswer",
    name: "Réponse à une demande de confidentialité",
    body: "Une réponse à une demande de confidentialité a été ajoutée",
  },
  itemChange: {
    id: "itemChange",
    name: "Modification d'un élément",
    body: "Un élément que vous suivez a été modifié",
  },
  transferAnswer: {
    id: "transferAnswer",
    name: "Réponse à une demande de transfert",
    body: "Une réponse à une demande de transfert a été ajoutée",
  },
};

export type NotificationStatus =
  | "pending"
  | "finished"
  | "canceled"
  | "deleted";
export const notificationStatuses: Record<
  NotificationStatus,
  {
    id: NotificationStatus;
    name: string;
  }
> = {
  pending: {
    id: "pending",
    name: "En attente",
  },
  finished: {
    id: "finished",
    name: "Fait",
  },
  canceled: {
    id: "canceled",
    name: "Annulé",
  },
  deleted: {
    id: "deleted",
    name: "Supprimé",
  },
};

//
//! ///////////////////////////////////////
//! ////////    DISCHARGE  ///////////////////
//! ///////////////////////////////////////
//

export type Discharge = PatientItem & {
  type: "discharge";
  dischargeType: "immediate" | "planned";
  exitMode: ExitMode;
  exitDate: string | null | undefined;
  status: PatientItemStatus;
  notes: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  conclusion?: string | null | undefined;
  parent: Hospitalisation | null | undefined;
};

export type TemporaryLeave = PatientItem & {
  type: "temporaryLeave";
  exitDate: string | null | undefined;
  returnDate: string | null | undefined;
  status: PatientItemStatus;
  notes: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  conclusion?: string | null | undefined;
  parent: Hospitalisation | null | undefined;
};

export type TransferDemand = PatientItem & {
  type: "transferDemand";
  exitDate: string | null | undefined;
  service: Service | null | undefined;
  bed?: Bed | null | undefined;
  unit: StructureUnit | null | undefined;
  status: PatientItemStatus;
  notes: string | null | undefined;
  conclusion?: string | null | undefined;
  uploads?: (Upload | null | undefined)[] | null | undefined;
  parent: Hospitalisation | null | undefined;
  acceptedBy: User | null | undefined;
};

export type ExitMode =
  | "authorized"
  | "againstMedicalAdvice"
  | "evacuation"
  | "escape"
  | "death";

export const exitModes: Record<ExitMode, { id: ExitMode; name: string }> = {
  authorized: {
    id: "authorized",
    name: "Sortie autorisée",
  },
  againstMedicalAdvice: {
    id: "againstMedicalAdvice",
    name: "Sortie contre avis médical",
  },
  evacuation: {
    id: "evacuation",
    name: "Evacuation",
  },
  escape: {
    id: "escape",
    name: "Evasion",
  },
  death: {
    id: "death",
    name: "Décès",
  },
};

//
//! ///////////////////////////////////////
//! ////////    TYPES  ///////////////////
//! ///////////////////////////////////////
//

export type PatientItemStatus =
  | "pending"
  | "skipped"
  | "inProgress"
  | "waitingForValidation"
  | "validated"
  | "finished"
  | "problem"
  | "canceled"
  | "refused";

export const patientItemStatuses: Record<
  PatientItemStatus,
  {
    id: PatientItemStatus;
    name: string;
    color?: string;
    secondaryColor?: string;
    colorName?: "blue" | "orange" | "red" | "gray" | "green" | "purple";
  }
> = {
  pending: {
    id: "pending",
    name: "En attente",
  },

  skipped: {
    id: "skipped",
    name: "Passé (non effectué)",
    color: "rgba(0, 0, 0,0.8)",
    secondaryColor: "rgba(0, 0, 0,0.1)",
    colorName: "gray",
  },
  inProgress: {
    id: "inProgress",
    name: "En cours",
    colorName: "blue",
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  waitingForValidation: {
    id: "waitingForValidation",
    name: "En attente de validation",
    colorName: "orange",
    color: "rgba(255, 124, 2,1)",
    secondaryColor: "rgba(255, 124, 2,0.1)",
  },
  validated: {
    id: "validated",
    name: "Validé",
    colorName: "blue",
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  finished: {
    id: "finished",
    name: "Terminé",
    colorName: "green",
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
  problem: {
    id: "problem",
    name: "Problème",
    colorName: "red",
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },
  canceled: {
    id: "canceled",
    name: "Annulé",
    colorName: "red",
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },
  refused: {
    id: "refused",
    name: "Refusé",
    colorName: "red",
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },
};
export type CardItemType =
  | "simple"
  | "gray"
  | "red"
  | "green"
  | "blue"
  | "orange"
  | "yellow"
  | "purple"
  | "pink"
  | "white"
  | "consultation"
  | "evolution"
  | "shift"
  | "discharge"
  | "temporaryLeave"
  | "transferDemand"
  | "anesthesiologyConsultation"
  | "anesthesiologySummary"
  | "complication"
  | "score"
  | "story"
  | "intervention"
  | "ventilation"
  | "initialExamination"
  | "admission"
  | "billet"
  | "avis"
  | "consigne"
  | "mesure"
  | "metastasis"
  | "examStep"
  | "upload"
  | "examDemand"
  | "toxicity"
  | "radiotherapy"
  | "surgery"
  | "cure"
  | "exam"
  | "diagnostic"
  | "appointment"
  | "document"
  | "ordonnance"
  | "hospitalisation"
  | "sample";

export type HealthStatus =
  | "veryGood"
  | "good"
  | "normal"
  | "bad"
  | "veryBad"
  | "stopped";
export const healthStatuses: Record<
  HealthStatus,
  {
    color: string;
    name: string;
  }
> = {
  veryGood: {
    color: "green",
    name: "Très bon",
  },
  good: {
    color: "green",
    name: "Bon",
  },
  normal: {
    color: "blue",
    name: "Normal",
  },
  bad: {
    color: "orange",
    name: "Mauvais",
  },
  veryBad: {
    color: "red",
    name: "Très mauvais",
  },
  stopped: {
    color: "purple",
    name: "Arrêté",
  },
} as const;

export type ServerPoll = Row & {
  status: ServerStatus;
  name: string;
  url: string;
};

export type ServerStatus = "up" | "down" | "unknown";

export const serverStatuses: Record<
  ServerStatus,
  {
    id: ServerStatus;
    name: string;
    icon?: any;
    color: string;
    secondaryColor: string;
  }
> = {
  up: {
    id: "up",
    name: "Connecté",
    icon: null,
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
  down: {
    id: "down",
    name: "Déconnecté",
    icon: null,
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },

  unknown: {
    id: "unknown",
    name: "Inconnu",
    icon: null,
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
};

export type InputsType =
  | "variable"
  | "title"
  | "subtitle"
  | "text"
  | "textarea"
  | "htmlarea"
  | "select"
  | "select-multiple"
  | "dialogSelect"
  | "chips"
  | "chips-multiple"
  | "number"
  | "date"
  | "datetime"
  | "upload"
  | "uploads"
  | "boolean"
  | "password";

export const inputTypes: Record<
  InputsType,
  {
    id: InputsType;
    name: string;
    shoudShowInForm: boolean;
  }
> = {
  text: {
    id: "text",
    name: "Texte",
    shoudShowInForm: true,
  },
  textarea: {
    id: "textarea",
    name: "Texte libre",
    shoudShowInForm: true,
  },

  number: {
    id: "number",
    name: "Nombre",
    shoudShowInForm: true,
  },
  date: {
    id: "date",
    name: "Date",
    shoudShowInForm: true,
  },
  datetime: {
    id: "datetime",
    name: "Date et heure",
    shoudShowInForm: true,
  },
  upload: {
    id: "upload",
    name: "Pièce jointe",
    shoudShowInForm: true,
  },
  chips: {
    id: "chips",
    name: "Choix",
    shoudShowInForm: true,
  },
  "chips-multiple": {
    id: "chips-multiple",
    name: "Choix multiple",
    shoudShowInForm: false,
  },

  select: {
    id: "select",
    name: "Menu",
    shoudShowInForm: true,
  },
  "select-multiple": {
    id: "select-multiple",
    name: "Menu multiple",
    shoudShowInForm: false,
  },
  boolean: {
    id: "boolean",
    name: "Oui/Non",
    shoudShowInForm: true,
  },

  uploads: {
    id: "uploads",
    name: "Pièces jointes multiples",
    shoudShowInForm: false,
  },

  title: {
    id: "title",
    name: "Titre",
    shoudShowInForm: true,
  },
  subtitle: {
    id: "subtitle",
    name: "Sous-titre",
    shoudShowInForm: true,
  },

  dialogSelect: {
    id: "dialogSelect",
    name: "Sélection dans une boite de dialogue",
    shoudShowInForm: false,
  },

  variable: {
    id: "variable",
    name: "Variable",
    shoudShowInForm: false,
  },

  htmlarea: {
    id: "htmlarea",
    name: "Zone de texte riche",
    shoudShowInForm: false,
  },
  password: {
    id: "password",
    name: "Mot de passe",
    shoudShowInForm: false,
  },
};

export const cardTypes = {
  simple: {
    id: "simple",
    name: "Simple",
    icon: null,
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  gray: {
    id: "gray",
    name: "Gris",
    icon: null,
    color: "rgba(0, 0, 0,0.8)",
    secondaryColor: "rgba(0, 0, 0,0.1)",
  },
  red: {
    id: "red",
    name: "Rouge",
    icon: null,
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },
  green: {
    id: "green",
    name: "Vert",
    icon: null,
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
  blue: {
    id: "blue",
    name: "Bleu",
    icon: null,
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  yellow: {
    id: "yellow",
    name: "Jaune",
    icon: null,
    color: "rgba(255, 255, 0,1)",
    secondaryColor: "rgba(255, 255, 0,0.1)",
  },
  orange: {
    id: "orange",
    name: "Orange",
    icon: null,
    color: "rgba(255, 124, 2,1)",
    secondaryColor: "rgba(255, 124, 2,0.1)",
  },
  purple: {
    id: "purple",
    name: "Violet",
    icon: null,
    color: "rgba(144, 0, 255,1)",
    secondaryColor: "rgba(144, 0, 255,0.1)",
  },
  pink: {
    id: "pink",
    name: "Rose",
    icon: null,
    color: "rgba(255, 77, 115,1)",
    secondaryColor: "rgba(255, 77, 115,0.1)",
  },
  white: {
    id: "white",
    name: "Blanc",
    icon: null,
    color: "rgba(255, 255, 255,1)",
    secondaryColor: "rgba(255, 255, 255,0.1)",
  },
};

//
//
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! //////// Pharmacy ///////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//! ///////////////////////////////////////
//
//

export type Product = Row & {
  name: string;
  code: string;
  ref?: string;
  isClone?: boolean;
  barcode: string;
  condition?: ProductCondition | null | undefined;
  conditionStr?: string | null | undefined;
  measureUnit?: FieldUnit | null | undefined;
  suppliers: (Supplier | null | undefined)[] | null | undefined;
  brand?: Brand | null | undefined;
  image: Upload | null | undefined;
  description: string;
  price: number;
  currency: Currency;
  generalCategory: ProductGeneralCategory | null | undefined;
  category: ProductCategory | null | undefined;
  tva?: number;
  usageCount?: number;
  structureUnit: StructureUnit | null | undefined;
  clones?: (Product | null | undefined)[] | null | undefined;
};

export type ProductWithStockItem = Product & {
  stockItem: StockItem | null | undefined;
};

export type Brand = Row & {
  image: Upload | null | undefined;
  name: string;
  description: string;
};

export type ProductCondition = Row & {
  name: string;
  description: string;
};

export type ProductGeneralCategory = Row & {
  name: string;
  description: string;
  code: string;
};

export type ProductCategory = Row & {
  name: string;
  code: string;
  description: string;
  parent: ProductGeneralCategory | null | undefined;
  status: "active" | "hidden" | null | undefined;
};

export type Supplier = Row & {
  image: Upload | null | undefined;
  type: SupplierType;
  name: string;
  description: string;
  contacts: (SupplierContact | null | undefined)[] | null | undefined;
  address: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  RC: string;
  NIF: string;
  NIS: string;
  AI: string;
  RIB: string;
  agreementNumber: string;
};
export type SupplierType =
  | "hospital"
  | "laboratory"
  | "administrative"
  | "other";

export type SupplierContact = Row & {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  position: string;
};

export type Currency = "DZD" | "EUR" | "USD";
export const currencies: Record<Currency, { id: Currency; name: string }> = {
  DZD: {
    id: "DZD",
    name: "Dinar Algérien",
  },
  EUR: {
    id: "EUR",
    name: "Euro",
  },
  USD: {
    id: "USD",
    name: "Dollar Américain",
  },
};

//! ///////////////////////////////////////
//! //////// Locations ///////////////////
//! ///////////////////////////////////////

export type GeneralLocation = Row & {
  name: string;
  description: string;
};
export type LocationType = "warehouse" | "coldRoom" | "room" | "shelf" | "box";
export const locationTypes: Record<
  LocationType,
  { id: LocationType; name: string }
> = {
  warehouse: {
    id: "warehouse",
    name: "Entrepôt",
  },
  coldRoom: {
    id: "coldRoom",
    name: "Chambre froide",
  },
  room: {
    id: "room",
    name: "Chambre",
  },
  shelf: {
    id: "shelf",
    name: "Etagère",
  },
  box: {
    id: "box",
    name: "Boxe",
  },
};

export type Location = Row & {
  name: string;
  type: LocationType;
  description: string;
  parent: GeneralLocation | null | undefined;
};

export type LocationChange = Row & {
  stockLot: StockLot | null | undefined;
  oldLocation: Location | null | undefined;
  newLocation: Location | null | undefined;
};

//! ///////////////////////////////////////
//! //////// Tenders ///////////////////
//! ///////////////////////////////////////

// let counts = {};
// for (let item of a) {
//   counts[item.tender] ||= 0;
//   counts[item.tender]++;
//   if (counts[item.tender] > 1) {
//     console.log("tender", item.tender);
//   }
// }

// console.log(counts);

export type Tender = Row & {
  name: string;
  code: string;
  description: string;
  supplier: Supplier | null | undefined;
  startDate: string;
  endDate: string;
  status: "pending" | "ongoing" | "finished" | "canceled";
  totalValue: number;
  currency: Currency;
  structureUnit: StructureUnit | null | undefined;
  year?: number;
  amourTenderType?: "M" | "C" | "P" | "D";
  lots: (TenderLot | null | undefined)[];
};

export type TenderLot = Row & {
  name: string;
  code: string;
  year?: string;
  imputation: Imputation | null | undefined;
  tender: Tender | null | undefined;
  description: string;
  status: "pending" | "ongoing" | "finished" | "canceled";
  supplier: Supplier | null | undefined;
  currency: Currency;
  totalValue: number;
  amourTenderType?: "M" | "C" | "P" | "D";
  items: (TenderItem | null | undefined)[];
  structureUnit: StructureUnit | null | undefined;
};

export type TenderItem = Row & {
  product: Product | null | undefined;
  tenderLot: TenderLot | string;
  quantity: number;
  minimumQuantity: number;
  maximumQuantity: number;
  forcastedQuantity: number;
  commandedQuantity: number;
  deliveredQuantity: number;
  unitPrice: number;
  totalPrice: number;
  tva: number;
};

export type CommandType =
  | "tenderCommand"
  | "serviceCommand"
  | "automaticVentilationCommand"
  | "freeCommand"
  | "donation"
  | "reintegration"
  | "peremption"
  | "decharge"
  | "casse";

export const commandTypes: Record<
  CommandType,
  { id: CommandType; name: string }
> = {
  tenderCommand: {
    id: "tenderCommand",
    name: "Commande",
  },
  freeCommand: {
    id: "freeCommand",
    name: "Commande libre",
  },
  serviceCommand: {
    id: "serviceCommand",
    name: "Transfert vers service",
  },
  automaticVentilationCommand: {
    id: "automaticVentilationCommand",
    name: "Ventilation automatique vers service",
  },

  donation: {
    id: "donation",
    name: "Don",
  },

  reintegration: {
    id: "reintegration",
    name: "Réintégration",
  },

  decharge: {
    id: "decharge",
    name: "Décharge",
  },

  peremption: {
    id: "peremption",
    name: "Péremption",
  },

  casse: {
    id: "casse",
    name: "Casse",
  },
};

export type Command = Row & {
  code: string;
  type: CommandType;
  sourceCommand?: Command | null | undefined;
  tenderLot: TenderLot | null | undefined;
  status: CommandStatus;
  imputation: Imputation | null | undefined;
  supplier: Supplier | null | undefined;
  commandedFromService: Service | null | undefined;
  commandorService: Service | null | undefined;
  commandorStructureUnit?: StructureUnit | null | undefined;
  automaticVentilation: Service | null | undefined;
  commandedItems: (CommandItem | null | undefined)[] | null | undefined;
  deliveries: (Delivery | null | undefined)[] | null | undefined;
  totalPrice: number;
  ordonnances?: (Ordonnance | null | undefined)[] | null | undefined;
  patients?: (Patient | null | undefined)[] | null | undefined;
  notes?: string | null | undefined;
  number: number | null | undefined;
  year: string | null | undefined;
  structureUnit: StructureUnit | null | undefined;
};

export type Imputation = Row & {
  name: string;
  description: string;
  budget: number | null | undefined;
  service: Service | null | undefined;
};

export type CommandStatus =
  | "pending"
  | "validated"
  | "beingDelivered"
  | "delivered"
  | "deliveredWithErrors"
  | "finished"
  | "canceled";

export const commandStatuses: Record<
  CommandStatus,
  { id: CommandStatus; name: string }
> = {
  pending: {
    id: "pending",
    name: "En attente",
  },
  validated: {
    id: "validated",
    name: "Validée",
  },
  beingDelivered: {
    id: "beingDelivered",
    name: "En cours de livraison",
  },
  delivered: {
    id: "delivered",
    name: "Livrée",
  },
  deliveredWithErrors: {
    id: "deliveredWithErrors",
    name: "Livrée avec erreurs",
  },
  canceled: {
    id: "canceled",
    name: "Annulée",
  },
  finished: {
    id: "finished",
    name: "Terminée",
  },
};

export type CommandItem = Row & {
  tenderItem: TenderItem | null | undefined;
  command: Command | null | undefined;
  product: Product | null | undefined;
  demandedQuantity: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  maximumQuantity: number | null | undefined;
  unitPrice: number;
  tva: number | null | undefined;
  totalPrice: number;
  structureUnit: StructureUnit | null | undefined;
};

export type DeliveryType =
  | "commandDelivery"
  | "freeDelivery"
  | "donation"
  | "reintegration"
  | "retour";

export const deliveryTypes: Record<
  DeliveryType,
  { id: DeliveryType; name: string }
> = {
  commandDelivery: {
    id: "commandDelivery",
    name: "Commande",
  },
  freeDelivery: {
    id: "freeDelivery",
    name: "Entrée libre",
  },
  donation: {
    id: "donation",
    name: "Don",
  },
  reintegration: {
    id: "reintegration",
    name: "Réintégration",
  },
  retour: {
    id: "retour",
    name: "Retour",
  },
};

export type DeliverySearchIn =
  | "all"
  | "bemNumber"
  | "deliveryNumber"
  | "commandNumber"
  | "invoiceNumber"
  | "supplier";

export const deliverySearchIns: Record<
  DeliverySearchIn,
  { id: DeliverySearchIn; name: string }
> = {
  all: {
    id: "all",
    name: "Rechercher par",
  },

  bemNumber: {
    id: "bemNumber",
    name: "Par Numéro BEM",
  },
  deliveryNumber: {
    id: "deliveryNumber",
    name: "Par Numéro de livraison",
  },
  commandNumber: {
    id: "commandNumber",
    name: "Par Numéro de commande",
  },
  invoiceNumber: {
    id: "invoiceNumber",
    name: "Par Numéro de facture",
  },
  supplier: {
    id: "supplier",
    name: "Par Fournisseur",
  },
};

export type Delivery = Row & {
  status: DeliveryStatus;
  type: DeliveryType;
  automaticVentilation?: Service | null | undefined;
  hasBeenVentilated?: boolean | null | undefined;
  sender: Service | null | undefined;
  receiver: Service | null | undefined;
  command: Command | null | undefined;
  sentLots: (DeliveryLot | null | undefined)[];
  receivedLots: (DeliveryLot | null | undefined)[];
  releaseNumber: string | null | undefined;
  deliveryNumber: string | null | undefined;
  bemNumber: number | null | undefined;
  bemDate: string | null | undefined;
  structureUnit: StructureUnit | null | undefined;
  invoice?: Invoice | null | undefined;
  deliveredQuantity?: number | null | undefined;
  deliveryDate: string | null | undefined;
  notes?: string | null | undefined;
  supplier?: Supplier | null | undefined;
};

export type Invoice = Row & {
  command: Command | null | undefined;
  supplier: Supplier | null | undefined;
  invoiceNumber: string | null | undefined;
  invoiceDate: string | null | undefined;
  invoiceTVAPercentage: number | null | undefined;
  totalPrice: number | null | undefined;
  totalPriceHT: number | null | undefined;
  items: (InvoiceItem | null | undefined)[] | null | undefined;
  structureUnit: StructureUnit | null | undefined;
};

export type InvoiceItem = Row & {
  product: Product | null | undefined;
  quantity: number | null | undefined;
  unitPrice: number;
  tva: number | null | undefined;
  totalPrice: number;
  structureUnit: StructureUnit | null | undefined;
  commandItem: CommandItem | null | undefined;
};

export type DeliveryStatus =
  | "pending"
  | "sent"
  | "received"
  | "receivedWithErrors"
  | "canceled";

export const deliveryStatuses: Record<
  DeliveryStatus,
  { id: DeliveryStatus; name: string }
> = {
  pending: {
    id: "pending",
    name: "En cours",
  },
  sent: {
    id: "sent",
    name: "Envoyée",
  },
  received: {
    id: "received",
    name: "Reçue",
  },
  receivedWithErrors: {
    id: "receivedWithErrors",
    name: "Reçue avec erreurs",
  },
  canceled: {
    id: "canceled",
    name: "Annulée",
  },
};

export type DeliveryLot = Row & {
  lot: StockLot | null | undefined;
  product: Product | null | undefined;
  senderService: Service | null | undefined;
  receiverService: Service | null | undefined;
  delivery: Delivery | null | undefined;
  quantity: number;
  tva: number | null | undefined;
  stockAtThatPoint: number | null | undefined;
  lotStockAtThatPoint: number | null | undefined;
  maximumQuantity: number | null | undefined;
  brand: Brand | null | undefined;
  creator: User | null | undefined;
  hasBeenRemovedFromStock?: boolean;
};

//! ///////////////////////////////////////
//! ////////    STOCKS   ///////////////////
//! ///////////////////////////////////////

export type StockItem = Row & {
  product: Product | null | undefined;
  totalQuantity: number;
  totalInitialQuantity: number;
  lots: (StockLot | null | undefined)[];
  service: Service | null | undefined;
  structureUnit: StructureUnit | null | undefined;
};

export type StockItemExpiryStatus =
  | "all"
  | "expiresSoon"
  | "expired"
  | "notExpired";

export const stockItemExpiryStatuses: Record<
  StockItemExpiryStatus,
  { id: StockItemExpiryStatus; name: string }
> = {
  all: {
    id: "all",
    name: "Tous",
  },
  notExpired: {
    id: "notExpired",
    name: "Non expiré",
  },
  expiresSoon: {
    id: "expiresSoon",
    name: "Expire bientôt",
  },
  expired: {
    id: "expired",
    name: "Expiré",
  },
};

export type StockLot = Row & {
  service: Service | null | undefined;
  product: Product | null | undefined;
  brand?: Brand | null | undefined;
  number?: string | null | undefined;
  barcode?: string | null | undefined;
  quantity?: number | null | undefined;
  initialQuantity?: number | null | undefined;
  inventoryDate?: string | null | undefined;
  inventoryDeclaration?: InventoryDeclaration | null | undefined;
  expiryDate?: string | null | undefined;
  item: StockItem | null | undefined;
  location: Location | null | undefined;
  unitPrice?: number | null | undefined;
  tva?: number | null | undefined;
  notes?: string | null | undefined;
};

export type InventoryDeclaration = Row & {
  service: Service | null | undefined;
  product: Product | null | undefined;
  brand?: Brand | null | undefined;
  number?: string | null | undefined;
  barcode?: string | null | undefined;
  quantity?: number | null | undefined;
  initialQuantity?: number | null | undefined;
  expiryDate?: string | null | undefined;
  item: StockItem | null | undefined;
  location: Location | null | undefined;
  unitPrice?: number | null | undefined;
  notes?: string | null | undefined;
};

export type StockStatus =
  | "all"
  | "inStock"
  | "lowStock"
  // | "veryLowStock"
  | "outOfStock";

export const stockStatuses: Record<
  StockStatus,
  {
    id: StockStatus;
    name: string;
    color: string;
  }
> = {
  all: {
    id: "all",
    name: "Tous",
    color: "blue",
  },
  inStock: {
    id: "inStock",
    name: "En stock",
    color: "green",
  },
  lowStock: {
    id: "lowStock",
    name: "Stock bas",
    color: "orange",
  },
  // veryLowStock: {
  //   id: "veryLowStock",
  //   name: "Stock très faible",
  //   color: "orange",
  // },
  outOfStock: {
    id: "outOfStock",
    name: "Rupture de stock",
    color: "red",
  },
};

export const xPatientItem = defineSchema<PatientItem>({
  tableName: "samples",
  columns: {
    ...xRow.columns,

    patient: { type: "string", isForeignKeyOf: "patients" },
    readBy: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
    confidentiality: { type: "string", isForeignKeyOf: "confidentialities" },
  },
});

export const xSnomedConcept = defineSchema<SnomedConcept>({
  tableName: "concepts",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    synonyms: { type: "string" },
    nameLength: { type: "number" },
    language: { type: "string" },
    descriptions: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "descriptions",
    },
    sourceRelationships: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "relationships",
    },
    destinationRelationships: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "relationships",
    },
    parents: { type: "string", isArray: true, isForeignKeyOf: "concepts" },
    allParents: { type: "string", isArray: true, isForeignKeyOf: "concepts" },
    children: { type: "string", isArray: true, isForeignKeyOf: "concepts" },
    originalId: { type: "string" },
    count: { type: "number" },
  },
} as const);

export const xSnomedConcept_fts = defineSchema({
  tableName: "concepts_fts",
  columns: {
    id: {
      type: "string",
      isPrimaryKey: true,
    },
    synonyms: {
      type: "string",
    },
  },
} as const);

export const xSnomedDescription = defineSchema<SnomedDescription>({
  tableName: "descriptions",
  columns: {
    ...xRow.columns,
    concept: { type: "string", isForeignKeyOf: "concepts" },
    term: { type: "string" },
    languageCode: { type: "string" },
    type: { type: "string" },
    originalId: { type: "string" },
  },
} as const);

export const xSnomedRelationship = defineSchema<SnomedRelationship>({
  tableName: "relationships",
  columns: {
    ...xRow.columns,
    source: { type: "string", isForeignKeyOf: "concepts" },
    destination: { type: "string", isForeignKeyOf: "concepts" },
    type: { type: "string" },
    characteristicType: { type: "string" },
    relationshipGroupId: { type: "number" },
    originalId: { type: "string" },
  },
} as const);

export const xReference = defineSchema<Reference>({
  tableName: "references",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    snomedConcept: {
      type: "string",
      isForeignKeyOf: "concepts",
    },
    isSnomedValidated: { type: "boolean", isNullable: true },
    description: { type: "string", isNullable: true },
    externalId: { type: "string", isNullable: true },
    formulation: { type: "string", isNullable: true },
    category: { type: "string" },
    cim10Code: { type: "string", isNullable: true },
    cim11Code: { type: "string", isNullable: true },
    usageCount: { type: "number", isNullable: true },
    sections: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "fieldsSections",
    },
    isVisible: { type: "boolean", isNullable: true },
    isAddedAutomatically: { type: "boolean", isNullable: true },
    emergencyLevel: { type: "string", isNullable: true },
    children: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
  },
} as const);

export const xReferencesGroup = defineSchema<ReferencesGroup>({
  tableName: "referencesGroups",
  columns: {
    ...xRow.columns,
    usageCount: { type: "number" },
    name: { type: "string" },
    visibility: { type: "string" },
    type: { type: "string" },

    references: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
  },
} as const);

export const xFieldsSection = defineSchema<FieldsSection>({
  tableName: "fieldsSections",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    type: { type: "string" },

    isCommonWithSnomedChildren: { type: "boolean", isNullable: true },
    isExpandable: { type: "boolean", isNullable: true },
    fields: { type: "string", isArray: true, isForeignKeyOf: "fields" },
    tableColumns: { type: "string", isArray: true, isNullable: true },
    tableRows: { type: "string", isArray: true, isNullable: true },
    conditionGroupStr: { type: "string", isNullable: true },
  },
} as const);

export const xField = defineSchema<Field>({
  tableName: "fields",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    placeholder: { type: "string" },
    type: { type: "string" },
    description: { type: "string" },
    defaultValue: { type: "string" },
    isRequired: { type: "boolean", isNullable: true },
    source: { type: "string", isNullable: true },
    formulation: { type: "string", isNullable: true },
    oldIds: { type: "string", isArray: true, isNullable: true },
    options: { type: "string", isArray: true, isForeignKeyOf: "options" },
    unit: { type: "string", isForeignKeyOf: "fieldUnits", isNullable: true },
    min: { type: "number", isNullable: true },
    max: { type: "number", isNullable: true },
    conditionGroupStr: { type: "string", isNullable: true },
  },
} as const);

export const xFieldUnit = defineSchema<FieldUnit>({
  tableName: "fieldUnits",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
  },
} as const);

export const xFieldOption = defineSchema<FieldOption>({
  tableName: "options",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
    value: { type: "string" },
  },
} as const);

export const xHospital = defineSchema<Hospital>({
  tableName: "hospitals",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    address: { type: "string", isNullable: true },
    latitude: { type: "number", isNullable: true },
    longitude: { type: "number", isNullable: true },
    wilaya: { type: "string", isNullable: true },
    type: { type: "string" },
  },
} as const);

export const xService = defineSchema<Service>({
  tableName: "services",
  columns: {
    ...xRow.columns,

    name: { type: "string", isNullable: true },
    chef: { type: "string", isForeignKeyOf: "users", isNullable: true },
    hospital: { type: "string", isForeignKeyOf: "hospitals" },
    waitingScreens: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "waitingScreens",
    },
    appointmentTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },
    units: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "units",
    },
    url: { type: "string", isNullable: true },
    iframeUrl: { type: "string", isNullable: true },
    iframeErrorMessage: { type: "string", isNullable: true },
    type: { type: "string", isNullable: true },
    apiId: { type: "string", isNullable: true },
    theme: { type: "string", isNullable: true },
    color: { type: "string", isNullable: true },
    headerType: { type: "string", isNullable: true },
    headerImage1: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
    headerImage2: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
    backgroundImage: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
    logo: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
   
    logoOpacity: { type: "number", isNullable: true },
    logoHeight: { type: "number", isNullable: true },
    logoWidth: { type: "number", isNullable: true },
    logoPadding: { type: "number", isNullable: true },
    showHeaderBarcodeInsteadOfLogo: { type: "boolean", isNullable: true },
    backgroundImageOpacity: { type: "number", isNullable: true },
    backgroundImageHeight: { type: "number", isNullable: true },
    backgroundImageWidth: { type: "number", isNullable: true },
    footerType: { type: "string", isNullable: true },
    showAddressInFooter: { type: "boolean", isNullable: true },
    showEmailInFooter: { type: "boolean", isNullable: true },
    showPhoneNumberInFooter: { type: "boolean", isNullable: true },
    h1: { type: "string", isNullable: true },
    h2: { type: "string", isNullable: true },
    h3: { type: "string", isNullable: true },
    h4: { type: "string", isNullable: true },
    h5: { type: "string", isNullable: true },
    h6: { type: "string", isNullable: true },
    footerLogo: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
    showFooterBarcodeInsteadOfLogo: { type: "boolean", isNullable: true },
    footerLogoPadding: { type: "number", isNullable: true },
    footerLogoOpacity: { type: "number", isNullable: true },
    footerLogoHeight: { type: "number", isNullable: true },
    footerLogoWidth: { type: "number", isNullable: true },
    footerH1: { type: "string", isNullable: true },
    footerH2: { type: "string", isNullable: true },
    footerH3: { type: "string", isNullable: true },
    footerH4: { type: "string", isNullable: true },
    footerH5: { type: "string", isNullable: true },
    footerH6: { type: "string", isNullable: true },
    footerAddress: { type: "string", isNullable: true },
    footerEmail: { type: "string", isNullable: true },
    footerPhoneNumber1: { type: "string", isNullable: true },
    footerPhoneNumber2: { type: "string", isNullable: true },
    email: { type: "string", isNullable: true },
    address: { type: "string", isNullable: true },
    header: { type: "string", isNullable: true },
    leftBar: { type: "string" },
    phoneNumber1: { type: "string", isNullable: true },
    phoneNumber2: { type: "string", isNullable: true },
    phoneNumber3: { type: "string", isNullable: true },
  },
} as const);

export const xStructureUnit = defineSchema<StructureUnit>({
  tableName: "units",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    type: { type: "string", isNullable: true },
    chef: { type: "string", isForeignKeyOf: "users", isNullable: true },
    boxes: { type: "string", isArray: true, isForeignKeyOf: "boxes" },

    beds: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "beds",
    },

    generalCategories: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "productGeneralCategories",
    },

    documentTemplates: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "documentTemplates",
    },
  },
} as const);

export const xBox = defineSchema<Box>({
  tableName: "boxes",
  columns: {
    ...xRow.columns,

    name: { type: "string", isNullable: true },
    ticketPrefix: { type: "string", isNullable: true },
    unit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    appointmentTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },
    currentAppointment: {
      type: "string",
      isForeignKeyOf: "appointments",
    },
    appointments: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointments",
    },
    type: { type: "string", isNullable: true },
    isDisabled: { type: "boolean", isNullable: true },
  },
} as const);

export const xBed = defineSchema<Bed>({
  tableName: "beds",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    unit: { type: "string", isForeignKeyOf: "units" },
    type: { type: "string", isNullable: true },
    color: { type: "string", isNullable: true },
    actions: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "bedActions",
    },
  },
} as const);

export const xBedAction = defineSchema<BedAction>({
  tableName: "bedActions",
  columns: {
    ...xRow.columns,

    type: { type: "string" },
    bed: { type: "string", isForeignKeyOf: "beds" },
    status: { type: "string" },
    doneBy: { type: "string", isForeignKeyOf: "users", isNullable: true },
    doneDatetime: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
  },
} as const);
export const xWaitingScreen = defineSchema<WaitingScreen>({
  tableName: "waitingScreens",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    type: { type: "string", isNullable: true },
    format: { type: "string", isNullable: true },
    description: { type: "string" },
    quadrant1: { type: "string", isJson: true, isNullable: true },
    quadrant2: { type: "string", isJson: true, isNullable: true },
    quadrant3: { type: "string", isJson: true, isNullable: true },
    quadrant4: { type: "string", isJson: true, isNullable: true },
    quadrant5: { type: "string", isJson: true, isNullable: true },
    quadrant6: { type: "string", isJson: true, isNullable: true },
    boxes: { type: "string", isArray: true, isForeignKeyOf: "boxes" },
    leftCornerText: { type: "string", isNullable: true },
    leftCornerImage1: {
      type: "string",
      isForeignKeyOf: "uploads",
    },
    leftCornerImage2: { type: "string", isNullable: true },
    title: { type: "string", isNullable: true },
    rightCornerText: { type: "string", isNullable: true },
    rightCornerImage1: { type: "string", isNullable: true },
    rightCornerImage2: { type: "string", isNullable: true },
    inscructionsImage: { type: "string", isNullable: true },
    gridCount: { type: "number", isNullable: true },
    enableNextScreens: { type: "boolean", isNullable: true },
    nextScreens: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "waitingScreens",
    },
    nextScreenIntervalInSeconds: { type: "number" },
  },
} as const);

export const xPost = defineSchema<Post>({
  tableName: "posts",
  columns: {
    ...xRow.columns,
    content: { type: "string" },
    category: { type: "string" },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    comments: { type: "string", isArray: true, isForeignKeyOf: "comments" },
  },
} as const);

export const xFeedComment = defineSchema<FeedComment>({
  tableName: "comments",
  columns: {
    ...xRow.columns,
    content: { type: "string" },
    uploads: { type: "string", isArray: true, isForeignKeyOf: "uploads" },
    post: { type: "string", isForeignKeyOf: "posts", isNullable: true },
  },
} as const);

export const xMessage = defineSchema<Message>({
  tableName: "messages",
  columns: {
    ...xRow.columns,
    content: { type: "string" },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    item: { type: "string" },
    discussion: {
      type: "string",
      isForeignKeyOf: "discussions",
    },
    readBy: { type: "string", isArray: true, isForeignKeyOf: "users" },
  },
} as const);

export const xDiscussion = defineSchema<Discussion>({
  tableName: "discussions",
  columns: {
    ...xRow.columns,
    name: { type: "string", isNullable: true },
    users: { type: "string", isArray: true, isForeignKeyOf: "users" },
    lastMessage: {
      type: "string",
      isForeignKeyOf: "messages",
    },
    type: { type: "string" },
    status: { type: "string" },
  },
} as const);

export const xRhEvent = defineSchema<RhEvent>({
  tableName: "rhEvents",
  columns: {
    ...xRow.columns,
    start: { type: "string", isNullable: true },
    end: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    type: { type: "string", isNullable: true },
    unit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    user: { type: "string", isForeignKeyOf: "users", isNullable: true },
  },
} as const);

export const xRemedConfig = defineSchema<RemedConfig>({
  tableName: "remedConfigs",
  columns: {
    ...xRow.columns,
    version: { type: "string", isNullable: true },
    hospital: {
      type: "string",
      isForeignKeyOf: "hospitals",
      isNullable: true,
    },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    radiotherapyForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    chemotherapyForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    surgeryForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    ventilationForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    anesthesiologyConsultationForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    anesthesiologySummaryForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    complicationForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
    weight: {
      type: "string",
      isForeignKeyOf: "references",
      isNullable: true,
    },
    height: {
      type: "string",
      isForeignKeyOf: "references",
      isNullable: true,
    },
    bmi: { type: "string", isForeignKeyOf: "references", isNullable: true },
    surfaceArea: {
      type: "string",
      isForeignKeyOf: "references",
    },
    creatinineMdrd: {
      type: "string",
      isForeignKeyOf: "references",
    },
    creatinineCockcroft: {
      type: "string",
      isForeignKeyOf: "references",
    },
    anapathExam: {
      type: "string",
      isForeignKeyOf: "references",
    },
    consultationType: {
      type: "string",
      isForeignKeyOf: "appointmentTypes",
    },
    chemotherapyType: {
      type: "string",
      isForeignKeyOf: "appointmentTypes",
    },
  },
} as const);

export const xStatisticsReport = defineSchema<StatisticsReport>({
  tableName: "statisticsReports",
  columns: {
    ...xRow.columns,
    title: { type: "string", isNullable: true },
    males: { type: "number" },
    females: { type: "number" },
    patientGroups: { type: "string" },
    patientsDates: { type: "string" },
    appointmentsDates: { type: "string" },
    appointments: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointments",
    },
    groups: { type: "string", isArray: true },
    type: { type: "string" },
    patients: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "patients",
    },
  },
} as const);

export const xECG = defineSchema<ECG>({
  tableName: "ecgs",
  columns: {
    ...xRow.columns,
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    notes: { type: "string" },
    uploads: { type: "string", isArray: true, isForeignKeyOf: "uploads" },
    antecedents: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    interpretations: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ecgInterpretations",
    },
    mainUpload: { type: "string", isForeignKeyOf: "uploads", isNullable: true },
  },
} as const);

export const xEcgInterpretation = defineSchema<EcgInterpretation>({
  tableName: "ecgInterpretations",
  columns: {
    ...xRow.columns,

    content: { type: "string" },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xHospitalisation = defineSchema<Hospitalisation>({
  tableName: "hospitalisations",
  columns: {
    ...xPatientItem.columns,
    sourceStructure: { type: "string", isNullable: true },

    type: { type: "string" },
    bed: { type: "string", isForeignKeyOf: "beds", isNullable: true },
    source: { type: "string", isNullable: true },
    sourceUnit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    status: { type: "string" },
    sourceWilaya: { type: "string", isNullable: true },
    sourceHospitalName: { type: "string", isNullable: true },
    motives: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    diagnostics: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    discharge: {
      type: "string",
      isForeignKeyOf: "discharges",
    },
    temporaryLeave: {
      type: "string",
      isForeignKeyOf: "temporaryLeaves",
    },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    admissionNumber: { type: "string", isNullable: true },
    entryMode: { type: "string", isNullable: true },
    decisionDoctor: {
      type: "string",
      isForeignKeyOf: "users",
    },
  },
} as const);

export const xConsultation = defineSchema<Consultation>({
  tableName: "consultations",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    motives: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    diagnostics: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    clinicalExam: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    appointment: {
      type: "string",
      isForeignKeyOf: "appointments",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    aiAudioSessions: {
      type: "string",
      isArray: true,
      isJson: true,
    },
  },
} as const);

export const xInitialExamination = defineSchema<InitialExamination>({
  tableName: "initialExaminations",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xAdmission = defineSchema<Admission>({
  tableName: "admissions",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xBillet = defineSchema<Billet>({
  tableName: "billets",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    patientDocument: {
      type: "string",
      isForeignKeyOf: "patientDocuments",
    },
  },
} as const);

export const xEvolution = defineSchema<Evolution>({
  tableName: "evolutions",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xShift = defineSchema<Shift>({
  tableName: "shifts",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    functionalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    physicalSigns: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xIntervention = defineSchema<Intervention>({
  tableName: "interventions",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xAvis = defineSchema<Avis>({
  tableName: "avis",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    doctor: { type: "string", isForeignKeyOf: "users", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    demandNotes: { type: "string", isNullable: true },
    demandUploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    isDemand: { type: "boolean", isNullable: true },
  },
} as const);

export const xVentilation = defineSchema<Ventilation>({
  tableName: "ventilations",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    content: {
      type: "string",
      isForeignKeyOf: "refAnswers",
      isNullable: true,
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xAnesthesiologyConsultation =
  defineSchema<AnesthesiologyConsultation>({
    tableName: "anesthesiologyConsultations",
    columns: {
      ...xPatientItem.columns,

      type: { type: "string" },
      status: { type: "string", isNullable: true },
      reference: {
        type: "string",
        isForeignKeyOf: "references",
      },
      refAnswer: {
        type: "string",
        isForeignKeyOf: "refAnswers",
      },
      parent: {
        type: "string",
        isForeignKeyOf: "hospitalisations",
      },
      notes: { type: "string", isNullable: true },
      conclusion: { type: "string", isNullable: true },
      uploads: {
        type: "string",
        isArray: true,
        isForeignKeyOf: "uploads",
      },
    },
  } as const);
export const xAnesthesiologySummary = defineSchema<AnesthesiologySummary>({
  tableName: "anesthesiologySummaries",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xComplication = defineSchema<Complication>({
  tableName: "complications",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xConsigne = defineSchema<Consigne>({
  tableName: "consignes",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xStory = defineSchema<Story>({
  tableName: "stories",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xSample = defineSchema<Sample>({
  tableName: "samples",
  columns: {
    ...xPatientItem.columns,
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },

    type: { type: "string" },
    externalDemandId: { type: "string", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    status: { type: "string", isNullable: true },
    barcode: { type: "string", isNullable: true },
    number: { type: "number", isNullable: true },
    year: { type: "number", isNullable: true },
    unit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    numberOfPieces: { type: "number", isNullable: true },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    targetService: {
      type: "string",
      isForeignKeyOf: "services",
    },
    conclusion: { type: "string", isNullable: true },
    exams: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "exams",
    },
    steps: { type: "string", isArray: true, isForeignKeyOf: "examSteps" },
    slices: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "sampleSlices",
    },
    locationCategory: { type: "string", isNullable: true },
    location: { type: "string", isForeignKeyOf: "locations", isNullable: true },
  },
} as const);

export const xSampleSlice = defineSchema<SampleSlice>({
  tableName: "sampleSlices",
  columns: {
    ...xRow.columns,

    barcode: { type: "string", isNullable: true },
    rootBarcode: { type: "string", isNullable: true },
    sampleNumber: { type: "number" },
    sample: { type: "string", isForeignKeyOf: "samples", isNullable: true },
    notes: { type: "string", isNullable: true },
    status: { type: "string", isNullable: true },
    location: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xDiagnostic = defineSchema<Diagnostic>({
  tableName: "diagnostics",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    exams: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    metastasis: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    story: { type: "string", isForeignKeyOf: "stories", isNullable: true },
  },
} as const);

export const xRadiotherapy = defineSchema<Radiotherapy>({
  tableName: "radiotherapies",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    status: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xSurgery = defineSchema<Surgery>({
  tableName: "surgeries",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    status: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
  },
} as const);

export const xMetastasis = defineSchema<Metastasis>({
  tableName: "metastasis",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    status: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xToxicity = defineSchema<Toxicity>({
  tableName: "toxicities",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    status: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xExam = defineSchema<Exam>({
  tableName: "exams",
  columns: {
    ...xPatientItem.columns,
    examGroup: {
      type: "string",
      isForeignKeyOf: "examGroups",
    },
    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    document: {
      type: "string",
      isForeignKeyOf: "patientDocuments",
    },
    status: { type: "string" },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    samples: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "samples",
    },
  },
} as const);

export const xExamGroup = defineSchema<ExamGroup>({
  tableName: "examGroups",
  columns: {
    ...xRow.columns,
    type: { type: "string" },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    patient: { type: "string", isForeignKeyOf: "patients" },
    referencesGroup: { type: "string", isForeignKeyOf: "referencesGroups" },
  },
} as const);

export const xExamStepsGroup = defineSchema<ExamStepsGroup>({
  tableName: "examStepsGroups",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    steps: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
  },
} as const);

export const xExamStep = defineSchema<ExamStep>({
  tableName: "examSteps",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    status: { type: "string", isNullable: true },
    parent: { type: "string", isForeignKeyOf: "samples", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    slices: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "sampleSlices",
    },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    doneBy: { type: "string", isForeignKeyOf: "users", isNullable: true },
    affectedTo: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
  },
} as const);

export const xExamDemand = defineSchema<ExamDemand>({
  tableName: "examDemands",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    reference: { type: "string", isForeignKeyOf: "references" },
    content: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    status: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xMesureSession = defineSchema<MesureSession>({
  tableName: "mesureSessions",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    mesures: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xNotification = defineSchema<Notification>({
  tableName: "notifications",
  columns: {
    ...xRow.columns,

    type: { type: "string" },
    status: { type: "string" },
    item: { type: "string" },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    targetUsers: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
    readBy: { type: "string", isArray: true, isForeignKeyOf: "users" },
  },
} as const);

export const xItemSubscription = defineSchema<ItemSubscription>({
  tableName: "itemSubscriptions",
  columns: {
    ...xRow.columns,

    item: { type: "string" },
    user: { type: "string", isForeignKeyOf: "users", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
  },
} as const);

export const xDischarge = defineSchema<Discharge>({
  tableName: "discharges",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    dischargeType: { type: "string" },
    exitMode: { type: "string" },
    exitDate: { type: "string", isNullable: true },
    status: { type: "string" },
    notes: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    conclusion: { type: "string", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xTemporaryLeave = defineSchema<TemporaryLeave>({
  tableName: "temporaryLeaves",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    exitDate: { type: "string", isNullable: true },
    returnDate: { type: "string", isNullable: true },
    status: { type: "string" },
    notes: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    conclusion: { type: "string", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
  },
} as const);

export const xTransferDemand = defineSchema<TransferDemand>({
  tableName: "transferDemands",
  columns: {
    ...xPatientItem.columns,

    type: { type: "string" },
    exitDate: { type: "string", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    bed: { type: "string", isForeignKeyOf: "beds", isNullable: true },
    unit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    status: { type: "string" },
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    acceptedBy: { type: "string", isForeignKeyOf: "users", isNullable: true },
  },
} as const);

export const xProduct = defineSchema<Product>({
  tableName: "products",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    code: { type: "string" },
    ref: { type: "string", isNullable: true },
    isClone: { type: "boolean", isNullable: true },
    barcode: { type: "string" },
    condition: {
      type: "string",
      isForeignKeyOf: "productConditions",
    },
    conditionStr: { type: "string", isNullable: true },

    measureUnit: {
      type: "string",
      isForeignKeyOf: "fieldUnits",
    },
    suppliers: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "suppliers",
    },
    brand: { type: "string", isForeignKeyOf: "brands", isNullable: true },
    image: { type: "string", isForeignKeyOf: "uploads", isNullable: true },
    description: { type: "string" },
    price: { type: "number" },
    currency: { type: "string" },
    generalCategory: {
      type: "string",
      isForeignKeyOf: "productGeneralCategories",
    },
    category: {
      type: "string",
      isForeignKeyOf: "productCategories",
    },

    tva: { type: "number", isNullable: true },
    usageCount: { type: "number", isNullable: true },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    clones: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "products",
    },
  },
} as const);

export const xBrand = defineSchema<Brand>({
  tableName: "brands",
  columns: {
    ...xRow.columns,
    image: { type: "string", isForeignKeyOf: "uploads", isNullable: true },
    name: { type: "string" },
    description: { type: "string" },
  },
} as const);

export const xProductCondition = defineSchema<ProductCondition>({
  tableName: "productConditions",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
  },
} as const);

export const xProductGeneralCategory = defineSchema<ProductGeneralCategory>({
  tableName: "productGeneralCategories",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    description: { type: "string" },
    code: { type: "string" },
  },
} as const);

export const xProductCategory = defineSchema<ProductCategory>({
  tableName: "productCategories",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    code: { type: "string" },
    description: { type: "string" },
    parent: {
      type: "string",
      isForeignKeyOf: "productGeneralCategories",
    },
    status: { type: "string" },
  },
} as const);

export const xSupplier = defineSchema<Supplier>({
  tableName: "suppliers",
  columns: {
    ...xRow.columns,

    image: { type: "string", isForeignKeyOf: "uploads", isNullable: true },
    type: { type: "string" },
    name: { type: "string" },
    description: { type: "string" },
    contacts: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "supplierContacts",
    },
    address: { type: "string" },
    phone: { type: "string" },
    fax: { type: "string" },
    email: { type: "string" },
    website: { type: "string" },
    RC: { type: "string" },
    NIF: { type: "string" },
    NIS: { type: "string" },
    AI: { type: "string" },
    RIB: { type: "string" },
    agreementNumber: { type: "string" },
  },
} as const);

export const xSupplierContact = defineSchema<SupplierContact>({
  tableName: "supplierContacts",
  columns: {
    ...xRow.columns,

    firstName: { type: "string" },
    lastName: { type: "string" },
    phone: { type: "string" },
    email: { type: "string" },
    position: { type: "string" },
  },
} as const);

export const xGeneralLocation = defineSchema<GeneralLocation>({
  tableName: "generalLocations",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
  },
} as const);

export const xLocation = defineSchema<Location>({
  tableName: "locations",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    type: { type: "string" },
    description: { type: "string" },
    parent: {
      type: "string",
      isForeignKeyOf: "generalLocations",
    },
  },
} as const);

export const xLocationChange = defineSchema<LocationChange>({
  tableName: "locationChanges",
  columns: {
    ...xRow.columns,
    stockLot: { type: "string", isForeignKeyOf: "stockLots", isNullable: true },
    oldLocation: {
      type: "string",
      isForeignKeyOf: "locations",
    },
    newLocation: {
      type: "string",
      isForeignKeyOf: "locations",
    },
  },
} as const);

export const xTender = defineSchema<Tender>({
  tableName: "tenders",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    code: { type: "string" },
    description: { type: "string" },
    supplier: { type: "string", isForeignKeyOf: "suppliers", isNullable: true },
    startDate: { type: "string" },
    endDate: { type: "string" },
    status: { type: "string" },
    totalValue: { type: "number" },
    currency: { type: "string" },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    year: { type: "number", isNullable: true },
    amourTenderType: { type: "string", isNullable: true },
    lots: { type: "string", isArray: true, isForeignKeyOf: "tenderLots" },
  },
} as const);

export const xTenderLot = defineSchema<TenderLot>({
  tableName: "tenderLots",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    code: { type: "string" },
    year: { type: "string", isNullable: true },
    imputation: {
      type: "string",
      isForeignKeyOf: "imputations",
    },
    tender: { type: "string", isForeignKeyOf: "tenders", isNullable: true },
    description: { type: "string" },
    status: { type: "string" },
    supplier: { type: "string", isForeignKeyOf: "suppliers", isNullable: true },
    currency: { type: "string" },
    totalValue: { type: "number" },
    amourTenderType: { type: "string", isNullable: true },
    items: { type: "string", isArray: true, isForeignKeyOf: "tenderItems" },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
  },
} as const);

export const xTenderItem = defineSchema<TenderItem>({
  tableName: "tenderItems",
  columns: {
    ...xRow.columns,

    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    tenderLot: { type: "string" },
    quantity: { type: "number" },
    minimumQuantity: { type: "number" },
    maximumQuantity: { type: "number" },
    forcastedQuantity: { type: "number" },
    commandedQuantity: { type: "number" },
    deliveredQuantity: { type: "number" },
    unitPrice: { type: "number" },
    totalPrice: { type: "number" },
    tva: { type: "number" },
  },
} as const);

export const xCommand = defineSchema<Command>({
  tableName: "commands",
  columns: {
    ...xRow.columns,
    commandorStructureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    code: { type: "string" },
    type: { type: "string" },
    sourceCommand: {
      type: "string",
      isForeignKeyOf: "commands",
    },
    tenderLot: {
      type: "string",
      isForeignKeyOf: "tenderLots",
    },
    status: { type: "string" },
    imputation: {
      type: "string",
      isForeignKeyOf: "imputations",
    },
    supplier: { type: "string", isForeignKeyOf: "suppliers", isNullable: true },
    commandedFromService: {
      type: "string",
      isForeignKeyOf: "services",
    },
    commandorService: {
      type: "string",
      isForeignKeyOf: "services",
    },
    automaticVentilation: {
      type: "string",
      isForeignKeyOf: "services",
    },
    commandedItems: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "commandItems",
    },
    deliveries: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "deliveries",
    },
    totalPrice: { type: "number" },
    ordonnances: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ordonnances",
    },
    patients: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "patients",
    },
    notes: { type: "string", isNullable: true },
    number: { type: "number", isNullable: true },
    year: { type: "string", isNullable: true },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
  },
} as const);

export const xImputation = defineSchema<Imputation>({
  tableName: "imputations",
  columns: {
    ...xRow.columns,

    name: { type: "string" },
    description: { type: "string" },
    budget: { type: "number", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
  },
} as const);

export const xCommandItem = defineSchema<CommandItem>({
  tableName: "commandItems",
  columns: {
    ...xRow.columns,

    tenderItem: {
      type: "string",
      isForeignKeyOf: "tenderItems",
    },
    command: { type: "string", isForeignKeyOf: "commands", isNullable: true },
    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    demandedQuantity: { type: "number" },

    receivedQuantity: { type: "number" },
    acceptedQuantity: { type: "number" },
    maximumQuantity: { type: "number", isNullable: true },
    unitPrice: { type: "number" },
    tva: { type: "number", isNullable: true },
    totalPrice: { type: "number" },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
  },
} as const);

export const xInvoice = defineSchema<Invoice>({
  tableName: "invoices",
  columns: {
    ...xRow.columns,

    command: { type: "string", isForeignKeyOf: "commands", isNullable: true },
    supplier: { type: "string", isForeignKeyOf: "suppliers", isNullable: true },
    invoiceNumber: { type: "string", isNullable: true },
    invoiceDate: { type: "string", isNullable: true },
    invoiceTVAPercentage: { type: "number", isNullable: true },
    totalPrice: { type: "number", isNullable: true },
    totalPriceHT: { type: "number", isNullable: true },
    items: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "invoiceItems",
    },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
  },
} as const);

export const xInvoiceItem = defineSchema<InvoiceItem>({
  tableName: "invoiceItems",
  columns: {
    ...xRow.columns,

    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    quantity: { type: "number", isNullable: true },
    unitPrice: { type: "number" },
    tva: { type: "number", isNullable: true },
    totalPrice: { type: "number" },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    commandItem: {
      type: "string",
      isForeignKeyOf: "commandItems",
    },
  },
} as const);

export const xDelivery = defineSchema<Delivery>({
  tableName: "deliveries",
  columns: {
    ...xRow.columns,
    hasBeenVentilated: { type: "boolean", isNullable: true },
    status: { type: "string" },
    type: { type: "string" },
    automaticVentilation: {
      type: "string",
      isForeignKeyOf: "services",
    },
    command: { type: "string", isForeignKeyOf: "commands", isNullable: true },
    sentLots: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "deliveryLots",
    },
    receivedLots: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "deliveryLots",
    },
    releaseNumber: { type: "string", isNullable: true },
    deliveryNumber: { type: "string", isNullable: true },
    bemNumber: { type: "number", isNullable: true },
    bemDate: { type: "string", isNullable: true },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
    invoice: { type: "string", isForeignKeyOf: "invoices", isNullable: true },
    sender: { type: "string", isForeignKeyOf: "services", isNullable: true },
    receiver: { type: "string", isForeignKeyOf: "services", isNullable: true },

    deliveredQuantity: { type: "number", isNullable: true },
    deliveryDate: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    supplier: { type: "string", isForeignKeyOf: "suppliers", isNullable: true },
  },
} as const);

export const xDeliveryLot = defineSchema<DeliveryLot>({
  tableName: "deliveryLots",
  columns: {
    ...xRow.columns,
    hasBeenRemovedFromStock: { type: "boolean" },
    tva: { type: "number" },
    stockAtThatPoint: { type: "number" },
    lotStockAtThatPoint: { type: "number" },

    lot: { type: "string", isForeignKeyOf: "stockLots", isNullable: true },
    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    senderService: {
      type: "string",
      isForeignKeyOf: "services",
    },

    receiverService: {
      type: "string",
      isForeignKeyOf: "services",
    },
    delivery: {
      type: "string",
      isForeignKeyOf: "deliveries",
    },
    quantity: { type: "number" },
    maximumQuantity: { type: "number", isNullable: true },
    brand: { type: "string", isForeignKeyOf: "brands", isNullable: true },
    creator: { type: "string", isForeignKeyOf: "users", isNullable: true },
  },
} as const);

export const xStockItem = defineSchema<StockItem>({
  tableName: "stockItems",
  columns: {
    ...xRow.columns,

    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    totalQuantity: { type: "number" },
    totalInitialQuantity: { type: "number" },
    lots: { type: "string", isArray: true, isForeignKeyOf: "stockLots" },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    structureUnit: {
      type: "string",
      isForeignKeyOf: "units",
    },
  },
} as const);

export const xStockLot = defineSchema<StockLot>({
  tableName: "stockLots",
  columns: {
    ...xRow.columns,
    inventoryDeclaration: {
      type: "string",
      isForeignKeyOf: "inventoryDeclarations",
      isNullable: true,
    },
    tva: { type: "number", isNullable: true },
    notes: { type: "string", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    brand: { type: "string", isForeignKeyOf: "brands", isNullable: true },
    inventoryDate: { type: "string", isNullable: true },
    number: { type: "string", isNullable: true },
    barcode: { type: "string", isNullable: true },
    quantity: { type: "number", isNullable: true },
    initialQuantity: { type: "number", isNullable: true },
    expiryDate: { type: "string", isNullable: true },
    item: { type: "string", isForeignKeyOf: "stockItems", isNullable: true },
    location: { type: "string", isForeignKeyOf: "locations", isNullable: true },
    unitPrice: { type: "number", isNullable: true },
  },
} as const);

export const xInventoryDeclaration = defineSchema<InventoryDeclaration>({
  tableName: "inventoryDeclarations",
  columns: {
    ...xRow.columns,
    brand: { type: "string", isForeignKeyOf: "brands", isNullable: true },
    unitPrice: { type: "number", isNullable: true },
    notes: { type: "string", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    product: { type: "string", isForeignKeyOf: "products", isNullable: true },
    number: { type: "string", isNullable: true },
    barcode: { type: "string", isNullable: true },
    quantity: { type: "number", isNullable: true },
    initialQuantity: { type: "number", isNullable: true },
    expiryDate: { type: "string", isNullable: true },
    item: { type: "string", isForeignKeyOf: "stockItems", isNullable: true },
    location: { type: "string", isForeignKeyOf: "locations", isNullable: true },
  },
} as const);

export const xTicketNumber = defineSchema<TicketNumber>({
  tableName: "ticketNumbers",
  columns: {
    ...xRow.columns,

    number: { type: "number" },
    type: { type: "string" },
    prefix: { type: "string" },
    emergencyLevel: { type: "string" },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    unit: { type: "string", isForeignKeyOf: "units", isNullable: true },
    box: { type: "string", isForeignKeyOf: "boxes", isNullable: true },
    notes: { type: "string", isNullable: true },
  },
} as const);

export const xOrdonnance = defineSchema<Ordonnance>({
  tableName: "ordonnances",
  columns: {
    ...xRow.columns,

    category: { type: "string" },
    patient: { type: "string", isForeignKeyOf: "patients" },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    template: {
      type: "string",
      isForeignKeyOf: "ordonnanceTemplates",
    },
    weight: { type: "number", isNullable: true },
    height: { type: "number", isNullable: true },
    bmi: { type: "number", isNullable: true },
    surface: { type: "number", isNullable: true },
    ordonnanceMedicines: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ordonnanceMedicines",
    },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
  },
} as const);

export const xOrdonnanceMedicine = defineSchema<OrdonnanceMedicine>({
  tableName: "ordonnanceMedicines",
  columns: {
    ...xRow.columns,
    duration: { type: "string", isNullable: true },
    notes: { type: "string" },
    startDate: { type: "string", isNullable: true },
    endDate: { type: "string", isNullable: true },
    medicine: { type: "string", isForeignKeyOf: "medicines" },
    intakes: { type: "string", isArray: true, isForeignKeyOf: "intakes" },
    tasks: { type: "string", isArray: true, isForeignKeyOf: "tasks" },
    ordonnance: {
      type: "string",
      isForeignKeyOf: "ordonnances",
    },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
  },
} as const);

export const xOrdonnanceTemplate = defineSchema<OrdonnanceTemplate>({
  tableName: "ordonnanceTemplates",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    ordonnanceMedicines: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ordonnanceMedicines",
    },
    category: { type: "string" },
  },
} as const);

export const xMedicine = defineSchema<Medicine>({
  tableName: "medicines",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    dci: { type: "string", isNullable: true },
    dosage: { type: "string", isNullable: true },
    form: { type: "string", isNullable: true },
    defaultUnit: { type: "string" },
    defaultRoute: { type: "string", isNullable: true },
    usageCount: { type: "number", isNullable: true },
  },
} as const);

export const xIntake = defineSchema<Intake>({
  tableName: "intakes",
  columns: {
    ...xRow.columns,
    posology: { type: "string" },
    duration: { type: "number" },
    notes: { type: "string", isNullable: true },
    solution: { type: "string", isNullable: true },
    dilutionVolume: { type: "number", isNullable: true },
    horaire: { type: "string" },
    unit: { type: "string" },
    route: { type: "string" },
  },
} as const);

export const xTask = defineSchema<Task>({
  tableName: "tasks",
  columns: {
    ...xRow.columns,
    start: { type: "string", isNullable: true },
    end: { type: "string", isNullable: true },
    finishedDate: { type: "string", isNullable: true },
    cancelDate: { type: "string", isNullable: true },
    status: { type: "string", isNullable: true },
    medicine: { type: "string", isForeignKeyOf: "medicines", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    doctor: { type: "string", isForeignKeyOf: "users", isNullable: true },
    creator: { type: "string", isForeignKeyOf: "users", isNullable: true },
    posology: { type: "string" },
    dilutionVolume: { type: "number", isNullable: true },
    dilutionSolution: { type: "string", isNullable: true },
    duration: { type: "number", isNullable: true },
    notes: { type: "string", isNullable: true },
    unit: { type: "string" },
    route: { type: "string" },
  },
} as const);

export const xRefAnswer = defineSchema<RefAnswer>({
  tableName: "refAnswers",
  columns: {
    ...xRow.columns,
    notes: { type: "string", isNullable: true },
    conclusion: { type: "string", isNullable: true },
    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    status: { type: "string", isNullable: true },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
    patient: { type: "string", isForeignKeyOf: "patients" },
    fieldAnswers: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "fieldAnswers",
    },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
    isAbsent: { type: "boolean", isNullable: true },
  },
} as const);

export const xPatientScore = defineSchema<PatientScore>({
  tableName: "patientScores",
  columns: {
    ...xRow.columns,
    score: { type: "string", isForeignKeyOf: "scores" },
    answers: { type: "string", isArray: true, isForeignKeyOf: "fieldAnswers" },
    interval: {
      type: "string",
      isForeignKeyOf: "scoreIntervals",
    },
    points: { type: "number" },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
  },
} as const);

export const xFieldSection = xFieldsSection;

export const xFieldType = defineSchema<any>({
  tableName: "fieldTypes",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
  },
} as const);

export const xFieldAnswer = defineSchema<FieldAnswer>({
  tableName: "fieldAnswers",
  columns: {
    ...xRow.columns,
    value: { type: "string" },
    field: { type: "string", isForeignKeyOf: "fields", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "refAnswers",
      isNullable: true,
    },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    cancelDate: { type: "string", isNullable: true },
    start: { type: "string", isNullable: true },
    end: { type: "string", isNullable: true },
    finishedDate: { type: "string", isNullable: true },
    status: { type: "string", isNullable: true },
    doctor: { type: "string", isForeignKeyOf: "users", isNullable: true },
    creator: { type: "string", isForeignKeyOf: "users", isNullable: true },
    notes: { type: "string", isNullable: true },
  },
} as const);

export const xOption = xFieldOption;

export const xReferenceGroup = xReferencesGroup;

export const xScore = defineSchema<Score>({
  tableName: "scores",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
    formula: { type: "string" },
    formulaType: { type: "string" },
    sections: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "fieldsSections",
    },
    fieldsIntervals: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "scoreIntervals",
    },
    intervals: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "scoreIntervals",
    },
  },
} as const);

export const xScoreInterval = defineSchema<ScoreInterval>({
  tableName: "scoreIntervals",
  columns: {
    ...xRow.columns,
    value: { type: "string" },
    min: { type: "number" },
    max: { type: "number" },
    type: { type: "string" },
    points: { type: "number" },
    interpretation: { type: "string" },
    color: { type: "string" },
    field: { type: "string", isForeignKeyOf: "fields", isNullable: true },
  },
} as const);

export const xDocumentTemplate = defineSchema<DocumentTemplate>({
  tableName: "documentTemplates",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    title: { type: "string" },
    type: { type: "string", isNullable: true },
    content: { type: "string" },
    showHeader: { type: "boolean" },
    showLeftBar: { type: "boolean" },
    barcodeLocation: { type: "string", isNullable: true },
    showTitle: { type: "boolean" },
    showPatientIdentity: { type: "boolean" },
    format: { type: "string" },
    registryName: { type: "string", isNullable: true },
    isValidatable: { type: "boolean", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "documentTemplates",
    },
    reference: {
      type: "string",
      isForeignKeyOf: "references",
    },
  },
} as const);

export const xDocumentVariable = defineSchema<DocumentVariable>({
  tableName: "documentVariables",
  columns: {
    ...xRow.columns,
    id: { type: "string" },
    name: { type: "string" },
    value: { type: "string", isNullable: true },
    category: { type: "string" },
    source: { type: "string" },
    format: { type: "string" },
    fieldId: { type: "string", isNullable: true },
    sectionId: { type: "string", isNullable: true },
    nullValue: { type: "string", isNullable: true },
    statisticsReport: {
      type: "string",
      isForeignKeyOf: "statisticsReports",
    },
    conditionGroup: {
      type: "string",
      isJson: true,
    },
    conditionReplacement: { type: "string", isNullable: true },
    conditionElseReplacement: { type: "string", isNullable: true },
    creator: { type: "string", isNullable: true },
    _date: { type: "string", isNullable: true },
  },
} as const);

export const xPatientDocument = defineSchema<PatientDocument>({
  tableName: "patientDocuments",
  columns: {
    ...xRow.columns,
    content: { type: "string" },
    title: { type: "string", isNullable: true },
    registryNumber: { type: "number", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients" },
    parent: {
      type: "string",
      isForeignKeyOf: "hospitalisations",
    },
    template: {
      type: "string",
      isForeignKeyOf: "documentTemplates",
    },
    refAnswer: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    status: { type: "string" },
    isValidatedBy: {
      type: "string",
      isForeignKeyOf: "users",
    },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
    initialCreator: {
      type: "string",
      isForeignKeyOf: "users",
    },
  },
} as const);

export const xPrintingDemand = defineSchema<PrintingDemand>({
  tableName: "printingDemands",
  columns: {
    ...xRow.columns,
    printDate: { type: "string", isNullable: true },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    patientDocument: {
      type: "string",
      isForeignKeyOf: "patientDocuments",
    },
    status: { type: "string" },
  },
} as const);

export const xAppointment = defineSchema<Appointment>({
  tableName: "appointments",
  columns: {
    ...xRow.columns,
    start: { type: "string", isNullable: true },
    end: { type: "string", isNullable: true },
    anchorDate: { type: "string", isNullable: true },
    arrivedDate: { type: "string", isNullable: true },
    ticketNumber: {
      type: "string",
      isForeignKeyOf: "ticketNumbers",
    },
    demandService: {
      type: "string",
      isForeignKeyOf: "services",
    },
    service: { type: "string", isForeignKeyOf: "services", isNullable: true },
    isHospitalized: { type: "boolean", isNullable: true },
    isUrgent: { type: "boolean", isNullable: true },
    notes: { type: "string", isNullable: true },
    status: { type: "string" },
    doctor: { type: "string", isForeignKeyOf: "users", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    type: {
      type: "string",
      isForeignKeyOf: "appointmentTypes",
    },
    item: { type: "string" },
    demandContent: {
      type: "string",
      isForeignKeyOf: "refAnswers",
    },
    emergencyLevel: { type: "string", isNullable: true },

    uploads: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "uploads",
    },
    protocolType: { type: "string", isNullable: true },
    protocol: {
      type: "string",
      isForeignKeyOf: "protocols",
      isNullable: true,
    },
    protocolCycle: { type: "number", isNullable: true },
    protocolDay: {
      type: "string",
      isForeignKeyOf: "protocolDays",
    },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
    externalId: { type: "string", isNullable: true },
    preparations: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    refusalNotes: { type: "string", isNullable: true },
    refusalCategory: { type: "string", isNullable: true },
    readBy: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "users",
    },
    initialMotives: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "refAnswers",
    },
  },
} as const);

export const xAppointmentType = defineSchema<AppointmentType>({
  tableName: "appointmentTypes",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    duration: { type: "number" },
    price: { type: "number" },
    color: { type: "string" },
    description: { type: "string", isNullable: true },
    itemType: { type: "string" },
    needsValidation: { type: "boolean", isNullable: true },
    exam: { type: "string", isForeignKeyOf: "references", isNullable: true },
    externalId: { type: "string", isNullable: true },
    preparations: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "references",
    },
    demandForm: {
      type: "string",
      isForeignKeyOf: "references",
    },
  },
} as const);

export const xPatient = defineSchema<Patient>({
  tableName: "patients",
  columns: {
    ...xRow.columns,
    shortId: { type: "string", isNullable: true },
    birthHour: { type: "string", isNullable: true },
    notes: { type: "string", isNullable: true },
    minAge: { type: "string", isNullable: true },
    maxAge: { type: "string", isNullable: true },

    recordId: { type: "string", isNullable: true },
    recordCreationDate: { type: "string", isNullable: true },
    firstName: { type: "string", isNullable: true },
    firstNameAr: { type: "string", isNullable: true },
    lastName: { type: "string", isNullable: true },
    lastNameAr: { type: "string", isNullable: true },
    maidenName: { type: "string", isNullable: true },
    searchInfo: { type: "string", isNullable: true },
    searchInfoAr: { type: "string", isNullable: true },
    birth: { type: "string", isNullable: true },
    presumed: { type: "boolean", isNullable: true },
    birthTerm: { type: "number", isNullable: true },
    sex: { type: "string", isNullable: true },
    address: { type: "string", isNullable: true },
    addressAr: { type: "string", isNullable: true },
    nationality: { type: "string", isNullable: true },
    wilaya: { type: "string", isNullable: true },
    commune: { type: "string", isNullable: true },
    phoneNumber: { type: "string", isNullable: true },
    secondaryPhoneNumber: { type: "string", isNullable: true },
    email: { type: "string", isNullable: true },
    profilePic: { type: "string", isForeignKeyOf: "uploads", isNullable: true },
    carceralStatus: { type: "string", isNullable: true },
    deathStatus: { type: "string", isNullable: true },
    deathDate: { type: "string", isNullable: true },
    deathCause: { type: "string", isNullable: true },
    familyMemberFullName: { type: "string", isNullable: true },
    familyMemberPhoneNumber: { type: "string", isNullable: true },
    familyMemberAddress: { type: "string", isNullable: true },
    maritalStatus: { type: "string", isNullable: true },
    fatherName: { type: "string", isNullable: true },
    motherName: { type: "string", isNullable: true },
    spouseName: { type: "string", isNullable: true },
    childrenCount: { type: "string", isNullable: true },
    profession: { type: "string", isNullable: true },
    bloodType: { type: "string", isNullable: true },
    socialSecurityNumber: { type: "string", isNullable: true },
    assuranceAffiliation: { type: "string", isNullable: true },
    nin: { type: "string", isNullable: true },
    confidentiality: { type: "string", isForeignKeyOf: "confidentialities" },
    services: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "services",
    },
    familyMembers: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "familyMembers",
    },
    isFamilyMember: { type: "boolean", isNullable: true },
  },
} as const);

export const xFamilyMember = defineSchema<any>({
  tableName: "familyMembers",
  columns: {
    ...xRow.columns,
    relationship: { type: "string" },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
  },
} as const);

export const xUpload = defineSchema<Upload>({
  tableName: "uploads",
  columns: {
    ...xRow.columns,
    field: { type: "string", isForeignKeyOf: "fields", isNullable: true },
    parent: { type: "string", isForeignKeyOf: "refAnswers", isNullable: true },
    thumbnail: { type: "string", isNullable: true },
    name: { type: "string" },
    url: { type: "string", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    original_name: { type: "string" },
    description: { type: "string", isNullable: true },
    fileType: { type: "string" },
    confidentiality: {
      type: "string",
      isForeignKeyOf: "confidentialities",
    },
    progress: { type: "number", isNullable: true },
    compressionProgress: { type: "number", isNullable: true },
    error: { type: "string", isNullable: true },
  },
} as const);

export const xDicomReception = defineSchema<DicomReception>({
  tableName: "dicomReceptions",
  columns: {
    ...xRow.columns,
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    filePath: { type: "string" },
    type: { type: "string" },
  },
} as const);

export const xProtocol = defineSchema<Protocol>({
  tableName: "protocols",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    days: { type: "string", isArray: true, isForeignKeyOf: "protocolDays" },
    intercure: { type: "number" },
    appointmentType: {
      type: "string",
      isForeignKeyOf: "appointmentTypes",
    },
    numberOfCycles: { type: "number" },
    description: { type: "string" },
  },
} as const);

export const xProtocolDay = defineSchema<ProtocolDay>({
  tableName: "protocolDays",
  columns: {
    ...xRow.columns,
    day: { type: "number", isNullable: true },
    protocol: {
      type: "string",
      isForeignKeyOf: "protocols",
      isNullable: true,
    },
    templates: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "ordonnanceTemplates",
    },
  },
} as const);

export const xIncome = defineSchema<Income>({
  tableName: "incomes",
  columns: {
    ...xRow.columns,
    basePrice: { type: "number" },
    status: { type: "string" },
    total: { type: "number" },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    parent: {
      type: "string",
      isForeignKeyOf: "consultations",
    },
    appointmentTypes: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "appointmentTypes",
    },
    credits: { type: "string", isArray: true, isForeignKeyOf: "credits" },
  },
} as const);

export const xCredit = defineSchema<Credit>({
  tableName: "credits",
  columns: {
    ...xRow.columns,
    amount: { type: "number" },
    income: { type: "string", isForeignKeyOf: "incomes", isNullable: true },
    patient: { type: "string", isForeignKeyOf: "patients", isNullable: true },
    status: { type: "string" },
  },
} as const);

export const xComment = xFeedComment;

export const xEcg = xECG;

export const xMigratedItem = defineSchema<MigratedItem>({
  tableName: "migratedItems",
  columns: {
    ...xRow.columns,
    oldTable: { type: "string" },
    oldValue: { type: "string", isNullable: true },
    oldId: { type: "string" },
    newItem: { type: "string" },
  },
} as const);

export const xStatusChange = defineSchema<StatusChange>({
  tableName: "statusChanges",
  columns: {
    ...xRow.columns,
    oldStatus: { type: "string" },
    newStatus: { type: "string" },
    item: { type: "string" },
  },
} as const);

export const xMigrationSession = defineSchema<MigrationSession>({
  tableName: "migrationSessions",
  columns: {
    ...xRow.columns,
    dbName: { type: "string", isNullable: true },
    status: { type: "string" },
    phase: { type: "string" },
    itemsCount: { type: "number" },
    totalItemsCount: { type: "number" },
    startDatetime: { type: "string" },
    currentStartDatetime: { type: "string", isNullable: true },
    currentEndDatetime: { type: "string", isNullable: true },
    shouldAddItems: { type: "boolean" },
    shouldAddPatients: { type: "boolean" },
    shouldAddTasks: { type: "boolean" },
    shouldUpdateWaitingLists: { type: "boolean" },
  },
} as const);

export const xDeletedItem = defineSchema<any>({
  tableName: "deletedItems",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xServerPoll = defineSchema<ServerPoll>({
  tableName: "serverPolls",
  columns: {
    ...xRow.columns,
    status: { type: "string" },
    name: { type: "string" },
    url: { type: "string" },
  },
} as const);

export const xVariableTemplate = defineSchema<VariableTemplate>({
  tableName: "variableTemplates",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    content: { type: "string" },
    category: { type: "string" },
  },
} as const);

export const xSession = defineSchema<any>({
  tableName: "sessions",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xEntryItem = defineSchema<any>({
  tableName: "entryItems",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xProductRelease = defineSchema<any>({
  tableName: "productReleases",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xProductReleaseItem = defineSchema<any>({
  tableName: "productReleaseItems",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xProductEntry = defineSchema<any>({
  tableName: "productEntries",
  columns: {
    ...xRow.columns,
  },
} as const);

export const xConcept = xSnomedConcept;

export const xDescription = xSnomedDescription;

export const xRelationship = xSnomedRelationship;

export const xMysteryGame = defineSchema<any>({
  tableName: "mysteryGames",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    userContext: { type: "string" },
    description: { type: "string" },
    numberOfCharacters: { type: "number" },
    difficultyLevel: { type: "string" },
    characters: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "mysteryCharacters",
    },
    events: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "mysteryEvents",
    },
    locations: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "mysteryLocations",
    },
    clues: { type: "string", isArray: true, isForeignKeyOf: "mysteryClues" },
    rawGeneratedDetails: { type: "string" },
  },
} as const);

export const xMysteryCharacter = defineSchema<MysteryCharacter>({
  tableName: "mysteryCharacters",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    physicalDescription: { type: "string" },
    mentalDescription: { type: "string" },
    whatTheyKnow: { type: "string" },
    whatTheyDoNotKnow: { type: "string" },
    isCulprit: { type: "boolean" },
    imageUrl: { type: "string", isNullable: true },
  },
} as const);

export const xMysteryEvent = defineSchema<MysteryEvent>({
  tableName: "mysteryEvents",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
    imageUrl: { type: "string", isNullable: true },
  },
} as const);

export const xMysteryLocation = defineSchema<MysteryLocation>({
  tableName: "mysteryLocations",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
    imageUrl: { type: "string", isNullable: true },
  },
} as const);

export const xMysteryClue = defineSchema<MysteryClue>({
  tableName: "mysteryClues",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
    imageUrl: { type: "string", isNullable: true },
    location: {
      type: "string",
      isForeignKeyOf: "mysteryLocations",
    },
    hasBeenFound: { type: "boolean" },
  },
} as const);

//
//
//
//
//
//

export const xAiDiscussion = defineSchema<AiDiscussion>({
  tableName: "aiDiscussions",
  columns: {
    ...xRow.columns,
    name: { type: "string" },
    description: { type: "string" },
  },
} as const);

export const xAiMessage = defineSchema<AiMessage>({
  tableName: "aiMessages",
  columns: {
    ...xRow.columns,

    discussionId: {
      type: "string",
      isForeignKeyOf: "aiDiscussions",
    },
    content: { type: "string" },
    role: { type: "string" },
    actionType: { type: "string" },
    tasks: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "aiTasks",
    },
  },
} as const);

export const xAiTask = defineSchema<AiTask>({
  tableName: "aiTasks",
  columns: {
    ...xRow.columns,
    prompt: {
      type: "string",
    },
    message: {
      type: "string",
      isForeignKeyOf: "aiMessages",
    },
    name: {
      type: "string",
    },
    description: {
      type: "string",
    },
    actions: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "aiTaskActions",
    },
    status: {
      type: "string",
    },
  },
} as const);

export const xAiTaskAction = defineSchema<AiTaskAction>({
  tableName: "aiTaskActions",
  columns: {
    ...xRow.columns,
    plannedNextTasks: {
      type: "string",
    },
    name: {
      type: "string",
    },
    description: {
      type: "string",
    },
    status: {
      type: "string",
    },
    steps: {
      type: "string",
      isArray: true,
      isForeignKeyOf: "aiTaskSteps",
    },
    type: {
      type: "string",
    },
    result: {
      type: "string",
    },
  },
} as const);

export const xAiTaskStep = defineSchema<AiTaskStep>({
  tableName: "aiTaskSteps",
  columns: {
    ...xRow.columns,
    targetId: {
      type: "string",
    },
    name: {
      type: "string",
    },
    description: {
      type: "string",
    },
    type: {
      type: "string",
    },
    status: {
      type: "string",
    },
    value: {
      type: "string",
    },
  },
} as const);
